import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { HOURS_CHANGED_EVENT } from '../../services/liveChatHoursOverride';
import chatAgentService, {
  STREAM_IDLE_TIMEOUT_MS,
  type LiveChatAvailability,
} from "../../services/RealChatAgentService";
import { regionFailoverService } from "../../services/regionFailoverService";
import { servedRegionService } from "../../services/servedRegionService";
import { chaosParticipationService } from "../../services/chaosParticipationService";
import * as handoffFunnel from "../../services/handoffFunnel";
import type { HandoffInitiator } from "../../services/handoffInitiator";
import { OPEN_ATTEMPT_STORAGE_KEY } from "../../services/handoffOpenAttempt";
import {
  createRetryScheduler,
  MAX_RETRIES,
  type RetryScheduler,
} from "../../services/messageRetryScheduler";
import { getBackendApiUrl } from "../../config/backend";
import { updateSessionHash } from "../../hooks/useSessionSync";
import { useSessionExpiry } from "../../hooks/useSessionExpiry";
import { useYMMV } from "../../hooks/useYMMV";
import { useScrollBehavior, ANCHOR_TOP_GAP_PX } from "../../hooks/useScrollBehavior";
import { useRepReplyReveal } from "../../hooks/useRepReplyReveal";
import { useStaffHandoffPoll } from '../../hooks/useStaffHandoffPoll';
import { STAFF_SENT_NOTICE } from './staffSentNotice';
import { useChatStream } from "../../hooks/useChatStream";
import { useRegionHealth } from "../../hooks/useRegionHealth";
import { usePersistedWelcome } from "../../hooks/usePersistedWelcome";
import { effectiveKind } from "../../utils/messageKind";
import { friendlyCard } from "../../utils/redactCritical";
import {
  beginYmmvOpenMorph,
  beginYmmvCloseMorph,
  cancelYmmvMorph,
  cancelYmmvOpenMorph,
  captureYmmvMorphOrigin,
  isYmmvOpenMorphInFlight,
} from "../../utils/ymmvMorph";
import MessageRenderer from "./MessageRenderer";
import { type ConversationSurveyAnswers } from "./ConversationSurveyCard";
import ThinkingIndicator from "./ThinkingIndicator";
import { dispatchCardChunk } from "./chatStreamCards";
import { dispatchToolChunk } from "./chatStreamTools";
import type { Message, MessagePart } from "../../types/chat.types";
import VehicleTypeahead from "./VehicleTypeahead";
import type { YMMVMatch, YMMVGroup, TireSizeData } from "./VehicleTypeahead";
import ChatModals from "./ChatModals";
import ChatInputBar from "./ChatInputBar";
import { dismissPhoneKeyboard } from "../../utils/phoneKeyboard";
import { bannerModel, hoursModalCopy } from "./chatBanner";
import { emergencyOverlayActive, handoffEngaged, overlayHumanRoute } from "./emergencyOverlay";
import { BetaDisclaimer, LegalDisclaimer } from "./ChatDisclaimers";
import { SkeletonCanvas } from "./ChatSkeleton";
import { isHumanFirstMode, hideModeBanner } from "./humanFirst";
import AnnouncementBanner from "../common/AnnouncementBanner";
import { applyOutagePreview, outagePreviewOn, OUTAGE_PREVIEW_NOTE } from "./outagePreview";
import {
  createWelcomeMessage,
  openingKind,
  liveChatClosedFrom,
  openingUndecided,
  OPEN_INTENT_EVENT,
  StarterPrompt,
  MENU_OPTIONS,
  ORDERS_SUBMENU_VALUE,
  ORDERS_SUBMENU_OPTIONS,
  ORDERS_SUBMENU_PROMPT,
} from "./WelcomeMessage";
import { DEFAULT_STARTER_PROMPTS } from "../../constants/starterPrompts";
import OrderLookupCard from "./OrderLookupCard";
import { isOrderLookupEnabled } from "../../services/orderLookupService";
import { AGENT_UNAVAILABLE_MESSAGE, RETRY_EXHAUSTED_MESSAGE, LIVE_CHAT_CLOSED_CONTACT, HUMAN_CONTACT, handoffDeclinedMessage, handoffSlowMessage, handoffUnjoinedMessage } from "../../constants/humanContact";
import ChatTrackingRoot from "./ChatTrackingRoot";
import VehicleHeaderBar from "./VehicleHeaderBar";
import type { ActiveVehicle } from "./VehicleHeaderBar";
import { parseVehicleDescription } from "./VehicleHeaderBar";
import styles from "./ChatInterface.module.css";
import { isMessageTooLong } from "./messageLimit";
import messageRendererStyles from "./MessageRenderer.module.css";
import embeddableChatStyles from "./EmbeddableChat.module.css";
import { demoMessages } from "../../data/demoConversation";
import { vehicleIdentityKey } from "../../utils/vehicleIdentity";
import { upsertActiveVehicle, upsertVehicleHistory } from "../../utils/mergeVehicle";
import { logger } from "../../utils/logger";
import { logLpFailure } from "../../utils/clientLog";
import { postLiveAgentConnected } from "./liveAgentAttribution";
import {
  postLiveChatEvent,
  isLiveChatEvent,
  LIVE_CHAT_EVENTS,
  markLiveChatConnected,
  liveChatConnectedEmitted,
  clearLiveChatConnected,
  postExperimentExposed,
  postStarterPromptClick,
  starterPromptExperimentArms,
} from "./hostAnalytics";
import { exposureDue, matchStarterPrompt } from "./experimentExposure";
import { postCapability } from "./engagementAnalytics";
import { connectingLabel, reconcileHandoffTranscript } from "./handoffResume";
import { withConnectDeskNotice } from "./connectDeskNotice";
import { resetTrackingState } from "../../services/interactionTrackingService";
import { shouldShowDevControls } from "../../utils/embedGating";
import { sanitizeVehicleQuery } from "../../utils/sanitizeVehicleQuery";
// TRAC-1002: kept apart from the ./emergencyOverlay import above so the health
// check exemption does not collide with edits to that import.
import { overlayBlocksChat } from "./emergencyOverlay";
import { isSyntheticHealthCheck } from "../../config/backend";
import { handoffWaitStatus, isHandoffSlowNotice, connectingTurnDepartment } from "./handoffWaitStatus";
import HandoffWaitStatusLine from "./HandoffWaitStatusLine";
import { startVisibleInterval } from "../../utils/visibleInterval";
import { offerAckDepartment } from "../../utils/offerAck";
import { installSecureForm, SECURE_FORM_STATUS_EVENT } from "./secureForm/installSecureForm";

interface ChatInterfaceProps {
  // TRAC-486: True when this iframe is rendered inside our own portal
  // (same-origin parent). Gates internal-only UI like the emergency overlay
  // dismiss and the dev settings button.
  isPortalContext?: boolean;
  onPreConversationChange?: (isPreConversation: boolean) => void;
  // TRAC-1108: whether the panel holding this chat is open. Inside the page shell the chat
  // loads collapsed on every page view; everywhere else it is only drawn when open.
  panelOpen?: boolean;
}

export interface ChatInterfaceHandle {
  handleClearSession: () => void;
  focusInput: () => void;
  openDevSettings: () => void;
  sendMessage: (message: string) => void;
  // TRAC-732: trigger / end the live-human handoff from an external header (EmbedPage).
  escalateToHuman: () => void;
  endHandoff: () => void;
  // TRAC-790: somebody opened the chat asking for a person, on a chat that already has
  // a conversation in it. The opening is long past, so answer it where they are.
  requestHuman: () => void;
}

// TRAC-497: Message and its part/tool types are canonical in chat.types.ts —
// the local duplicates (which had silently diverged) are gone. Message is
// re-exported for backwards compatibility.
export type { Message };

interface LineHeight {
  lineHeight: number;
  paddingTop: number;
  paddingBottom: number;
}

interface AnnouncementSettings {
  enabled: boolean;
  message: string;
  mode: "informational" | "emergency";
  startDate: string | null;
  endDate: string | null;
  enabled_by: string | null;
  enabled_at: string | null;
}

/**
 * ChatInterface - Core chat UI component
 *
 * Features:
 * - Message display with streaming support
 * - Auto-scroll with manual override
 * - Session persistence
 * - Stream interruption recovery
 * - Demo mode support (VITE_DEMO_MODE=true)
 */

// TRAC-790: the doors to a person are defined ONCE, in WelcomeMessage (MENU_OPTIONS).
// There used to be a second copy here for the button's "why are you here?" prompt, and
// the two had already drifted apart in wording ("Help with a Hazard Claim" vs "Help with
// a hazard claim") and in behaviour — this copy sent "My orders" straight to Customer
// Care while the opening menu offered the second menu behind it. Same doors, wherever
// the customer meets them.

// TRAC-732: while a customer waits in the live-chat queue we cycle "Did you know?"
// tire facts (every 9s) instead of repeating "connecting you…" a third time.
const TIRE_FUN_FACTS: string[] = [
  "The first air-filled (pneumatic) tire was patented in 1888 by John Boyd Dunlop — for his son's bicycle.",
  'Rubber is naturally a milky off-white — early tires were actually white! They turned black once makers added carbon black, which makes the rubber roughly 100× stronger and longer-lasting.',
  'A typical passenger tire turns about 800 times for every mile you drive.',
  "The world's largest tire is an 80-foot Uniroyal giant in Michigan — it was a Ferris wheel at the 1964 World's Fair.",
  'Penny test: put a penny in the tread with Lincoln upside down — if you see all of his head, it\'s time for new tires.',
  'Many race and aircraft tires are filled with nitrogen instead of air for more stable pressure as they heat up.',
  'Underinflated tires waste fuel — the soft rubber flexes and heats up, dragging down your gas mileage.',
  "Those little rubber hairs on a new tire are just leftover rubber from the mold's air vents — not a sign of quality.",
  'A tire\'s speed rating is a letter on the sidewall — a "Z" rating means it\'s built for sustained speeds over 149 mph.',
  'F1 tires are designed to last only about 60–120 km, while a good road tire can go 80,000 km or more.',
];

// TRAC-732: shuffle-bag for the fun facts — a random permutation of [0..n-1] so we cycle
// through ALL facts (no repeats) before reshuffling. `avoidFirst` keeps a reshuffled bag
// from opening on the same fact that just closed the previous one (no back-to-back repeat).
const shuffledFactBag = (n: number, avoidFirst?: number): number[] => {
  const bag = Array.from({ length: n }, (_, i) => i);
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  if (avoidFirst !== undefined && bag.length > 1 && bag[0] === avoidFirst) {
    [bag[0], bag[1]] = [bag[1], bag[0]];
  }
  return bag;
};

/** TRAC-1007: how long a customer waits for a rep before the chat says we are still trying. */
const HANDOFF_SLOW_AFTER_MS = 60_000;
/**
 * TRAC-1007: the longest the slow-wait timer sleeps before re-reading the wall clock. A timer
 * does not advance while the laptop sleeps, so one 60 s timer set before a lid close could
 * still owe most of its minute after waking; re-reading every few seconds shows the message
 * within this long of waking.
 */
const HANDOFF_SLOW_RECHECK_MS = 5_000;
/**
 * TRAC-1066: how long a page that came up mid-ask keeps asking before it believes a
 * "no handoff here" answer. `/agent/handoff-state` reports a handoff only once the rep side
 * exists (mode waiting/human); from the press until then — `escalate-to-human` answers
 * "escalating" — it truthfully says not-active about a customer who is visibly connecting.
 * A navigation inside that window is the outage journey, so the window is what this covers.
 */
const CONNECTING_SETTLE_RECHECK_MS = 2_000;
/** Attempts at CONNECTING_SETTLE_RECHECK_MS apart — ~30 s, far longer than an escalate takes. */
const CONNECTING_SETTLE_TRIES = 15;

const ChatInterface = forwardRef<ChatInterfaceHandle, ChatInterfaceProps>(
  ({ isPortalContext = false, onPreConversationChange, panelOpen = true }, ref) => {
    // Check if demo mode is enabled
    const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true";

    // TRAC-468: Stable getToken reference. Partner-token-only after TRAC-486
    // killed the non-embed path.
    const getToken = useMemo(
      () => async () => chatAgentService.getPartnerToken() || undefined,
      [],
    );

    const [messages, setMessages] = useState<Message[]>([]);


    // Pre-conversation state: no real user messages sent yet (only client-only synthetic messages)
    const isPreConversation = useMemo(
      // TRAC-732: the connecting turn and routing prompt are isClientOnly but the
      // conversation is no longer "pre-conversation" once a handoff starts — otherwise
      // the wrapper keeps the NoScroll class (overflow-y:hidden) and the user can't
      // scroll back up past the "Connecting you now…" message.
      () => messages.every(m => m.isClientOnly && !m.isConnectingTurn && !m.isRoutingPrompt),
      [messages],
    );

    useEffect(() => {
      onPreConversationChange?.(isPreConversation);
    }, [isPreConversation, onPreConversationChange]);

    // TRACI-1012: LivePerson secure forms (open when the rep sends one). Idempotent.
    useEffect(() => { installSecureForm(); }, []);

    const [inputValue, setInputValue] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    // TRAC-550 B5: distinct from isStreaming/isLoading. True only while the
    // SSE connection is being torn down + reopened against a freshly-flipped
    // region. Cleared when any new SSE connection successfully opens.
    const [isReconnecting, setIsReconnecting] = useState<boolean>(false);

    useEffect(() => {
      const unsubscribe = chatAgentService.onReconnectingChange(setIsReconnecting);
      return unsubscribe;
    }, []);

    // TRAC-550 B6: install cross-tab pin-sync listener once. Idempotent —
    // safe even if multiple ChatInterface instances mount.
    useEffect(() => {
      regionFailoverService.installStorageListener();
    }, []);

    // TRAC-550 B3 + B4: poll both regions and pump the health snapshot into
    // the pin-flip state machine. The hook polls every 15s; applyHealth
    // re-evaluates flip/unflip rules on each update.
    const regionHealth = useRegionHealth();
    useEffect(() => {
      regionFailoverService.applyHealth(regionHealth);
    }, [regionHealth]);
    const [isLoadingFadingOut, setIsLoadingFadingOut] =
      useState<boolean>(false);
    const [highlightInput, setHighlightInput] = useState<boolean>(false);
    const [isSurveyModalOpen, setIsSurveyModalOpen] = useState<boolean>(false);
    const [visiblePromptCount, setVisiblePromptCount] = useState<number | null>(null);
    const [announcementMessage, setAnnouncementMessage] = useState<
      string | null
    >(null);
    const [announcementMode, setAnnouncementMode] = useState<"informational" | "emergency">("informational");
    // TRAC-1052: the last announcement poll was rewritten by this browser's own
    // outage preview (QA settings gear). Only drives the tester-facing marker.
    const [outagePreviewForced, setOutagePreviewForced] = useState(false);
    // TRAC-1006: the handoff attempt this conversation was in on the previous page, read
    // synchronously so everything the outage screen's gate consults starts from it -- before
    // the first announcement poll result can apply. Without it a navigation mid-handoff
    // rendered `idle` and not-dismissed, and the first poll put the screen back over a live
    // rep with typing disabled. See services/handoffAttempt.ts.
    const [seededHandoffAttempt] = useState(() => chatAgentService.loadHandoffAttempt());
    const [announcementDismissed, setAnnouncementDismissed] = useState(() => seededHandoffAttempt?.overlayDismissed ?? false);
    const [surveyEnabled, setSurveyEnabled] = useState<boolean>(false);
    // TRAC-550 B8: replaces TRAC-81's 15s countdown timer. Tracks the
    // current auto-retry attempt index (1..MAX_RETRIES). null when no
    // retry is in flight.
    const [retryAttempt, setRetryAttempt] = useState<number | null>(null);
    // TRAC-862: the attempt number alone is not an honest indicator. Between
    // attempts the banner sat on the same text for the whole backoff + the whole
    // next stream timeout — up to ~95s of a frozen "Retrying… (1/4)". A tester
    // reported the counter "stayed at 1" as a bug when the failsafe was in fact
    // working; a recovery nobody can see is not a recovery. These two split
    // "waiting to try again" from "trying right now", and give the wait a number
    // that visibly moves.
    const [retryPhase, setRetryPhase] = useState<"waiting" | "inflight" | null>(null);
    const [retrySecondsLeft, setRetrySecondsLeft] = useState<number | null>(null);
    // Denominator for the countdown text.
    const [retryWaitTotal, setRetryWaitTotal] = useState<number | null>(null);
    // TRAC-862: the exact window the current bar should fill across, in ms —
    // the backoff delay while waiting, the per-attempt timeout while in flight.
    // Driving the bar off a real duration is what makes it proportional instead
    // of decorative; a constant-rate sweep tells the customer nothing about how
    // long anything will take.
    const [retryWindowMs, setRetryWindowMs] = useState<number | null>(null);
    const [retryBarFill, setRetryBarFill] = useState(0);
    /**
     * TRAC-862: true once retry exhaustion has engaged the overlay.
     *
     * The announcement poll runs every 30s and nulls `announcementMessage`
     * whenever the SERVER has no announcement to show — which is exactly the
     * situation here, because this overlay is client-owned. Without this flag
     * the overlay appeared and was then silently wiped within one poll, so the
     * customer got a ~30s flash of the only screen that tells them how to reach
     * a human. Observed in the browser; the DOM had the overlay at t+27.5s and
     * nothing at t+40s.
     */
    const retryExhaustedOverlayRef = useRef(false);
    // TRAC-975: true when the outage overlay was dismissed by the customer taking
    // its route to a person, as opposed to an admin pressing Dismiss. Only the
    // first kind is undone when the handoff ends — see the handoff-end branch.
    const overlayDismissedForHandoffRef = useRef(seededHandoffAttempt?.overlayDismissed ?? false);
    // TRAC-1007: per handoff attempt, carried across pages in the attempt record -- the team the
    // backend confirmed (the slow-wait message gives its number), and the ask that message was
    // already shown for, so it appears once per attempt rather than once per page.
    const attemptDeptRef = useRef<string | null>(seededHandoffAttempt?.department ?? null);
    const slowNoticeForRef = useRef<number | null>(seededHandoffAttempt?.slowNoticeFor ?? null);
    // TRAC-1212: who started this attempt (button / asked the AI / accepted the AI's offer),
    // carried in the attempt record so an escalate re-sent from the next page still says so.
    const attemptInitiatorRef = useRef<HandoffInitiator | null>(seededHandoffAttempt?.initiatedBy ?? null);
    // TRAC-1212: who started the handoff the AGENT asked for this turn, from its
    // human_handoff event (set by useChatStream; read when that handoff actually starts).
    const agentHandoffInitiatorRef = useRef<HandoffInitiator | null>(null);
    // TRAC-1066: how far this attempt's escalate request got — see handoffAttempt.ts. The page
    // that inherits a `pressed` attempt is the only one that can still send it.
    const escalateRequestRef = useRef<'pressed' | 'sent' | null>(
      seededHandoffAttempt?.escalateRequest ?? null,
    );
    /** TRAC-1066: an inherited escalate is re-issued at most once per mount. */
    const reissuedEscalateRef = useRef(false);
    const [showDevSettings, setShowDevSettings] = useState<boolean>(false);
    const [chunkTimeout, setChunkTimeout] = useState<number | null>(
      chatAgentService.getTestChunkTimeout(),
    );
    // Pending selection state - stores button clicks that happen during streaming
    const [pendingSelection, setPendingSelection] = useState<{
      value: string;
      label: string;
    } | null>(null);
    // Agent thinking state - shows tire animation while processing
    const [isThinking, setIsThinking] = useState<boolean>(false);

    // TRAC-475: filled in below, once escalateToHuman exists (see the escalate bridge).
    const replayHandoffRef = useRef<{
      onHandoff: (escalation?: string, department?: string) => void;
      onDone: () => void;
    } | null>(null);
    // TRAC-497: Session-expiry + interrupted-stream-replay concern extracted to useSessionExpiry.
    // Hook owns the expiry/toast/replay state, the TRAC-468 replay effect, and reset;
    // the streaming code below calls the returned setters + triggerExpiryResend.
    const {
      sessionExpired, setSessionExpired,
      needsReplay, setNeedsReplay,
      replayStreamState, setReplayStreamState,
      replayAttemptedRef,
      triggerExpiryResend, resetSessionExpiryState,
    } = useSessionExpiry({ setMessages, setIsStreaming, setIsLoading, getToken, replayHandoffRef });

    // TRAC-637: monotonic counter bumped by handleClearSession so the
    // persistence hook can drop its cached welcome and (after the next
    // mount) re-persist a fresh one. Without this, the rendered welcome
    // would keep showing the just-cleared content from React state.
    const [welcomeClearSignal, setWelcomeClearSignal] = useState(0);


    // TRAC-497: YMMV state now owned by useYMMV (hook call below, after upsertVehicle).

    // TRAC-277: Active vehicle header bar
    const [activeVehicle, setActiveVehicle] = useState<ActiveVehicle | null>(null);
    const [vehicleHistory, setVehicleHistory] = useState<ActiveVehicle[]>([]);
    // TRAC-918: makes already in the header, so a description-only card parses
    // "2026 Aston Martin DBX S" with the picker's make rather than a one-token guess.
    const vehicleHistoryRef = useRef<ActiveVehicle[]>([]);
    useEffect(() => {
      vehicleHistoryRef.current = vehicleHistory;
    }, [vehicleHistory]);
    const pendingVehicleContextRef = useRef<ActiveVehicle | null>(null);
    const prevActiveVehicleRef = useRef<ActiveVehicle | null>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);


    // TRAC-550 B8: scheduler instance + retry handler ref (kept fresh on
    // every render so the scheduler's doRetry callback uses the current
    // closure values).
    const retrySchedulerRef = useRef<RetryScheduler | null>(null);
    const autoRetryHandlerRef = useRef<(() => void) | null>(null);
    const hasLoadedFromStorageRef = useRef<boolean>(false);
    // TRAC-1033: the transcript this page started with has been read back. Set once, by the
    // mount-time load below, and never dropped: a new chat or a first-message retry resets
    // hasLoadedFromStorageRef without ever setting it again, so anything waiting on THAT flag
    // after one of those waits forever.
    const transcriptRestoredRef = useRef<boolean>(false);
    const lineHeightRef = useRef<LineHeight | null>(null);
    const surveyButtonRef = useRef<HTMLButtonElement>(null);
    // Ref to track if session ID notification has been sent to analytics test page
    const sessionIdNotifiedRef = useRef<boolean>(false);
    // Ref to track latest messages for use in callbacks (avoids stale closure)
    const messagesRef = useRef<Message[]>(messages);

    // Keep messagesRef in sync with messages state
    useEffect(() => {
      messagesRef.current = messages;
    }, [messages]);

    // TRAC-277: Extract vehicle data from a message part
    const extractVehicleFromPart = useCallback((part: MessagePart, source: ActiveVehicle["source"]): ActiveVehicle | null => {
      if (part.type === "vehicle_info_card" && part.vehicle) {
        const v = part.vehicle;
        if (!v.make || !v.model || !v.year) return null;
        const desc = [v.year, v.make, v.model, v.variant].filter(Boolean).join(" ");
        return {
          id: vehicleIdentityKey(v.year, v.make, v.model, v.variant),
          description: desc,
          year: v.year,
          make: v.make,
          model: v.model,
          variant: v.variant,
          tireSize: v.tireSize,
          staggered: v.staggered,
          frontSize: v.frontSize ?? undefined,
          rearSize: v.rearSize ?? undefined,
          runflat: v.runflat,
          source,
          timestamp: Date.now(),
        };
      }
      if (part.type === "tdg_link_card" && part.vehicle?.description) {
        // Use structured fields from agent when available, fall back to regex parsing
        const hasStructured = part.vehicle.make && part.vehicle.model && part.vehicle.year;
        const parsed = hasStructured ? null : parseVehicleDescription(part.vehicle.description, vehicleHistoryRef.current.map((v) => v.make));
        if (!hasStructured && !parsed) return null;
        const year = hasStructured ? part.vehicle.year! : parsed!.year;
        const make = hasStructured ? part.vehicle.make! : parsed!.make;
        const model = hasStructured ? part.vehicle.model! : parsed!.model;
        const variant = hasStructured ? (part.vehicle.variant || undefined) : parsed!.variant;
        // Use structured front/rear fields when available, fall back to regex for cached responses
        const frontSize = part.vehicle.frontTireSize ?? undefined;
        const rearSize = part.vehicle.rearTireSize ?? undefined;
        const hasStructuredSizes = frontSize && rearSize;
        const staggeredMatch = hasStructuredSizes ? null : part.vehicle.tireSize?.match(/Front:\s*(\S+),\s*Rear:\s*(\S+)/i);
        return {
          id: vehicleIdentityKey(year, make, model, variant),
          description: part.vehicle.description,
          year,
          make,
          model,
          variant,
          tireSize: hasStructuredSizes || staggeredMatch ? undefined : part.vehicle.tireSize,
          frontSize: hasStructuredSizes ? frontSize : (staggeredMatch ? staggeredMatch[1] : undefined),
          rearSize: hasStructuredSizes ? rearSize : (staggeredMatch ? staggeredMatch[2] : undefined),
          source,
          timestamp: Date.now(),
        };
      }
      if (part.type === "tire_size_confirmation" && part.vehicleDescription) {
        const parsed = parseVehicleDescription(part.vehicleDescription, vehicleHistoryRef.current.map((v) => v.make));
        if (!parsed) return null;
        return {
          id: vehicleIdentityKey(parsed.year, parsed.make, parsed.model, parsed.variant),
          description: part.vehicleDescription,
          year: parsed.year,
          make: parsed.make,
          model: parsed.model,
          variant: parsed.variant,
          tireSize: part.tireSize,
          staggered: part.staggered,
          frontSize: part.frontSize,
          rearSize: part.rearSize,
          source,
          timestamp: Date.now(),
        };
      }
      return null;
    }, []);

    // TRAC-277: Helper to add/update a vehicle in history and set as active
    const upsertVehicle = useCallback((vehicle: ActiveVehicle) => {
      // Matching rules (and the TRAC-918 multi-word-make case) live in isSameVehicle.
      setVehicleHistory((prev) => upsertVehicleHistory(prev, vehicle));
      setActiveVehicle((prev) => upsertActiveVehicle(prev, vehicle));
    }, []);

    // TRAC-277: Watch messages for vehicle-carrying parts and update header
    useEffect(() => {
      if (messages.length === 0) return;
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.role !== "assistant" || !lastMsg.parts) return;

      for (const part of lastMsg.parts) {
        const vehicle = extractVehicleFromPart(part, part.type as ActiveVehicle["source"]);
        if (vehicle) {
          upsertVehicle(vehicle);
        }
      }
    }, [messages, extractVehicleFromPart, upsertVehicle]);

    // TRAC-497: YMMV vehicle-search concern extracted to useYMMV. handleSendMessageRef
    // breaks the definition-order cycle (handleSendMessage is defined below but YMMV
    // handlers call it); it is kept fresh via an effect near useImperativeHandle.
    // TRAC-732: widened to the 6-param async signature the handoff callers use
    // (canned replies / routing / soft-cancel pass metadata, bypass, and a canned part).
    const handleSendMessageRef = useRef<
      ((m?: string, d?: string, skip?: boolean,
        metadata?: { selectedValue: string; selectedLabel: string },
        bypass?: boolean, canned?: { parts: MessagePart[] },
        comebackGuardGen?: number, systemMarker?: boolean) => Promise<void>) | null
    >(null);
    const {
      ymmvMode, ymmvResults, ymmvGroups, ymmvLoading, ymmvSelectedIndex,
      ymmvTotalCount, ymmvGroupType, ymmvMultiTier, ymmvTotalMatches,
      ymmvDroppedYear, ymmvStep, ymmvSelectedVehicle, ymmvTireSizeData,
      ymmvTireSizeLoading, ymmvTireSizeError, ymmvCanGoBack,
      pendingYmmvTimingRef, ymmvPrefillRef, allTireSizesRef,
      ymmvIntentRef, ymmvPrefillSizeRef, pendingSelectorCompletionRef,
      activateYmmvMode, resetYmmvState, handleYmmvSelect, handleYmmvGroupSelect,
      handleTireSizeSelect, handleTireSizeRetry, handleBackToVehicle,
      handleYmmvStepBack, handleYmmvDismiss, handleYmmvInputChange, handleYmmvKeyDown,
    } = useYMMV({
      handleSendMessageRef, upsertVehicle, setActiveVehicle, pendingVehicleContextRef,
      setMessages, isStreaming, setIsStreaming, setIsLoading, setInputValue, inputRef,
      // TRAC-896: fired at the top of every USER-initiated dismissal (X, Escape),
      // before the state update that leaves ymmvMode, so the cover is up before
      // the un-swap renders. Self-gating — see beginYmmvCloseMorph. Deliberately
      // NOT wired to completion-driven exits (a committed tire size): there the
      // conversation is moving forward into a new turn, so shrinking back into
      // the control the customer came from would animate the wrong story.
      onDismissRequested: (snapshot) => {
        beginYmmvCloseMorph(ymmvMorphContainerRef.current);
        recordYmmvDismissalOnOriginCard(snapshot);
      },
      // TRAC-896: the picker completed. Arrow-wrapped for the same reason as the
      // dismissal hook above — the handler is declared below this call, and only
      // ever runs later.
      onSelectionCommitted: (vehicleDisplay: string, tireSize?: string) => {
        handleYmmvSelectionCommitted(vehicleDisplay, tireSize);
      },
    });

    // TRAC-896: the chat container is the partial card's morph
    // TARGET — it's the element that goes teal in ymmvMode, so its rect is the
    // full-interface bounds. Measured at tap time (it doesn't resize on the flip).
    const ymmvMorphContainerRef = useRef<HTMLDivElement | null>(null);

    /**
     * TRAC-896: which partial YMMV card opened the picker, if one did. Set at tap
     * time by handleYmmvPreviewOpen and cleared by every other entry point, so a
     * completed selection can only ever write back to a card the customer
     * actually opened — never to one raised by the header bar, and never to a
     * stale capture from ten minutes ago.
     */
    const ymmvOriginCardRef = useRef<{ messageId: string; partIndex: number } | null>(null);


    /**
     * TRAC-896 dismissal-resume (Dylan, 8/12): backing out of the picker with
     * the X must not discard what the customer did in there. If they made
     * progress — drilled to a vehicle's tire-size list, or refined the search —
     * the card they opened takes that progress as its new prefill (so the field
     * shows where they left off, and the option preview refetches for it), and
     * the part records which SCREEN to reopen: a later tap resumes the
     * tire-size list or re-runs the refined query instead of starting over.
     *
     * Doing nothing in the picker writes nothing: the card keeps its original
     * emit prefill. Openings that didn't come from a card (header bar, starter
     * chip) have no card to write to and are unaffected.
     */
    const recordYmmvDismissalOnOriginCard = useCallback(
      (snapshot: {
        step: "vehicle" | "tire_size";
        vehicle: { year: string; make: string; model: string; display: string; tire_sizes?: string[] } | null;
        variant: string | null;
      }): void => {
        const target = ymmvOriginCardRef.current;
        ymmvOriginCardRef.current = null;
        if (!target) return;
        const onSizeStep = snapshot.step === "tire_size" && !!snapshot.vehicle;
        // Same pure-size guard the completion path uses: a variant that is
        // literally a tire size would be redundant in the display string.
        const isPureSizeVariant = snapshot.variant
          ? /^[A-Z]{0,2}\d+\/\d+-\d+$/.test(snapshot.variant)
          : false;
        const display = onSizeStep
          ? snapshot.variant && !isPureSizeVariant
            ? `${snapshot.vehicle!.display} ${snapshot.variant}`
            : snapshot.vehicle!.display
          : inputValue.trim();
        setMessages((prev) => {
          let changed = false;
          const updated = prev.map((msg) => {
            if (msg.id !== target.messageId) return msg;
            const parts = msg.parts;
            const part = parts?.[target.partIndex];
            if (!parts || part?.type !== "ymmv_preview_card") return msg;
            // No progress → no write. An unchanged (or emptied) field means the
            // customer looked and left; the card keeps saying what the agent said.
            if (!display || display === part.prefill) return msg;
            changed = true;
            const nextParts = [...parts];
            nextParts[target.partIndex] = {
              ...part,
              prefill: display,
              resume: onSizeStep
                ? { step: "tire_size" as const, vehicle: snapshot.vehicle!, variant: snapshot.variant }
                : { step: "vehicle" as const, query: display },
            };
            return { ...msg, parts: nextParts };
          });
          if (!changed) return prev;
          chatAgentService.saveMessagesToSession(updated);
          return updated;
        });
      },
      [setMessages, inputValue],
    );

    /**
     * TRAC-896 (ruled 8/12): a finished picker presents as a message FROM the
     * customer — a card in their slot carrying the vehicle and tire size — the
     * way a typed answer would sit in the flow. The originating agent card is
     * deliberately left alone (it greys out with its original prefill; the
     * capture ref is cleared without a write-back) so the selection appears
     * exactly once. Appended BEFORE the structured resume streams the agent's
     * reply, so the transcript reads question → answer → response; persisted
     * like every other message, so a reload re-renders it in place.
     */
    const handleYmmvSelectionCommitted = useCallback(
      (vehicleDisplay: string, tireSize?: string): void => {
        ymmvOriginCardRef.current = null;
        if (!vehicleDisplay) return;
        // TRAC-965: the picker pick is the customer completing a fitment lookup — the one
        // capability that is the customer's act rather than a stream card, so it fires on the
        // commit itself. A picker that was shown but never completed fires nothing.
        postCapability(window, "fitment_lookup", chatAgentService.agentSessionId);
        setMessages((prev) => {
          const updated = [
            ...prev,
            {
              id: `ymmv-selection-${Date.now()}`,
              role: "user" as const,
              content: "",
              timestamp: new Date(),
              parts: [
                {
                  type: "ymmv_selection_card",
                  vehicleDescription: vehicleDisplay,
                  tireSize,
                } as MessagePart,
              ],
            },
          ];
          chatAgentService.saveMessagesToSession(updated);
          return updated;
        });
      },
      [setMessages],
    );

    // TRAC-896: the visual hold. While the card→interface panel is still growing,
    // ymmvMode is already TRUE (the search fires, state is armed) but the screen
    // must still look like the conversation — otherwise the container goes teal
    // on frame one and the panel animates teal-on-teal, i.e. invisibly. So every
    // PRESENTATIONAL ymmvMode gate below reads `ymmvVisual` instead, and the
    // whole swap happens in one render at `onCover`, underneath an opaque
    // full-bleed panel. BEHAVIORAL gates (input handlers, dismissal) keep using
    // the real ymmvMode. Always false unless a morph is actually in flight, so
    // reduced motion and every non-card open behave exactly as before.
    const [ymmvMorphing, setYmmvMorphing] = useState(false);
    const ymmvVisual = ymmvMode && !ymmvMorphing;

    /**
     * TRAC-896: the ONE way this component opens the vehicle picker.
     *
     * Every user-gesture entry funnels through here so the morph is consistent
     * and, more importantly, so the origin bookkeeping can't drift: `origin` is
     * the element the customer actually tapped, captured by that element's own
     * click handler and passed straight through (never queried later — several
     * cards/chips can be on screen, and the tapped one is inside a display:none
     * subtree the moment the picker mounts).
     *
     * Pass null for an open with no originating element — the agent raising the
     * picker by itself. That clears any previously captured origin, so the
     * picker opens and closes plainly instead of growing out of whatever chip
     * somebody happened to tap ten minutes ago.
     */
    const openYmmvFromOrigin = useCallback(
      (
        origin: HTMLElement | null,
        prefill?: string,
        source: "starter" | "agent" | "user" | "card" = "starter",
        // TRAC-896 dismissal-resume: the screen to reopen (carried on the card
        // part by a previous mid-flow dismissal). Only card taps pass one.
        resume?: MessagePart["resume"],
      ): void => {
        // Opening is idempotent while a morph is in flight. During that window the
        // picker has not mounted, so there is no X — and the overlay used to let
        // taps through to the origin underneath, where a re-entry restarted the
        // morph and pushed the picker's arrival back again. Repeat that and it
        // never arrives. The overlay swallows input now, so this is belt and
        // braces (keyboard re-trigger, programmatic re-entry), but a second open
        // request mid-morph is always the same open — never a fresh one.
        if (isYmmvOpenMorphInFlight()) return;
        // TRAC-896: only a card tap sets this (just before it calls in here with
        // source "card"), so clearing on every other source is what keeps a
        // header-bar or starter open from completing into a card the customer
        // never touched — including one they opened and abandoned earlier.
        if (source !== "card") ymmvOriginCardRef.current = null;
        captureYmmvMorphOrigin(origin);
        setYmmvMorphing(
          beginYmmvOpenMorph(ymmvMorphContainerRef.current, () => setYmmvMorphing(false)),
        );
        activateYmmvMode(prefill, source, resume ?? undefined);
      },
      [activateYmmvMode],
    );

    // TRAC-896: no orphaned overlays. Unmount kills whatever is in flight, and
    // any exit from ymmvMode that ISN'T the X (e.g. a tire size was committed
    // mid-morph) cancels a still-running OPEN morph so it can't sit on top of
    // the returning conversation. A close morph is deliberately left alone —
    // it runs precisely while ymmvMode is false.
    useEffect(() => {
      if (!ymmvMode) cancelYmmvOpenMorph();
    }, [ymmvMode]);
    useEffect(() => cancelYmmvMorph, []);

    // Starter prompts state (TRAC-195)
    const [starterPrompts, setStarterPrompts] = useState<StarterPrompt[]>([]);

    // TRAC-291: Page context from PDP postMessage (for contextual greeting)
    // Wait for context event before showing welcome message to avoid flash.
    const [pageContext, setPageContext] = useState(chatAgentService.getPageContext());
    const [pageContextReady, setPageContextReady] = useState(!!chatAgentService.getPageContext());

    // TRAC-497: Scroll machinery extracted to useScrollBehavior (refs/functions/state).
    // The scroll EFFECTS stay below in this component (preserving their order vs the
    // load-from-storage effect) and consume these returns by the same names.
    const {
      isInitialScrollPending, setIsInitialScrollPending,
      pendingInitialScroll, setPendingInitialScroll,
      showScrollButton, setShowScrollButton, hasNewMessage,
      messagesEndRef, messagesAreaRef, shouldAutoScrollRef, isProgrammaticScrollRef,
      lastAssistantMsgRef, lastUserMsgRef, lastTurnWrapperRef, lockAutoScrollRef, embedScrollRef,
      getViewportHeight, getScrollTop, doScrollTo, getElementScrollOffset,
      scheduleProgrammaticScrollEnd, scrollToLatestMessage, scrollRestoredHistoryIntoView,
      beginPhoneSend, settleScroll, isPhoneTurn, noteTypingSettled,
      handleScroll, handleScrollButtonClick,
    } = useScrollBehavior({ messages, isStreaming });
    // TRACI-256: bring a live rep's reply on screen when it arrives (desktop).
    const { noteRepReply, claimRepReveal, revealRepReply } = useRepReplyReveal({
      embedScrollRef, lastAssistantMsgRef, lastUserMsgRef, isProgrammaticScrollRef, isPhoneTurn,
      getElementScrollOffset, doScrollTo, scheduleProgrammaticScrollEnd, setShowScrollButton,
    });

    // ─────────── TRAC-732 handoff: state, refs, effects (ported onto Dylan's structure) ───────────
    // TRAC-790: set when the chat is opened asking for a person; drained by the effect
    // below showRoutingPrompt, once the opening has rendered and can be read.
    const [humanRequested, setHumanRequested] = useState(false);
    // TRAC-790: bumped whenever somebody states how they want the chat opened, so an
    // opening built before the request arrived is rebuilt with it. Only matters
    // pre-engagement — once there's a conversation the welcome is frozen to it, and the
    // request is answered in the thread instead (see the humanRequested effect).
    const [openIntentTick, setOpenIntentTick] = useState(0);
    useEffect(() => {
      const onIntent = () => setOpenIntentTick((n) => n + 1);
      window.addEventListener(OPEN_INTENT_EVENT, onIntent);
      return () => window.removeEventListener(OPEN_INTENT_EVENT, onIntent);
    }, []);
    // TRAC-732: "we're open at…" popup, shown when the customer taps the button
    // while live chat is closed (outside LP hours).
    const [hoursPopupOpen, setHoursPopupOpen] = useState(false);
    const hoursTrayRef = useRef<HTMLDivElement>(null);
    // TRAC-732: true after the customer requests live chat (routing prompt shown) until
    // they pick an option or reply — locks the "Live Chat Available" button so a second
    // click can't double-fire the handoff.
    const [routingPending, setRoutingPending] = useState(() => seededHandoffAttempt?.routingPending ?? false);
    // TRAC-732: which fun fact is showing, drawn from a shuffle-bag (see the effect below)
    // so we cycle through all of them in random order before repeating.
    const factBagRef = useRef<number[]>(shuffledFactBag(TIRE_FUN_FACTS.length));
    const factBagPosRef = useRef(0);
    const [factIndex, setFactIndex] = useState(() => factBagRef.current[0]);
    // TRAC-732: same click-outside dismissal for the "we're open at…" popup.
    useEffect(() => {
      if (!hoursPopupOpen) return;
      const onOutside = (e: MouseEvent | TouchEvent) => {
        const tray = hoursTrayRef.current;
        if (tray && !tray.contains(e.target as Node)) setHoursPopupOpen(false);
      };
      document.addEventListener('mousedown', onOutside);
      document.addEventListener('touchstart', onOutside);
      return () => {
        document.removeEventListener('mousedown', onOutside);
        document.removeEventListener('touchstart', onOutside);
      };
    }, [hoursPopupOpen]);
    // TRAC-732: live-chat availability gate (working hours + agents online). Polled
    // so the button enables/disables itself. Start in 'checking', NEVER 'open' — we
    // must not flash "Live Chat Available" optimistically and then yank it when the
    // first check resolves to closed. The button only shows "available" AFTER a
    // confirmed-open result.
    const [liveChatAvail, setLiveChatAvail] = useState<LiveChatAvailability>({ state: 'checking', hours: null, wait_seconds: null, schedule: [] });
    // TRAC-965: tell Adobe "live chat available" ONCE per app mount — on the first answer
    // LivePerson really said was open. Never per poll, and never on a fail-open answer (the
    // service says 'open' on any error so the button never hides on a hiccup; the button
    // text is the product decision, the Adobe number is the measurement, and a swallowed
    // error is not a reachable desk). Per mount = per page view under the page-level shell
    // and per first open under the site's own host code — each control's own population.
    const liveChatAvailableEmittedRef = useRef(false);
    useEffect(() => {
      let alive = true;
      const fetchAvail = async () => {
        const a = await chatAgentService.getLiveChatAvailability();
        if (!alive) return;
        setLiveChatAvail(a);
        if (a.state === 'open' && !a.failOpen && !liveChatAvailableEmittedRef.current) {
          liveChatAvailableEmittedRef.current = true;
          const msg = postLiveChatEvent(window, 'live_chat_available', chatAgentService.agentSessionId);
          logger.info('[TRAC-965] live chat available', msg ?? '(not embedded — no postMessage sent)');
        }
      };
      fetchAvail();
      const id = setInterval(fetchAvail, 60000);
      // Dev settings drawer flipped the hours override: re-check now, not in a minute.
      window.addEventListener(HOURS_CHANGED_EVENT, fetchAvail);
      return () => { alive = false; clearInterval(id); window.removeEventListener(HOURS_CHANGED_EVENT, fetchAvail); };
    }, []);
    // TRACI-425: the 60 s poll can reopen live chat while the popup is up; its copy
    // would then be wrong, and the banner already offers the way in.
    useEffect(() => {
      if (liveChatAvail.state === 'open') setHoursPopupOpen(false);
    }, [liveChatAvail.state]);

    const scrollTopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const scheduleScrollTurnToTop = (releaseLockAfter = false): void => {
      // TRAC-1132 PR 2: a phone keeps the newest message at the bottom; no anchor, no spacer.
      if (beginPhoneSend()) { requestAnimationFrame(() => settleScroll()); return; }
      shouldAutoScrollRef.current = false;
      lockAutoScrollRef.current = true;
      if (scrollTopTimerRef.current) clearTimeout(scrollTopTimerRef.current);
      scrollTopTimerRef.current = setTimeout(() => {
        scrollTopTimerRef.current = null;
        const anchor = lastUserMsgRef.current;
        const wrapper = lastTurnWrapperRef.current;
        if (anchor) {
          if (wrapper) wrapper.style.minHeight = `${getViewportHeight()}px`;
          isProgrammaticScrollRef.current = true;
          const targetScroll = Math.max(0, getElementScrollOffset(anchor) - ANCHOR_TOP_GAP_PX);
          doScrollTo({ top: targetScroll, behavior: 'smooth' });
          scheduleProgrammaticScrollEnd(1000);
          // Escalate has no agent stream to release the scroll lock, so release it once
          // the smooth scroll settles — otherwise the user can't scroll back up freely.
          if (releaseLockAfter) {
            setTimeout(() => {
              lockAutoScrollRef.current = false;
              isProgrammaticScrollRef.current = false;
            }, 1100);
          }
        }
      }, 300);
    };


    const [handoffState, setHandoffState] = useState<'idle' | 'connecting' | 'queued' | 'active' | 'ended'>(() => seededHandoffAttempt?.state ?? 'idle');
    const [handoffAgentName, setHandoffAgentName] = useState('Tire Rack team');
    const [handoffAgentAvatar, setHandoffAgentAvatar] = useState<string | null>(null);
    // TRAC-732: end-of-chat framing. On chat-end we anchor the view on the
    // "Chat with {name} ended" divider — it's the line that tells the customer the
    // live chat is over. `endHumanRef` points at the LAST such divider (the one the
    // survey sits under); `endSpacerPx` is the exact trailing room that lets that
    // divider scroll UP to the top without letting the block over-scroll off-screen;
    // `endOfChatFramedRef` makes the one-shot glide-into-frame fire only once.
    const endHumanRef = useRef<HTMLDivElement | null>(null);
    const endOfChatFramedRef = useRef(false);
    const [endSpacerPx, setEndSpacerPx] = useState(0);
    // TRAC-732: the framing "settle guard" — a short rAF loop that re-pins the divider to
    // the top through late layout shifts (the welcome-back reply's feedback tray appears a
    // beat after it streams and would otherwise clamp the scroll back down) and any competing
    // programmatic scroll. It bows out the instant the customer actively scrolls.
    const endFrameGuardRafRef = useRef<number | null>(null);
    const endFrameUserScrolledRef = useRef(false);
    const endFrameCleanupRef = useRef<(() => void) | null>(null);
    // Miles 7/15: the mode banner starts full-size and squishes thin once the customer
    // engages — either by sending/picking an option, or by scrolling off the top. It
    // expands again at scroll-top, and a fresh mode switch resets it to full (below).
    const [bannerInteracted, setBannerInteracted] = useState(false);
    const [bannerScrolled, setBannerScrolled] = useState(false);
    const bannerSquished = bannerInteracted || bannerScrolled;
    // A fresh mode switch resets the banner to full. The mockup can assume scroll is at
    // the top here because its toggle also resets scrollTop; ours doesn't, so re-read the
    // real position — otherwise a switch mid-thread leaves it full while scrolled down.
    // The squish is a ONE-WAY latch now: once the customer scrolls, sends, or taps,
    // the banner becomes the slim top bar and STAYS there — no grow-back at scroll-top.
    // So we only ever latch it squished here, never reset it.
    useEffect(() => {
      const el = embedScrollRef.current;
      if (el && el.scrollTop > 8) setBannerScrolled(true);
    }, [handoffState, embedScrollRef]);
    // useScrollBehavior's handleScroll takes no event (it reads the container off its
    // own ref), so read scrollTop directly rather than threading one through.
    const handleScrollWithBanner = useCallback(() => {
      const el = embedScrollRef.current;
      if (el && el.scrollTop > 8) setBannerScrolled(true);   // one-way latch (no grow-back)
      handleScroll();
    }, [handleScroll, embedScrollRef]);
    // live transient status shown at the bottom of the thread during a handoff
    const [handoffActivity, setHandoffActivity] = useState<'typing' | 'awaiting' | null>(null);
    // TRAC-732: while the customer waits in the live-chat queue, cycle a tire fun fact
    // every 9s (the "Did you know?" loader) so we're not just repeating "connecting you".
    useEffect(() => {
      // Only cycle facts PRE-connection; once the rep joins it's "Waiting on {name}".
      if (handoffActivity !== 'awaiting' || handoffState === 'active') return;
      // Fresh shuffle each time a wait begins; open on a random fact (avoid repeating the
      // one currently on screen), then step through the bag, reshuffling when it's spent.
      factBagRef.current = shuffledFactBag(TIRE_FUN_FACTS.length, factIndex);
      factBagPosRef.current = 0;
      setFactIndex(factBagRef.current[0]);
      const id = setInterval(() => {
        factBagPosRef.current += 1;
        if (factBagPosRef.current >= factBagRef.current.length) {
          // Reshuffle for the next lap, avoiding a back-to-back repeat across the seam.
          factBagRef.current = shuffledFactBag(TIRE_FUN_FACTS.length, factBagRef.current[factBagRef.current.length - 1]);
          factBagPosRef.current = 0;
        }
        setFactIndex(factBagRef.current[factBagPosRef.current]);
      }, 9000);
      return () => clearInterval(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handoffActivity, handoffState]);
    const handoffStateRef = useRef<'idle' | 'connecting' | 'queued' | 'active' | 'ended'>(seededHandoffAttempt?.state ?? 'idle');
    // TRAC-1006: keep the persisted handoff attempt in step with the page. An effect rather
    // than a write inside each setter, because routingPending alone is set from a dozen
    // places (useChatStream included) and the one that got missed would be the next version
    // of this bug. The dismissal is read from its ref; every change to it is paired with an
    // announcementDismissed change, which is what re-runs this.
    const routingPendingRef = useRef(routingPending);
    // TRAC-475: the agent has asked to connect the customer now and escalateToHuman has not
    // started yet (it waits for the reply to finish streaming). Until it does, the record says
    // what a press says before its request leaves -- connecting, pressed -- so a reload in that
    // window is re-sent by the next page (TRAC-1066's path) instead of left in silence.
    const agentEscalateDueRef = useRef(false);
    const persistHandoffAttempt = () => {
      if (isDemoMode) return;
      const agentDue = agentEscalateDueRef.current && !handoffEngaged(handoffStateRef.current);
      chatAgentService.saveHandoffAttempt({
        state: agentDue ? 'connecting' : handoffStateRef.current,
        routingPending: routingPendingRef.current,
        overlayDismissed: overlayDismissedForHandoffRef.current,
        department: (agentDue ? pendingHandoffDeptRef.current : attemptDeptRef.current) ?? undefined,
        slowNoticeFor: slowNoticeForRef.current ?? undefined,
        escalateRequest: agentDue ? 'pressed' : escalateRequestRef.current ?? undefined,
        initiatedBy: (agentDue ? agentHandoffInitiatorRef.current : attemptInitiatorRef.current) ?? undefined,
      });
    };
    const persistHandoffAttemptRef = useRef(persistHandoffAttempt);
    persistHandoffAttemptRef.current = persistHandoffAttempt;
    useEffect(() => {
      routingPendingRef.current = routingPending;
      persistHandoffAttempt();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handoffState, routingPending, announcementDismissed]);
    // TRAC-1006: the retrying resume check outlives renders; this is how it learns the
    // component is gone. Set in the effect body (not only initialised) so StrictMode's
    // mount/unmount/mount rehearsal does not leave it reading "unmounted".
    const unmountedRef = useRef(false);
    useEffect(() => {
      unmountedRef.current = false;
      return () => {
        unmountedRef.current = true;
        // TRAC-1066: nothing should still be asking about a handoff on a page that is gone.
        if (connectingSettleTimerRef.current) {
          clearTimeout(connectingSettleTimerRef.current);
          connectingSettleTimerRef.current = null;
        }
      };
    }, []);
    // TRAC-732: bumped on every (re)escalation to a human. The AI "welcome back" comeback captures
    // this at send time; if it changes before the comeback finishes, the customer re-engaged a rep
    // and the comeback is stale — the stream loop drops it. Kills the "comeback lands after the
    // second leave, out of place" bug.
    const escalateGenRef = useRef(0);
    // TRAC-1006: bumped whenever the customer ends a handoff themselves (Leave, Clear
    // conversation). A resume check still retrying from page load must not apply an answer
    // that was sent before the leave.
    const leaveGenRef = useRef(0);
    // TRAC-732 (per-department feedback): the escalate-generation we last dropped a survey
    // card for. Dedups a survey per CHAT INSTANCE (one card per chat-end, even if the same
    // end fires twice), while still letting a RECONNECT to the same department get its own
    // fresh card — that card's submit overwrites the department's stored record (latest wins).
    const lastSurveyGenRef = useRef<number>(-1);
    const handoffAgentNameRef = useRef('Tire Rack team');
    const handoffAgentAvatarRef = useRef<string | null>(null);
    // The rep's real display name for funnel telemetry — undefined until identity
    // resolves, never the "Tire Rack team" placeholder (a placeholder in the traces
    // would read as a rep literally named that).
    const liveRepName = () => {
      const n = handoffAgentNameRef.current;
      return n && n !== 'Tire Rack team' ? n : undefined;
    };
    // TRACI-1012: a line in the live chat when the rep sends the payment form and when the
    // customer submits it, so both sides of the chat show it happened. Client-only: never
    // sent to LivePerson as transcript (LivePerson records the form on its own side).
    useEffect(() => {
      const onStatus = (e: Event) => {
        const d = ((e as CustomEvent).detail || {}) as { state?: string; agentName?: string };
        if (handoffStateRef.current === 'idle') return;
        const name = d.agentName || liveRepName() || 'Your Tire Rack expert';
        const submitted = d.state === 'submitted';
        setMessages((prev) => {
          const next = [...prev, {
            id: `sf-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
            role: 'assistant',
            content: submitted
              ? `Payment details sent securely to ${name}.`
              : `${name} sent you a secure payment form.`,
            timestamp: new Date(),
            responder: 'system',
            secureFormNote: submitted ? 'submitted' : 'sent',
            inHumanZone: true,
            isClientOnly: true,
          } as Message];
          chatAgentService.saveMessagesToSession(next);
          return next;
        });
      };
      window.addEventListener(SECURE_FORM_STATUS_EVENT, onStatus);
      return () => window.removeEventListener(SECURE_FORM_STATUS_EVENT, onStatus);
    }, []);
    // TRAC-732: true while a transfer's next rep is connecting, so the "connecting/connected"
    // copy reads "Transferring…/Transferred" instead of "Connecting…/Connected".
    const pendingTransferRef = useRef(false);
    // TRAC-732: set when the agent triggers a handoff mid-stream; the actual handoff
    // fires when the turn finishes (so it doesn't fight the stream).
    const pendingHandoffRef = useRef(false);
    // TRAC-732: the department chosen for the handoff (Sales / Customer Care),
    // forwarded to the backend so it routes to the right LP skill. Null for a manual
    // "live chat" click → the backend classifies the chat instead.
    const pendingHandoffDeptRef = useRef<string | null>(null);
    // TRAC-732 (per-department feedback): the department of the CURRENTLY-live handoff,
    // resolved once it connects. Read when that chat ends so the survey card is tagged
    // with the right team — each department chatted with gets its own survey + CSAT.
    const activeHandoffDeptRef = useRef<string | null>(null);
    // TRAC-732: soft-handoff — when escalation is "offer" (a topic the AI can't
    // cover, not an explicit human request), we show the 10s "Connecting you to a Tire
    // Expert" card instead of forwarding immediately.
    const pendingHandoffOfferRef = useRef(false);
    // TRAC-732: department for a soft-handoff card whose display is DEFERRED until the
    // agent's reply finishes typing (set at stream-complete, consumed by onTypingSettled).
    const pendingSoftHandoffDeptRef = useRef<string | null>(null);
    // TRAC-732: id of the "Connecting you now…" turn so we can flip it to
    // "Connected ✅" in place once a human actually joins.
    const connectingMsgIdRef = useRef<string | null>(null);
    // TRAC-732: id of the current cycle's enter-human band header, so we can flip
    // it from "Connecting…" (pending) to "Chat with {name} started" when the human joins.
    // ─────────── end TRAC-732 handoff state block ───────────
    useEffect(() => {
      // Event-driven: get notified the instant context arrives (or immediately if already set)
      const unsubscribe = chatAgentService.onPageContext((ctx) => {
        setPageContext(ctx);
        setPageContextReady(true);
      });
      // Safety fallback: if no postMessage after 3s, show default greeting
      const timeout = setTimeout(() => {
        setPageContextReady(true);
      }, 3000);
      return () => {
        unsubscribe();
        clearTimeout(timeout);
      };
    }, []);

    // TRAC-468: initSession() is called directly from setPartnerToken() and setTokenGetter()
    // in the service singleton, so it fires immediately when auth is configured — no React
    // timing issues. The pending/active lifecycle (TRAC-464) ensures validation is skipped
    // for sessions that haven't sent a message yet.

    // TRAC-518 Phase 8a follow-up: mirror the service's agentSessionId into
    // React state so MessageRenderer re-renders (and fires starter-prompt
    // impressions) the moment initSession() resolves with a pregenerated ID.
    const [agentSessionIdState, setAgentSessionIdState] = useState<string | null>(
      chatAgentService.agentSessionId
    );
    useEffect(() => {
      const unsubscribe = chatAgentService.onAgentSessionIdChange((id) => {
        setAgentSessionIdState(id);
      });
      return unsubscribe;
    }, []);
    // TRAC-732: the live-person funnel needs the session to attribute anything, and it
    // arms a page-leave listener that is how "gave up waiting" gets caught at all.
    useEffect(() => {
      if (!agentSessionIdState) return;
      handoffFunnel.initHandoffFunnel(agentSessionIdState, getToken);
      // TRAC-1049: this tab may not be the one that pressed the button. The per-tab attempt
      // record is another tab's sessionStorage; the funnel's is beside the transcript, shared,
      // and carries the team. Taken here so the still-trying line holds the right space from
      // the start, exactly as it does in the tab that asked.
      if (!attemptDeptRef.current) {
        attemptDeptRef.current = handoffFunnel.handoffAskedDepartment().department;
      }
    }, [agentSessionIdState, getToken]);

    // TRAC-1049: a tab opened in the second or two between the press and the backend naming the
    // team resumes with the generic heading, and nothing else would ever tell it. The tab that
    // asked writes the CONFIRMED team to the shared record; the browser tells every other tab of
    // the origin through a storage event. Bring this tab's heading (and the line under it) up to
    // that team. Only the exact generic wording is replaced — never a "Connected", a transfer, or
    // a heading that already names a team.
    useEffect(() => {
      const onStorage = (e: StorageEvent) => {
        if (e.key !== OPEN_ATTEMPT_STORAGE_KEY) return;
        const confirmed = handoffFunnel.handoffConfirmedDepartment();
        if (!confirmed) return;
        attemptDeptRef.current = confirmed;
        const generic = connectingLabel(null);
        setMessages((prev) => {
          let changed = false;
          const next = prev.map((m) => {
            if (!m.isConnectingTurn || m.content !== generic) return m;
            changed = true;
            return { ...m, content: connectingLabel(confirmed) };
          });
          if (!changed) return prev;
          messagesRef.current = next;
          chatAgentService.saveMessagesToSession(next);
          return next;
        });
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    }, []);

    // TRAC-1007: nobody has picked up. A minute after the customer asked for a person, while the
    // handoff is still connecting or queued, tell them we are still trying and give the phone
    // number for their team. They stay in the queue: nothing here touches the handoff, the poll
    // or LivePerson, and a rep who joins afterwards connects exactly as before.
    //
    // The clock is the funnel's ask, so a page change neither restarts nor forgets it. Declared
    // after the funnel's init above so that, on a new page, the ask has been read back first.
    // Once per attempt: the ask it was shown for is kept in the attempt record, and the message
    // id carries it too, so a second tab reading the same transcript does not add another.
    // Everything is re-checked when the timer fires, so a rep who joined, a leave, or an ending
    // in the meantime means no message.
    //
    // TRAC-1033: on dev it never appeared. Every tick waited on hasLoadedFromStorageRef, which a
    // new chat (the header's trash can) and the first-message retry drop without ever setting
    // again, so after either of those this rescheduled itself every 250 ms until the handoff
    // ended. The transcript gate is now a flag only the mount-time load owns. The ask used to be
    // read once and given up on when it was not there yet; no path today flips the state ahead of
    // the ask, but nothing guaranteed it, so it is read on every tick as well.
    useEffect(() => {
      if (isDemoMode) return;
      if (handoffState !== 'connecting' && handoffState !== 'queued') return;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const check = () => {
        if (timer) { clearTimeout(timer); timer = null; }
        const st = handoffStateRef.current;
        if (st !== 'connecting' && st !== 'queued') return;
        // The ask is whichever attempt is open NOW, so a newer attempt runs on its own clock. No
        // ask yet: look again, quickly while the page is still coming up, then at the usual
        // cadence, since each look with nothing waiting is a storage read.
        const askedAt = handoffFunnel.handoffAskedAt();
        if (askedAt === null) {
          timer = setTimeout(check, transcriptRestoredRef.current ? HANDOFF_SLOW_RECHECK_MS : 250);
          return;
        }
        const id = `handoff-slow-${askedAt}`;
        if (slowNoticeForRef.current === askedAt) return;
        // The restored transcript is what says another page or tab already showed it.
        if (!transcriptRestoredRef.current) { timer = setTimeout(check, 250); return; }
        if (messagesRef.current.some((m) => m.id === id)) { slowNoticeForRef.current = askedAt; return; }
        const left = askedAt + HANDOFF_SLOW_AFTER_MS - Date.now();
        if (left > 0) { timer = setTimeout(check, Math.min(left, HANDOFF_SLOW_RECHECK_MS)); return; }
        slowNoticeForRef.current = askedAt;
        // TRAC-1049: the team the customer picked lives in the per-TAB attempt record, so a
        // second tab of the same chat has none and used to write the both-numbers wording into
        // the shared transcript. The clock is shared (localStorage), so that tab's check does
        // fire. Two shared sources answer instead, in the order that keeps the line agreeing
        // with the heading above it: this attempt's heading in the transcript first (a heading
        // that names a team always wins, so the two can never name different ones), then the
        // shared attempt record — the same team the heading itself was drawn from in a tab that
        // never saw the press. All three stay null only when the wait truly has no team.
        const department = attemptDeptRef.current
          ?? connectingTurnDepartment(messagesRef.current)
          ?? handoffFunnel.handoffAskedDepartment().department;
        setMessages((prev) => {
          if (prev.some((m) => m.id === id)) return prev;
          const next = [...prev, {
            id,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isClientOnly: true,   // never sent to the agent or the rep
            inHumanZone: true,    // inside the waiting band, like anything else said while queued
            parts: [{ type: 'text', content: handoffSlowMessage(department) }],
          } as Message];
          chatAgentService.saveMessagesToSession(next);
          return next;
        });
        persistHandoffAttemptRef.current();
        handoffFunnel.handoffSlow(department ?? undefined);
      };
      check();
      // A background tab's timers are throttled; the moment it is looked at again, check now.
      const onVisible = () => { if (document.visibilityState === 'visible') check(); };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        if (timer) clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisible);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handoffState, agentSessionIdState, isDemoMode]);

    // TRAC-291: Set activeVehicle from pageContext so the header bar appears immediately
    useEffect(() => {
      if (pageContext?.vehicle_year && pageContext?.vehicle_make && pageContext?.vehicle_model) {
        const desc = [pageContext.vehicle_year, pageContext.vehicle_make, pageContext.vehicle_model, pageContext.vehicle_trim].filter(Boolean).join(" ");
        // Sizes the SITE already knows (Shop-by-Vehicle flow) ride the canonical
        // workingContext (page_selected_size wins over page_searched_size, both
        // normalized by buildWorkingContextFromV3). Without this the chip said
        // "Add Tire Size" on pages whose URL literally carried both sizes.
        const wcSize = chatAgentService.getWorkingContext()?.page_selected_size
          ?? chatAgentService.getWorkingContext()?.page_searched_size ?? null;
        const front = pageContext.front_tire_size ?? wcSize?.front;
        const rear = pageContext.rear_tire_size ?? wcSize?.rear;
        const staggered = !!(front && rear && front !== rear);
        setActiveVehicle((prev) => {
          // A tool-set vehicle (YMMV pick) outranks page context; but a chip we
          // ourselves derived from page context refreshes as context updates
          // (post-Clear re-request, size appearing later in the shop flow).
          if (prev && prev.source !== "pdp_context") return prev;
          return {
            id: `pdp-${Date.now()}`,
            description: desc,
            year: Number(pageContext.vehicle_year),
            make: pageContext.vehicle_make!,
            model: pageContext.vehicle_model!,
            variant: pageContext.vehicle_trim,
            tireSize: staggered ? undefined : (pageContext.tire_size ?? (front && front === rear ? front : front)),
            staggered,
            frontSize: staggered ? front : pageContext.front_tire_size,
            rearSize: staggered ? rear : pageContext.rear_tire_size,
            source: "pdp_context",
            timestamp: Date.now(),
          };
        });
      }
    }, [pageContext]);

    // TRAC-454: When VehicleHeaderBar appears (or disappears), immediately re-scroll
    // to account for the changed header height. Without this, the vehicle header
    // overlays content until the next natural scroll event.
    useEffect(() => {
      const wasHidden = !prevActiveVehicleRef.current;
      const isNowVisible = !!activeVehicle;
      prevActiveVehicleRef.current = activeVehicle;

      if (wasHidden && isNowVisible) {
        // Wait two frames: one for React render, one for layout
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToLatestMessage("smooth");
          });
        });
      }
    }, [activeVehicle]);

    // Fetch starter prompts from API (TRAC-454: simplified — just sorted prompts, filtered by page_name)
    // Waits for pageContextReady so we know whether to filter by page_name.
    // Standalone mode resolves instantly (ChatWidget responds to context request immediately).
    useEffect(() => {
      if (!pageContextReady) return;
      const pageName = pageContext?.page_name || null;

      const fetchPrompts = async () => {
        try {
          const url = pageName
            ? `${getBackendApiUrl()}/api/starter-prompts/?page_name=${encodeURIComponent(pageName)}`
            : `${getBackendApiUrl()}/api/starter-prompts/`;

          const res = await fetch(url);
          if (res.ok) {
            const prompts = await res.json();
            if (Array.isArray(prompts) && prompts.length > 0) {

              setStarterPrompts(
                prompts
                  .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
                  .map((p: any) => ({
                    text: p.text,
                    icon: p.icon,
                    // TRAC-1108: the row id rides the starter prompt click, never the text.
                    ...(typeof p.promptId === 'string' && { promptId: p.promptId }),
                    ...(typeof p.promptKind === 'string' && { promptKind: p.promptKind }),
                  }))
              );
              return;
            }
          }
          // No prompts came back (empty list, or a non-OK response) — fall back to
          // the built-in set. This used to also assign a `starterPromptsFetchedForRef`
          // that was never declared and never read anywhere, so the only thing the
          // line did was throw ReferenceError into the catch below, which logged an
          // empty prompt list as "Failed to fetch" and then applied these same
          // defaults. Removed rather than declared: nothing reads it.
          setStarterPrompts(DEFAULT_STARTER_PROMPTS);
        } catch (error) {
          logger.error('[StarterPrompts] Failed to fetch:', error);
          setStarterPrompts(DEFAULT_STARTER_PROMPTS);
        }
      };

      fetchPrompts();
    }, [pageContextReady, pageContext]);

    // Session expiry notification message (TRAC-189)
    const SESSION_EXPIRY_MESSAGE: Message = useMemo(() => ({
      id: 'session-expiry-notice',
      role: 'assistant' as const,
      content: 'Your session has expired due to inactivity. Starting fresh.',
      timestamp: new Date(),
      isClientOnly: true,
      isSessionExpiry: true,
    }), []);

    // TRAC-478: Build a snapshot of the currently-synthesized welcome message for committing
    // into the real messages array on first user action. Returns null when welcome wouldn't
    // have been shown (expiry, not-ready, no prompts).
    const buildCommittedWelcome = useCallback((selectedValue?: string): Message | null => {
      if (!pageContextReady || !(starterPrompts.length > 0 || pageContext)) {
        return null;
      }
      const limitedPrompts = visiblePromptCount != null
        ? starterPrompts.slice(0, visiblePromptCount)
        : starterPrompts;
      // Same conditions as the on-screen welcome, so what gets frozen into the
      // conversation is what the customer actually saw.
      const welcome = createWelcomeMessage(limitedPrompts, pageContext, {
        liveChatClosed: liveChatClosedFrom(liveChatAvail.state),
        reopenNote: liveChatAvail.hours,
        livePersonOnly: liveChatAvail.livePersonOnly === true,
      });
      const committed: Message = { ...welcome, isCommittedWelcome: true };
      if (selectedValue && committed.parts) {
        committed.parts = committed.parts.map(p =>
          p.type === 'follow_up_actions' ? { ...p, selectedValue } : p
        );
      }
      return committed;
    }, [pageContextReady, starterPrompts, pageContext, visiblePromptCount,
        liveChatAvail.state, liveChatAvail.hours, liveChatAvail.livePersonOnly]);

    // TRAC-637: Build the optimistic welcome upfront so we can hand it to
    // the persistence hook. The hook either renders the optimistic version
    // (first visit, nothing stored) or a localStorage-hydrated copy on
    // subsequent mounts. "Frozen at first contextual welcome" semantics:
    // navigating to a different PDP in-session keeps the original welcome.
    const optimisticWelcome = useMemo<Message | null>(() => {
      if (!pageContextReady) return null;
      if (starterPrompts.length === 0 && !pageContext) return null;
      // TRAC-790: an opening that offers doors to a person cannot be drawn until we know
      // whether anyone is there — drawing it first means flipping it from red to blue in
      // front of the customer. An opening that offers the agent waits for nothing, which
      // is every opening on a tire page and every plain open.
      if (openingUndecided(liveChatAvail.state, pageContext)) return null;
      const limitedPrompts = visiblePromptCount != null
        ? starterPrompts.slice(0, visiblePromptCount)
        : starterPrompts;
      return createWelcomeMessage(limitedPrompts, pageContext, {
        liveChatClosed: liveChatClosedFrom(liveChatAvail.state),
        reopenNote: liveChatAvail.hours,
        livePersonOnly: liveChatAvail.livePersonOnly === true,
      });
      // openIntentTick is a dependency because createWelcomeMessage reads the requested
      // opening, and that request can arrive AFTER this first ran (see OPEN_INTENT_EVENT).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageContextReady, starterPrompts, pageContext, visiblePromptCount,
        openIntentTick, liveChatAvail.state, liveChatAvail.hours, liveChatAvail.livePersonOnly]);

    // TRAC-637: persist the welcome only once the user has actually engaged
    // (sent at least one message). Pre-engagement, the optimistic welcome
    // floats with pageContext so PDP nav updates the greeting naturally.
    // At engagement, whatever welcome is on screen gets snapshotted and
    // frozen — that's the one in their conversation history.
    const hasUserEngagedWithWelcome = useMemo(
      () => messages.some((m) => m.role === 'user'),
      [messages],
    );
    const { welcome: persistedOrOptimisticWelcome } = usePersistedWelcome({
      optimisticWelcome,
      tireName: pageContext?.tire_name ?? null,
      starterPrompts: starterPrompts.map((p) => p.text),
      enabled: pageContextReady,
      clearSignal: welcomeClearSignal,
      hasUserEngaged: hasUserEngagedWithWelcome,
      // Miles 2026-07-27: a frozen welcome belongs to ITS conversation only.
      // With messages in the chat the welcome must not change (incl. reloads);
      // with no messages it floats with the current page — so a snapshot from
      // an old conversation must never resurrect into a new one.
      conversationId: agentSessionIdState,
    });

    // Display messages — slot the (possibly-hydrated) welcome above conversation.
    const displayMessages = useMemo(() => {
      const prefix: Message[] = [];

      // TRAC-203: Show expiry notice at top when session expired
      // Note: auto-resend paths (TRAC-468) no longer set sessionExpired — this only
      // triggers for the useSessionSync page-load expiry path (handleSessionClear).
      if (sessionExpired) {
        prefix.push(SESSION_EXPIRY_MESSAGE);
      }

      // TRAC-478: Skip synthesizing a welcome prefix when one is already committed in messages.
      // TRAC-637: Prefer the hook's welcome (persisted-when-available; optimistic otherwise).
      const hasCommittedWelcome = messages.some(m => m.isCommittedWelcome);
      // TRAC-732: never replay the AI greeting once a handoff exists. A handoff that started from
      // the routing prompt / "connect me" click never commits the welcome, so on reload the
      // prefix logic used to re-inject the "I'm a tire shopping assistant…" greeting (with its
      // starter-prompt buttons) into a live human chat. If there's ANY handoff/band content, the
      // conversation is clearly past the greeting — suppress it.
      const hasHandoffContent = messages.some(m =>
        m.responder || m.inHumanZone || m.isConnectingTurn || m.isRoutingPrompt);
      const welcomeToShow = persistedOrOptimisticWelcome ?? optimisticWelcome;
      if (!hasCommittedWelcome && !hasHandoffContent && welcomeToShow) {
        prefix.push(welcomeToShow as Message);
      }

      // TRAC-732: when a handoff is present, also drop any welcome that's a real message in the
      // array (a committed welcome / welcome-kind turn) — otherwise the greeting still renders
      // mid-transcript on reload. The prefix suppression above only covers the synthesized one.
      // TRAC-732: drop a phantom EMPTY customer message inside the human band. A duplicate,
      // content-less user turn was slipping into the band and rendering nothing but a second
      // timestamp under the real message (the "message, then two 2:23 PM stamps" bug). Filtering
      // it here removes it from BOTH the render and the band's run/border math. Guarded tightly:
      // only a user, in-band turn with no text and no card parts — never a real message, the
      // "Connecting…" turn, or a divider.
      const isEmptyBandUserMsg = (m: Message) =>
        m.role === 'user' && m.inHumanZone && !m.isConnectingTurn && !m.dividerKind &&
        !(m.content || '').trim() &&
        !(m.parts || []).some(p => p.type !== 'text' || (((p as { content?: string }).content) || '').trim());
      // TRAC-1035: the still-trying message (TRAC-1007) is saved like any other turn but not
      // drawn as one. As a block inside the waiting card it sat over the "Did you know?" tip and
      // pushed it down; it is drawn as a line under the "Connecting you…" heading instead (see
      // waitStatus below). Filtered here, like the empty band message above, so it is out of the
      // band's run/border math as well as the render.
      const body = (hasHandoffContent
        ? messages.filter(m => !m.isCommittedWelcome && effectiveKind(m) !== 'welcome')
        : messages
      ).filter(m => !isEmptyBandUserMsg(m) && !isHandoffSlowNotice(m));

      // If we have prefix messages or conversation messages, combine them
      if (prefix.length > 0) {
        return [...prefix, ...body];
      }
      return body;
    }, [sessionExpired, messages, SESSION_EXPIRY_MESSAGE, persistedOrOptimisticWelcome, optimisticWelcome]);

    // TRACI-910: tell the service which opening the shopper is looking at (doors to a person
    // or the agent), read off the welcome actually drawn, so the staff live chat page can say
    // "LivePerson first" or "AI first". The backend keeps the first value per chat.
    useEffect(() => {
      const drawn = displayMessages.find((m) => m.isCommittedWelcome || effectiveKind(m) === 'welcome');
      if (drawn) chatAgentService.setOpening?.(openingKind(drawn));
    }, [displayMessages]);

    // TRAC-1108: "experiment exposed" is the starter prompts of the session's arm actually
    // in front of the customer: read off the messages drawn (a welcome held back by a
    // handoff never counts) and only while the panel is open (the shell loads the chat
    // collapsed on every page view). postExperimentExposed holds it to once per session
    // per experiment.
    const promptsExposureDue = useMemo(
      () => exposureDue(panelOpen, displayMessages, agentSessionIdState),
      [panelOpen, displayMessages, agentSessionIdState],
    );
    const promptsPageBucket = pageContext?.page_kind || 'unknown';
    useEffect(() => {
      if (!promptsExposureDue || !agentSessionIdState) return;
      for (const { experimentId, arm } of starterPromptExperimentArms(agentSessionIdState)) {
        postExperimentExposed(window, agentSessionIdState, { experimentId, arm, pageBucket: promptsPageBucket });
      }
    }, [promptsExposureDue, agentSessionIdState, promptsPageBucket]);

    // TRAC-1035: which "Connecting you…" heading carries the still-trying line, and what it says.
    // Until the message is saved the line is held empty, sized for the team this attempt is
    // asking for. attemptDeptRef is only ever set alongside a messages or handoff-state change
    // (a new attempt, or the backend naming the team), which is what recomputes this.
    const waitStatus = useMemo(
      () => handoffWaitStatus(messages, attemptDeptRef.current),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [messages, handoffState],
    );

    // TRAC-891: latest COMPLETED agent turn — evidence for the QA sweep verdict
    // flow. Turn numbering matches the per-message calculation in renderMessage
    // (count of prior non-client-only assistant messages over displayMessages),
    // so the number lines up with what TurnFeedback / data-message-turn report
    // for the same message. Candidates skip streaming turns (not completed),
    // client-only injections (no backend turn), and human-band/system messages
    // (responder set — not agent turns). Same shape as the WP4 in-embed panel's
    // latestCompletedTurn (PR #1589) so the two stay mergeable.
    const latestCompletedTurn = useMemo((): { turnNumber: number; langfuseTraceUrl?: string; userPreview?: string } | null => {
      for (let i = displayMessages.length - 1; i >= 0; i--) {
        const m = displayMessages[i];
        if (m.role !== "assistant" || m.isClientOnly || m.responder || m.isStreaming) continue;
        const turnNumber = displayMessages
          .slice(0, i)
          .filter((x) => x.role === "assistant" && !x.isClientOnly).length;
        // The question this turn answered, so the Sweeps tab can show WHICH
        // exchange the evidence points at instead of an opaque turn number.
        let userPreview: string | undefined;
        for (let j = i - 1; j >= 0; j--) {
          if (displayMessages[j].role === "user" && !displayMessages[j].isClientOnly) {
            userPreview = displayMessages[j].content.slice(0, 140);
            break;
          }
        }
        return { turnNumber, langfuseTraceUrl: m.langfuseTraceUrl, userPreview };
      }
      return null;
    }, [displayMessages]);

    // TRAC-891: trace bridge, hop (a) of three (embed -> extension bridge ->
    // extension service worker). On each completed assistant turn, tell the
    // parent page about it so the QA extension's Sweeps tab can auto-attach
    // evidence (session + turn + Langfuse trace URL) to a verdict. Dev-gated:
    // customer embeds (prod gating) never emit. Fire-and-forget with '*' —
    // the payload holds no secrets beyond what the dev-gated embed already
    // shows on screen, and the extension validates shape, not origin.
    useEffect(() => {
      if (!shouldShowDevControls(import.meta.env.VITE_ENV || 'dev')) return;
      if (!latestCompletedTurn) return;
      if (window.parent === window) return; // not embedded — nobody to tell
      try {
        window.parent.postMessage({
          type: 'TRACI_TURN_COMPLETE',
          sessionId: agentSessionIdState ?? null,
          turnNumber: latestCompletedTurn.turnNumber,
          langfuseTraceUrl: latestCompletedTurn.langfuseTraceUrl ?? null,
          userPreview: latestCompletedTurn.userPreview ?? null,
        }, '*');
      } catch { /* parent gone or sandboxed — the bridge is best-effort */ }
    }, [latestCompletedTurn, agentSessionIdState]);

    // TRAC-790: the human-first opening — the connect-to-a-person menu is the first
    // thing the customer sees and they haven't turned it into an AI conversation. The
    // rules are pure and live in humanFirst.ts (humanFirst.test.ts covers them).
    //
    // Derived from displayMessages, not `messages`: until the customer engages, the
    // welcome is a prefix that lives OUTSIDE the message array, so reading `messages`
    // here sees an empty list on exactly the opening this is about.
    //
    // While it holds, the whole widget reads as "you are getting a person": the header
    // wears the headset (EmbedPage listens for the event below), the doors are red, and
    // the red "Connect with a Tire Expert" button stays out of it. It stays in human
    // dress right through connecting/queued; handoff 'active' then swaps in the rep's
    // own name and photo.
    const humanFirstMode = isHumanFirstMode(displayMessages);
    const hideBannerForHumanFirst = hideModeBanner({
      humanFirstMode, handoffState, availState: liveChatAvail.state, routingPending,
    });
    // TRAC-790: the moment before we know which opening this is. The panel paints as soon
    // as it is opened, but an opening that offers people cannot be drawn until we know
    // whether any are there — so the header would sit in AI blue and then snap to red,
    // which reads as the widget changing its mind in front of the customer. Said out loud
    // here so the header can wear a neutral "finding out" state instead of guessing.
    const openingIsUndecided = openingUndecided(liveChatAvail.state, pageContext);
    useEffect(() => {
      try {
        window.dispatchEvent(new CustomEvent('traci-human-first', {
          detail: { active: humanFirstMode, deciding: openingIsUndecided },
        }));
      } catch { /* no-op */ }
    }, [humanFirstMode, openingIsUndecided]);

    // useImperativeHandle moved after handleClearSession definition (around line 1340)



    // TRAC-454/TRAC-467: Measure available space, limit prompts, then fade grid in.
    // Grid starts at opacity:0 via CSS, gets .followUpActionsGridVisible after measurement.
    // Recalculates on container resize so prompt count stays correct when parent resizes.
    const measurePrompts = useCallback(() => {
      if (!isPreConversation) return;
      const grid = messagesAreaRef.current?.querySelector(`.${messageRendererStyles.followUpActionsGrid}`) as HTMLElement | null;
      if (!grid) return;
      const buttons = grid.querySelectorAll('button');
      if (!buttons.length) return;

      // TRAC-797: The pre-conversation wrapper is overflow-hidden and must
      // never sit scrolled — but programmatic scrolls can still move it, and
      // a scrolled container skews gridTop upward, inflating `available` by
      // exactly the scroll offset. That made a bad fit count self-confirming
      // (overflow -> scrolled down -> re-measure sees more room -> same bad
      // count). Clamp back to top before measuring.
      const scrollContainer = embedScrollRef.current;
      if (scrollContainer && scrollContainer.scrollTop !== 0) {
        scrollContainer.scrollTop = 0;
      }

      const gridTop = grid.getBoundingClientRect().top;
      const buttonH = buttons[0].getBoundingClientRect().height;
      // A hidden layout measures as all zeros, and zeros here compute fits=1 —
      // which then gets PERSISTED into the committed welcome as a one-chip
      // prompt list (seen live 8/12: chip → picker → completion committed a
      // single-chip welcome). The picker hides the whole messages wrapper
      // (display:none) while it is up, so any measurement scheduled into that
      // window must be discarded, not believed.
      if (buttonH === 0) return;
      const gap = 24;
      // "Full height" = the chat's OWN scroll container, not the window. The old
      // `window.innerHeight - 145` fallback assumed the chat owned the whole viewport
      // (true only as a full-height iframe); it over-counts in the short bubble and
      // would break in the on-page embed, where window.innerHeight is the entire TR
      // page. Measure the visible bottom of the scroll container instead.
      const bottomEdge = scrollContainer
        ? scrollContainer.getBoundingClientRect().top + scrollContainer.clientHeight
        : window.innerHeight - 145;
      const available = bottomEdge - gridTop;
      const fits = Math.max(1, Math.floor((available + gap) / (buttonH + gap)));

      setVisiblePromptCount(fits);
      grid.classList.add(messageRendererStyles.followUpActionsGridVisible);
    }, [isPreConversation]);

    useEffect(() => {
      if (!isPreConversation) return;
      // Fast path: measure after one rAF for the normal mount / reload case.
      const raf = requestAnimationFrame(measurePrompts);
      // TRAC-478: Slow path — re-measure after the widget-header height
      // transition (0.25s, see EmbeddableChat.module.css) has settled. The
      // handleClearSession flow fires while that transition is animating,
      // so the rAF-timed measurement reads a mid-animation layout and
      // ends up showing more prompts than actually fit. The post-transition
      // measurement corrects it; no-op if the geometry is unchanged.
      const settled = setTimeout(measurePrompts, 300);
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(settled);
      };
    }, [isPreConversation, displayMessages, measurePrompts]);

    // TRAC-467: Recalculate prompt count when container resizes (e.g., drag resize)
    // TRAC-790: watch the SCROLL container too, not just the messages area. The
    // messages area is sized by its content, so its box never changes when the chat
    // only gets SHORTER — a shorter window kept showing the same number of starter
    // prompts and the last ones ran underneath the composer. The scroll container is
    // the element whose height actually tracks the panel, so it's the one that has to
    // be observed. (Width-only changes, e.g. dragging the tray, still work either way.)
    useEffect(() => {
      if (!isPreConversation) return;
      const targets = [messagesAreaRef.current, embedScrollRef.current].filter(Boolean) as Element[];
      if (!targets.length) return;
      const observer = new ResizeObserver(() => {
        requestAnimationFrame(measurePrompts);
      });
      targets.forEach((t) => observer.observe(t));
      return () => observer.disconnect();
    }, [isPreConversation, measurePrompts, embedScrollRef]);

    // TRAC-486: Document-level scroll styling was only applied in the non-embed
    // path (dead in production). Embed relies on the wrapper div's own scroll,
    // and the parent page controls body styles.

    /**
     * TRAC-550 B8: schedule the next auto-retry attempt. Replaces TRAC-81's
     * 15s countdown timer. On exhaust, dispatches engageEmergencyOverlay
     * (Josh's mechanism — listened to elsewhere) so the user sees the
     * maintenance overlay instead of a dead chat.
     */
    // Mirror retryAttempt in a ref. scheduleAutoRetry is called from inside a
    // long-lived SSE stream-handler closure built at message-send time — that
    // closure captures *its* version of scheduleAutoRetry once and never re-
    // reads. Without the ref, retry 2 would re-schedule from attempt=0
    // because the captured closure still sees retryAttempt=null. The ref is
    // updated in lockstep with the state setter so reads are always fresh.
    const retryAttemptRef = useRef<number | null>(null);
    // TRAC-862: `resend` is the failing turn's own re-send thunk. It closes over
    // the exact message that failed, so the retry never has to infer what to
    // send from what happens to be on screen.
    const scheduleAutoRetry = useCallback((resend?: () => void) => {
      if (resend) {
        autoRetryHandlerRef.current = resend;
      }
      const next = retryAttemptRef.current ?? 0; // 0-indexed for the scheduler
      retrySchedulerRef.current?.schedule(next);
    }, []);

    // Initialize the scheduler once. doRetry calls into the latest auto-retry
    // handler (closure-captured via ref so it sees current messagesRef state).
    useEffect(() => {
      retrySchedulerRef.current = createRetryScheduler({
        doRetry: () => {
          // The request is going out now — say so (TRAC-862).
          setRetryPhase("inflight");
          setRetrySecondsLeft(null);
          // The attempt's own budget: it can't outlive the stream idle timeout.
          setRetryWindowMs(STREAM_IDLE_TIMEOUT_MS);
          const resend = autoRetryHandlerRef.current;
          if (!resend) {
            // Never fail silently here again: a no-op retry is indistinguishable
            // from a hung one, which is exactly how this went unnoticed.
            logger.error(
              "[TRAC-862] auto-retry fired with no pending resend — the chain would stall here",
            );
            return;
          }
          resend();
        },
        onAttempt: (attempt, delayMs) => {
          const visible = attempt + 1;
          retryAttemptRef.current = visible;
          setRetryAttempt(visible);
          // onAttempt fires at SCHEDULE time, so this is the start of the wait,
          // not the attempt itself. delayMs was previously discarded — it is the
          // only thing that lets the customer see the wait tick down.
          setRetryPhase("waiting");
          const secs = Math.max(1, Math.ceil(delayMs / 1000));
          setRetrySecondsLeft(secs);
          setRetryWaitTotal(secs);
          // Fill across exactly this backoff — 4s of waiting means a 4s fill.
          setRetryWindowMs(delayMs);
        },
        onExhausted: () => {
          retryAttemptRef.current = null;
          setRetryAttempt(null);
          // Tells subscribers (Josh's reconciliation path; admin tooling)
          // that this client gave up after MAX_RETRIES — engages the
          // emergency overlay so the user isn't stuck on a dead chat.
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("engageEmergencyOverlay"));
          }
        },
      });
      return () => {
        retrySchedulerRef.current?.abandon();
        retrySchedulerRef.current = null;
      };
    }, []);

    // TRAC-862: tick the visible wait down. One setTimeout per second, only
    // while we're actually waiting — it stops the moment the retry goes out.
    useEffect(() => {
      if (retryPhase !== "waiting" || retrySecondsLeft === null || retrySecondsLeft <= 0) {
        return;
      }
      const id = setTimeout(
        () => setRetrySecondsLeft((s) => (s === null ? null : Math.max(0, s - 1))),
        1000,
      );
      return () => clearTimeout(id);
    }, [retryPhase, retrySecondsLeft]);

    // TRAC-862: retryAttempt is cleared from several places (a successful `done`
    // in useChatStream, onExhausted, a pin-flip). Rather than teach each of them
    // about the new state, derive the reset — otherwise a stale "Trying again
    // now…" outlives the retry that owned it.
    useEffect(() => {
      if (retryAttempt === null) {
        setRetryPhase(null);
        setRetrySecondsLeft(null);
        setRetryWaitTotal(null);
        setRetryWindowMs(null);
        setRetryBarFill(0);
      }
    }, [retryAttempt]);

    // TRAC-862: restart the fill whenever the phase or attempt changes. Paint 0%
    // first, then transition to 100% over retryWindowMs — one CSS transition
    // across the real duration, so the bar's position always means "this much of
    // the window has elapsed". The double rAF guarantees the 0% is committed
    // before the transition starts, otherwise the browser coalesces both values
    // and the bar jumps straight to full.
    useEffect(() => {
      if (!retryPhase || !retryWindowMs) return;
      setRetryBarFill(0);
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setRetryBarFill(100));
      });
      return () => {
        cancelAnimationFrame(outer);
        if (inner) cancelAnimationFrame(inner);
      };
    }, [retryPhase, retryAttempt, retryWindowMs]);

    // TRAC-862 — PATH 3 for the emergency overlay: this client gave up.
    //
    // `onExhausted` above has dispatched `engageEmergencyOverlay` since TRAC-550
    // B8, and NOTHING in the repo listened for it. The event went into the void,
    // so retry exhaustion left the customer on a dead chat — the opposite of
    // what the comment there claimed.
    //
    // It also cannot be left to the announcement poll (PATHS 1 and 2, see
    // AnnouncementBanner). Both of those key on the BACKEND being unreachable.
    // The failure this ticket is about is an agent runtime dying while the
    // backend stays healthy and keeps answering `/settings/announcement` with
    // 200s — neither path will ever fire for it. Exhaustion has to force the
    // overlay itself.
    useEffect(() => {
      const onExhaustedOverlay = () => {
        logger.error("[TRAC-862] auto-retry exhausted — engaging emergency overlay");
        // Claim the overlay for the client BEFORE setting it, so the very next
        // announcement poll doesn't treat it as a stale server announcement and
        // clear it (see the guards in the poll below).
        retryExhaustedOverlayRef.current = true;
        setAnnouncementMessage(RETRY_EXHAUSTED_MESSAGE);
        setAnnouncementMode("emergency");
        // A dismissal from an earlier, unrelated announcement must not suppress
        // this one — otherwise the customer's chat dies silently.
        //
        // TRAC-1006: that includes a dismissal still held for a handoff. The agent failing
        // is proof the customer is back on the AI (a recap after a bridge ending is the usual
        // way here), so the hold is over too -- clear it and write it down, or the next page
        // would seed the screen as dismissed and the confirm loop would keep asking.
        overlayDismissedForHandoffRef.current = false;
        setAnnouncementDismissed(false);
        persistHandoffAttemptRef.current();
      };
      window.addEventListener("engageEmergencyOverlay", onExhaustedOverlay);
      return () =>
        window.removeEventListener("engageEmergencyOverlay", onExhaustedOverlay);
    }, []);

    // TRAC-550 B5+B8: pin-flip aborts the retry loop. Next user action will
    // land on the new region cleanly via getBackendApiUrl().
    useEffect(() => {
      return regionFailoverService.subscribe((action) => {
        if (action.type === "flip") {
          retrySchedulerRef.current?.abandon();
          retryAttemptRef.current = null;
          setRetryAttempt(null);
        }
      });
    }, []);


    // When streaming finishes and there's a pending selection, send it
    // TRAC-218: No setTimeout — bypassLoadingGuard lets us call handleSendMessage directly
    // so React batches all state updates (clear pending + start new stream) in one render.
    // This eliminates the visual flash where the spinner briefly disappears.
    useEffect(() => {
      if (!isStreaming && pendingSelection) {
        const { value, label } = pendingSelection;
        setPendingSelection(null);

        const message = createCardSelectionMessage(value, label);
        handleSendMessage(message.content, message.displayContent, true, undefined, true);
      }
    }, [isStreaming, pendingSelection]);

    /**
     * Generate user-friendly display text for TDG updates
     * Used by both normal card selection and pending selection during streaming
     */
    const generateTDGDisplayMessage = (
      structuredMessage: string,
      chipType: string,
    ): string => {
      // Parse the action from the structured message
      const actionMatch = structuredMessage.match(/Action:\s*(.+)/);
      const action = actionMatch ? actionMatch[1].trim() : "";

      // Map chip types to friendly names
      const friendlyNames: Record<string, string> = {
        wethandling: "Wet Handling",
        dryhandling: "Dry Handling",
        ridecomfort: "Ride Comfort",
        wear: "Treadwear",
        price: "Price",
        wintertraction: "Winter Traction",
        icetraction: "Ice Traction",
        snowtraction: "Snow Traction",
        trailtraction: "Trail Traction",
        winter: "Winter Tires",
        offroad: "Off-Road",
      };

      const friendlyName = friendlyNames[chipType] || chipType;

      // Generate appropriate message based on action
      if (action.includes("prioritize") && !action.includes("deprioritize")) {
        return `Prioritize ${friendlyName}`;
      } else if (action.includes("deprioritize")) {
        return `Remove ${friendlyName} priority`;
      } else if (action.includes("enable")) {
        return `Enable ${friendlyName}`;
      } else if (action.includes("disable")) {
        return `Disable ${friendlyName}`;
      } else if (action.includes("change tire type")) {
        // Check if enabling or disabling winter
        if (structuredMessage.includes("seasonType=winterOnly")) {
          return "Switch to Winter Tires";
        } else {
          return "Switch to All-Season Tires";
        }
      }

      return `Update ${friendlyName}`;
    };

    /**
     * TRAC-185: Create user message object for card selections
     * Single source of truth for message format - used by all card selection paths
     */
    const createCardSelectionMessage = (value: string, label: string): Message => {
      if (value?.startsWith?.("[TDG_UPDATE]")) {
        // TDG update card
        const displayMessage = generateTDGDisplayMessage(value, label);
        return {
          id: Date.now().toString(),
          role: "user",
          content: value,
          displayContent: displayMessage,
          timestamp: new Date(),
        };
      } else {
        // Regular card selection - send value (TRAC-259: label===value after backend normalization)
        return {
          id: Date.now().toString(),
          role: "user",
          content: `User selected: ${value}`,
          displayContent: null,
          timestamp: new Date(),
          parts: [{
            type: "user_selection_card",
            selectedValue: value,
            selectedLabel: value
          }]
        };
      }
    };

    // TRAC-497: the send/stream closure lives in useChatStream (pure factory —
    // no state or effects of its own; see the hook's doc comment).
    // TRAC-732: bridge so the hook's stream loop (which now owns the `done` event) can
    // call escalateToHuman — defined below, after the hook. Mirrors handleSendMessageRef.
    // TRAC-1212: callers say who started it (the agent's handoff, or the outage overlay's button).
    const escalateToHumanRef = useRef<(initiatedBy?: HandoffInitiator) => void>(() => {});

    const { handleSendMessage: streamSendMessage } = useChatStream({
      messages, setMessages, inputValue, setInputValue,
      isLoading, setIsLoading, setIsLoadingFadingOut,
      isStreaming, setIsStreaming, setIsThinking,
      sessionExpired, pageContext,
      retryAttempt, retryAttemptRef, retrySchedulerRef, autoRetryHandlerRef, setRetryAttempt,
      scheduleAutoRetry, sessionIdNotifiedRef,
      shouldAutoScrollRef, isProgrammaticScrollRef, lockAutoScrollRef,
      lastTurnWrapperRef, lastUserMsgRef,
      doScrollTo, getElementScrollOffset, getViewportHeight, scheduleProgrammaticScrollEnd,
      beginPhoneSend,
      activateYmmvMode, handleTireSizeSelect, ymmvIntentRef, ymmvPrefillSizeRef,
      pendingYmmvTimingRef, pendingSelectorCompletionRef, pendingVehicleContextRef,
      triggerExpiryResend, buildCommittedWelcome,
      // TRAC-732: live-human handoff — the hook routes sends to LP while in-handoff,
      // catches the `human_handoff` control event, and consumes the pending handoff
      // at stream-complete (offer → soft card; immediate → escalate).
      handoffStateRef, setHandoffActivity, setRoutingPending,
      pendingHandoffRef, pendingHandoffOfferRef, pendingHandoffDeptRef, pendingSoftHandoffDeptRef,
      escalateToHumanRef, agentEscalateDueRef, persistHandoffAttemptRef, escalateGenRef,
      agentHandoffInitiatorRef,
    });

    // Miles 7/15: every send — composer, Enter, card pick, or a card pick deferred until
    // the stream finishes — is the customer engaging, which squishes the mode banner.
    // Wrapping here catches all of them; the call sites below stay untouched.
    // TRACI-916: set below, once escalateToHuman exists. Connects an open offer the way the
    // card's own Connect button does.
    const softAckConnectRef = useRef<(department: string) => void>(() => {});
    const handleSendMessage = useCallback<typeof streamSendMessage>((...args) => {
      setBannerInteracted(true);
      // TRACI-916: "ok" / "thanks" typed while the offer card is open is a yes to it. Sent to
      // the AI, it paused the countdown and drew a second "Connecting you to Sales now" offer.
      // Only the composer (no override, no card pick) and never mid-reply.
      const [messageOverride, , , metadata] = args;
      if (!messageOverride && !metadata && !isLoading && !isStreaming
          && (handoffState === 'idle' || handoffState === 'ended')) {
        const dept = offerAckDepartment(displayMessages[displayMessages.length - 1], inputValue);
        if (dept !== null) {
          setInputValue('');
          softAckConnectRef.current(dept);
          return Promise.resolve();
        }
      }
      return streamSendMessage(...args);
    }, [streamSendMessage, isLoading, isStreaming, displayMessages, inputValue, handoffState]);


    // ─────────── TRAC-732 handoff: helper functions (dispatchHandoff … resetHandoff, escalateToHuman, endHandoffByUser) ───────────
    const enterHumanDivIdRef = useRef<string | null>(null);
    // TRAC-732: notify the external header (EmbedPage) of state + agent name/photo
    const dispatchHandoff = () => {
      try {
        window.dispatchEvent(new CustomEvent('traci-handoff-state', {
          detail: {
            state: handoffStateRef.current,
            agentName: handoffAgentNameRef.current,
            agentAvatar: handoffAgentAvatarRef.current,
          },
        }));
      } catch { /* no-op */ }
    };
    const setHandoff = (
      s: 'idle' | 'connecting' | 'queued' | 'active' | 'ended',
      opts?: { holdOverlayDismissal?: boolean },
    ) => {
      // TRAC-975: bring the outage screen back when the handoff is over.
      //
      // Taking the overlay's route to a person DISMISSES the overlay, and the
      // announcement poll only clears `dismissed` when the message or mode
      // actually changes — which it does not, because the outage is still on.
      // Without this the screen never returns: the customer leaves the rep,
      // lands in a chat that looks perfectly healthy, and talks to an agent
      // that is still down.
      //
      // This lives in setHandoff rather than at the end sites on purpose. A
      // handoff ends in at least two places — the LP stream's ended/error/voided
      // branch, and endHandoffByUser() when the customer leaves — and the first
      // version of this fix patched only one of them, which is exactly the bug
      // it was trying to fix, one level up. Every one of the 13 callers goes
      // through here, so a new ending cannot miss it.
      //
      // Only a dismissal we made for the handoff is undone; an admin's own
      // Dismiss press is left alone.
      //
      // TRAC-1006: `holdOverlayDismissal` is for the endings the page only HEARD about --
      // a bridge ended/error/voided event. A transient bridge error is not the end of a
      // handoff, so the dismissal stays until /agent/handoff-state confirms it (see
      // confirmHandoffOver). An ending the customer caused, or the backend already
      // confirmed, releases it here as before.
      if ((s === 'idle' || s === 'ended') && overlayDismissedForHandoffRef.current && !opts?.holdOverlayDismissal) {
        overlayDismissedForHandoffRef.current = false;
        setAnnouncementDismissed(false);
      }
      handoffStateRef.current = s;
      setHandoffState(s);
      dispatchHandoff();
    };
    const updateAgentInfo = (name?: string, avatar?: string) => {
      if (name) {
        handoffAgentNameRef.current = name;
        setHandoffAgentName(name);
        // Back-fill markers/messages appended before the rep's real name resolved
        // — they captured the "Tire Rack team" fallback at append time. Only the
        // fallback is patched, so prior cycles with a real name stay intact.
        setMessages((prev) => prev.map((m) => {
          const patched = m.agentName === 'Tire Rack team' ? { ...m, agentName: name } : m;
          // If a pending "While we wait" header is still up when the rep's name resolves,
          // they're clearly connected — flip it to "Chat with {name} started" too.
          return (patched.responder === 'divider' && patched.dividerKind === 'enter-human' && patched.dividerPending)
            ? { ...patched, dividerPending: false }
            : patched;
        }));
      }
      if (avatar) {
        handoffAgentAvatarRef.current = avatar;
        setHandoffAgentAvatar(avatar);
        // Mirror the name back-fill above for the photo — patch only this rep's own
        // photoless items (a prior cycle's rep already carries their own avatar/name).
        setMessages((prev) => prev.map((m) =>
          (m.responder === 'human' || m.responder === 'divider' || m.responder === 'summary')
          && m.agentName === handoffAgentNameRef.current && !m.agentAvatar
            ? { ...m, agentAvatar: avatar } : m,
        ));
      }
      dispatchHandoff();
    };
    const appendHumanMessage = (text: string, name: string, html?: string) => {
      // TRAC-732: once the customer has left, NOTHING from the handoff may reach the page —
      // not a status flip, not a rep's message. handleHandoffStatus already bails on 'idle';
      // this is the same guard for relayed rep messages, so a late poll response (or any other
      // straggler) can never drop a rep's reply into a chat the customer already walked away
      // from. Belt-and-braces with the backend force-close: even if LivePerson still emits, we
      // refuse to render it.
      if (handoffStateRef.current === 'idle') return;
      setHandoffActivity(null); // a reply arrived — clear typing/waiting
      noteRepReply(); // TRACI-256: bring it on screen (measured before it is added)
      setMessages((prev) => {
        const next = [...prev, {
          id: `human-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
          role: 'assistant',
          content: text,
          richHtml: html,
          timestamp: new Date(),
          responder: 'human',
          agentName: name,
          agentAvatar: handoffAgentAvatarRef.current || undefined,
          inHumanZone: true,
        } as Message];
        // TRAC-732 #15: persist the rep's message so it survives a refresh/navigation. Without
        // this, only the band dividers (saved at escalate) came back and the rep's replies
        // vanished — the split-brain the customer reported.
        chatAgentService.saveMessagesToSession(next);
        return next;
      });
    };
    const appendSystemMessage = (text: string) => {
      setMessages((prev) => [...prev, {
        id: `sys-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
        role: 'assistant',
        content: text,
        timestamp: new Date(),
        responder: 'system',
        isClientOnly: true, // never sent to LP as transcript
      } as Message]);
    };
    // TRAC-732: the "which team?" prompt. Funnels through the REAL send pipeline
    // (handleSendMessage) so it looks/feels exactly like the customer sent a message:
    // a big "Requesting live chat" user turn that smooth-scrolls to the top, then a
    // streamed canned agent reply with the department buttons underneath.
    const showRoutingPrompt = () => {
      const text = "Happy to connect you with a person! What are you looking to do?";
      const parts: MessagePart[] = [
        { type: 'text', content: text },
        { type: 'routing_actions',
          actions: MENU_OPTIONS,
          hint: "Not sure, or none of these fit? Just tell me in chat what you're looking for and I'll point you the right way." },
      ];
      setRoutingPending(true); // gate the button until they pick an option, reply, or cancel
      // "Requesting live chat" runs through the REAL send pipeline (which owns the smooth
      // scroll-to-top that already works), but the last arg tags that turn as a system marker so
      // it renders in the big-black handoff-marker style instead of a right-aligned customer bubble.
      void handleSendMessageRef.current?.('Requesting live chat', undefined, false, undefined, true, { parts }, undefined, true);
    };
    // TRAC-732: back out of the live-chat request without choosing a team — clicking the
    // (still-enabled) button again returns the customer to the agentic chat.
    const cancelRoutingPrompt = () => {
      setRoutingPending(false);
      // Mirror "Requesting live chat": a system state marker (big-black, not a customer bubble)
      // that's honest about what happened — the customer ended the request, they're not "saying"
      // anything. The last arg (systemMarker) gives it the same big-black style.
      void handleSendMessageRef.current?.("Canceled live chat request", undefined, false, undefined, true, {
        parts: [{ type: 'text', content: "No problem — I'm still here to help. What can I do for you?" }],
      }, undefined, true);
    };
    // TRAC-790: drain a "somebody asked for a person" request (EmbedPage sets it when the
    // chat is opened with that intent).
    //
    // Held rather than acted on immediately, because the answer depends on an opening
    // that has not necessarily rendered yet. On a first open the welcome itself becomes
    // the connect menu and there is nothing to do; on a chat that already holds a
    // conversation the welcome is frozen to that conversation, so the request would
    // otherwise vanish — press the site's "Chat with an Expert" after chatting to the
    // agent earlier and you simply got your old thread back, with nothing anywhere about
    // a person. So we wait for the opening to exist, then answer whichever case it is.
    useEffect(() => {
      if (!humanRequested) return;
      if (displayMessages.length === 0) return;       // the opening hasn't rendered yet
      if (liveChatAvail.state === 'checking') return; // can't answer until we know
      setHumanRequested(false);
      if (humanFirstMode) return;                     // the opening already answered it
      if (routingPending) return;                     // already asked, awaiting their pick
      const st = handoffStateRef.current;
      if (st !== 'idle' && st !== 'ended') return;    // already on the way to / with a person
      // Outside hours the doors lead nowhere, so they are not offered. Nothing is said
      // about it either: the entry button is already sitting there reading "Live Chat
      // Unavailable", and a paragraph repeating that is noise on top of an answer the
      // customer can already see.
      if (liveChatAvail.state !== 'open') return;
      showRoutingPrompt();
    }, [humanRequested, displayMessages.length, humanFirstMode, routingPending,
        liveChatAvail.state, liveChatAvail.hours]);
    // TRAC-732: SOFT-HANDOFF — drop the countdown card (client-only) below the agent's
    // reply. It auto-connects in 10s unless the customer connects now or opts to stay
    // with the AI (handled in handleCardSelect).
    // TRACI-865: one card at a time, stamped with when it was offered, and written down
    // at once: a page change during the countdown used to lose the card (it was never
    // persisted) and leave "Connecting you to Sales now" with nothing behind it.
    const isSoftHandoff = (m: Message) => !!m.parts?.some((pt) => pt.type === 'soft_handoff');
    const showSoftHandoff = (department?: string) => {
      const id = `soft-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
      setMessages((prev) => {
        const next = [...prev.filter((m) => !isSoftHandoff(m)), {
          id,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isClientOnly: true,
          parts: [{ type: 'soft_handoff', department: department || 'Customer Care', offeredAt: Date.now() }],
        } as Message];
        chatAgentService.saveMessagesToSession(next);
        return next;
      });
    };
    const removeSoftHandoff = () => {
      setMessages((prev) => {
        if (!prev.some(isSoftHandoff)) return prev;
        const next = prev.filter((m) => !isSoftHandoff(m));
        chatAgentService.saveMessagesToSession(next);
        return next;
      });
    };
    const flushPendingSoftHandoff = () => {
      if (pendingSoftHandoffDeptRef.current !== null) {
        const dept = pendingSoftHandoffDeptRef.current;
        pendingSoftHandoffDeptRef.current = null;
        showSoftHandoff(dept);
      }
    };
    // TRAC-732: the latest message finished streaming AND typing — if a soft-handoff card
    // was queued this turn, drop it in now (below the fully-typed reply) and start its timer.
    const handleTypingSettled = () => {
      noteTypingSettled();   // TRAC-1176: the phone scroll rules judge the reply once it is fully shown
      flushPendingSoftHandoff();
    };
    // TRACI-865: nobody is watching the reply type out in a background tab, and the typing
    // animation runs on timers the browser slows to one tick a second there, so a long reply
    // could hold the card (and its countdown) back for minutes. Hidden: show it now.
    useEffect(() => {
      const onHidden = () => { if (document.visibilityState === 'hidden') flushPendingSoftHandoff(); };
      if (!isStreaming) onHidden();
      document.addEventListener('visibilitychange', onHidden);
      return () => document.removeEventListener('visibilitychange', onHidden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStreaming]);
    // TRACI-865: once a person is on the way (by any route: the card, the top button, a rep
    // pulling the chat, or a restore into a live handoff) the offer is spent.
    const hasSoftHandoff = messages.some(isSoftHandoff);
    useEffect(() => {
      if (!hasSoftHandoff) return;
      if (handoffState === 'connecting' || handoffState === 'queued' || handoffState === 'active') removeSoftHandoff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handoffState, hasSoftHandoff]);
    // TRAC-732: timeline divider marking the human-chat boundary
    // TRAC-919: write the transcript down NOW. The turns that tell the story of a handoff
    // -- the "Connecting…" turn, the band header, their flips on join, their removal on a
    // failed connect -- are appended by the handlers below, not by the send pipeline, so
    // nothing else saves them. A page change before the next save lost them, and the
    // resumed panel showed the department menu as the latest message -- buttons live --
    // above a live conversation. Every path that changes the story persists after itself,
    // adds AND removals alike: saving only the additions would leave a reload after a
    // failed connect showing a "Connecting…" turn and a band the server knows nothing about.
    const persistTranscript = () => {
      setMessages((prev) => { chatAgentService.saveMessagesToSession(prev); return prev; });
    };
    const appendDivider = (kind: 'enter-human' | 'end-human' | 'back-to-ai', opts?: { pending?: boolean }) => {
      const id = `div-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
      if (kind === 'enter-human') enterHumanDivIdRef.current = id; // so we can flip pending→started on join
      setMessages((prev) => [...prev, {
        id,
        role: 'assistant', content: '', timestamp: new Date(),
        responder: 'divider', dividerKind: kind,
        dividerPending: opts?.pending ?? false,
        agentName: handoffAgentNameRef.current,
        agentAvatar: handoffAgentAvatarRef.current || undefined,
        // enter-human opens the band (top edge) and end-human closes it (bottom edge)
        // — both INSIDE the red card. back-to-ai is NOT in the band — it's the big
        // "Back with the AI tire agent" marker OUTSIDE/below the band.
        inHumanZone: kind !== 'back-to-ai',
        isClientOnly: true,
      } as Message]);
    };
    // TRAC-732: the band is opened at escalate-time (pending header) so pre-join queued
    // messages render inside it. If the handoff ends BEFORE any human joined, collapse
    // the band: drop the pending header and demote queued messages back to normal.
    const collapseUnjoinedBand = () => {
      const divId = enterHumanDivIdRef.current;
      setMessages((prev) => {
        // TRAC-732: demote ONLY the band from THIS connect attempt — the messages from its
        // "chat started" header onward. Earlier in-zone messages belong to a PRIOR, completed
        // human chat whose red band must survive. (Bug: this used to strip inHumanZone off EVERY
        // message in the conversation, so a quick connect-then-leave wiped the border/background
        // off an earlier real chat too.) If the header can't be located, demote nothing.
        const startIdx = divId ? prev.findIndex((m) => m.id === divId) : -1;
        const demote = new Set<string>();
        if (startIdx >= 0) {
          for (let i = startIdx; i < prev.length; i++) {
            if (prev[i].inHumanZone) demote.add(prev[i].id);
          }
        }
        return prev
          .filter((m) => m.id !== divId)
          .map((m) => (demote.has(m.id) ? { ...m, inHumanZone: false } : m));
      });
      enterHumanDivIdRef.current = null;
    };
    // TRAC-732: LLM recap card (to-human = "shared with Joshua", from-human = "recap from Joshua")
    const appendSummary = (direction: 'to-human' | 'from-human', text: string) => {
      setMessages((prev) => [...prev, {
        id: `sum-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
        role: 'assistant', content: text, timestamp: new Date(),
        responder: 'summary', summaryKind: direction,
        agentName: handoffAgentNameRef.current,
        agentAvatar: handoffAgentAvatarRef.current || undefined,
        inHumanZone: direction === 'to-human',
        isClientOnly: true,
      } as Message]);
    };
    // TRAC-732: end-of-conversation survey — the CSAT/resolution/free-text card
    // shown at the bottom of the red band when a live-agent chat ends. Each
    // answered step reports up via handleSurveySubmit; the record is stored in
    // TRACI's own feedback table (score + resolution are separately mirrored to
    // LivePerson's CSAT reporting by the backend).
    const buildFeedbackTranscript = (): Array<{ role: string; text: string; name?: string }> => {
      const asText = (m: Message): string => {
        if (typeof m.content === 'string' && m.content) return m.content;
        if (m.parts) {
          return m.parts
            .filter((p) => p.type === 'text')
            .map((p) => (p as { content?: string }).content || '')
            .join('\n')
            .trim();
        }
        return '';
      };
      return messagesRef.current
        .filter((m) => !m.isClientOnly && m.responder !== 'divider' && m.responder !== 'summary')
        .map((m) => (m.responder === 'human'
          ? { role: 'human_agent', text: asText(m), name: m.agentName }
          : { role: m.role, text: asText(m) }))
        .filter((t) => t.text);
    };
    const handleSurveySubmit = (answers: ConversationSurveyAnswers & { messageId?: string }) => {
      // TRAC-732: persist the answers ON this card's survey part (and save the thread), so a
      // reload rehydrates THIS card exactly where the customer left it — mid-survey progress,
      // or the locked "thanks" state once submitted. Without this the card remounted blank and
      // invited a second submission for a chat they'd already graded. One submission per
      // revealed card (per chat instance); a reconnect creates a NEW card that can be graded
      // again, which overwrites that department's stored record.
      if (answers.messageId) {
        setMessages((prev) => {
          const next = prev.map((m) => (m.id !== answers.messageId ? m : {
            ...m,
            parts: m.parts?.map((p) => (p.type !== 'conversation_survey' ? p : {
              ...p,
              surveyState: {
                csat: answers.csat,
                resolved: answers.resolved ?? null,
                feedback: answers.feedback ?? null,
                step: answers.step ?? 1,
              },
            })),
          }));
          chatAgentService.saveMessagesToSession(next);
          return next;
        });
      }
      void chatAgentService.submitConversationFeedback({
        csat: answers.csat,
        resolved: answers.resolved ?? null,
        feedback: answers.feedback ?? null,
        department: answers.department ?? null,
        transcript: buildFeedbackTranscript(),
      });
    };
    const showConversationSurvey = (department?: string | null) => {
      const dept = (department ?? activeHandoffDeptRef.current) || null;
      // TRAC-732 (per-department feedback): dedup per CHAT INSTANCE, not per department.
      // One chat-end must not double-add a card (the 'ended' status can arrive twice), but
      // RECONNECTING to the same department later is a NEW chat that gets its OWN fresh
      // survey. Its submit overwrites that department's stored record (keyed by department),
      // so the latest rating wins — exactly the "grade it again, override the old one" flow.
      // A different department is a different generation too → its own card + own record.
      const gen = escalateGenRef.current;
      if (lastSurveyGenRef.current === gen) return;
      lastSurveyGenRef.current = gen;
      setMessages((prev) => {
        return [...prev, {
          id: `survey-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
          role: 'assistant', content: '', timestamp: new Date(),
          isClientOnly: true,
          // inHumanZone: the survey lives INSIDE the red band, below the "Chat
          // ended" line — it's part of the human portion of the conversation, and
          // the "Back with the AI tire agent" marker below makes it clear the AI
          // chat continues. Appended AFTER end-human so it becomes the band's
          // bottom row and the red border wraps around it.
          inHumanZone: true,
          parts: [{ type: 'conversation_survey', department: dept ?? undefined }],
        } as Message];
      });
    };
    // TRAC-732 DEV-ONLY: open with ?devSurvey=1 to drop straight into a finished
    // human handoff (red band) with the survey inside it — so the survey UI can be
    // seen and clicked without staging a real LivePerson handoff. Double-guarded:
    // stripped from production builds via import.meta.env.DEV AND gated by the URL
    // param, so it can never fire in a deployed environment.
    const devSurveySeededRef = useRef(false);
    useEffect(() => {
      if (!import.meta.env.DEV) return;
      if (devSurveySeededRef.current) return;
      let hasParam = false;
      try { hasParam = new URLSearchParams(window.location.search).has('devSurvey'); } catch { hasParam = false; }
      if (!hasParam) return;
      devSurveySeededRef.current = true;
      const now = Date.now();
      const ts = new Date();
      // Real conversation content is NOT isClientOnly — otherwise isPreConversation
      // stays true (every message client-only) and the wrapper keeps its NoScroll
      // class (overflow-y:hidden). Dividers + the survey are legitimately client-only.
      const msg = { timestamp: ts, isClientOnly: false } as const;
      const marker = { timestamp: ts, isClientOnly: true } as const;
      setMessages(() => ([
        { ...msg, id: `dev-u1-${now}`, role: 'user', content: 'I need help with a warranty claim.' },
        { ...marker, id: `dev-ent-${now}`, role: 'assistant', content: '', responder: 'divider', dividerKind: 'enter-human', dividerPending: false, agentName: 'Steve', inHumanZone: true },
        { ...msg, id: `dev-h1-${now}`, role: 'assistant', content: 'Hi, this is Steve — happy to help with your warranty claim.', responder: 'human', agentName: 'Steve', inHumanZone: true },
        { ...msg, id: `dev-u2-${now}`, role: 'user', content: 'My tire has a bubble on the sidewall.', inHumanZone: true },
        { ...msg, id: `dev-h2-${now}`, role: 'assistant', content: "Got it — I've filed the hazard claim. You'll get a confirmation email shortly.", responder: 'human', agentName: 'Steve', inHumanZone: true },
        { ...marker, id: `dev-end-${now}`, role: 'assistant', content: '', responder: 'divider', dividerKind: 'end-human', dividerPending: false, agentName: 'Steve', inHumanZone: true },
        { ...marker, id: `dev-survey-${now}`, role: 'assistant', content: '', inHumanZone: true, parts: [{ type: 'conversation_survey', department: 'Customer Care' }] },
        { ...marker, id: `dev-back-${now}`, role: 'assistant', content: '', responder: 'divider', dividerKind: 'back-to-ai', dividerPending: false, inHumanZone: false },
        { ...msg, id: `dev-ai-${now}`, role: 'assistant', content: "I'm back! Is there anything else I can help you with about your tires?" },
      ] as Message[]));
    }, []);
    // TRAC-732 return path: trigger TRACI's comeback. We send only a lightweight MARKER
    // — the BACKEND assembles the actual recap from its own per-handoff transcript.
    const HANDOFF_RETURN_MARKER = '[RETURN_FROM_HANDOFF]';

    // Send the marker to TRACI (no visible user bubble) so its reply renders as the
    // "Back with TRACI" comeback. Uses the live ref to dodge stale closures.
    const sendRecapToAgent = (): void => {
      if (!handleSendMessageRef.current) return;
      // small delay so the back-to-ai divider's setMessages flushes first (recap
      // turn lands AFTER the closed band) and any handoff state settles.
      setTimeout(() => {
        // If the customer re-opened live chat during this window, skip the recap
        // entirely — the new handoff is the action now, not a comeback from the AI.
        const hs = handoffStateRef.current;
        if (hs === 'connecting' || hs === 'queued' || hs === 'active') return;
        // TRAC-732: tag this comeback with the current escalate-generation. If the customer
        // re-engages a rep before it finishes streaming, the generation changes and the stream
        // loop discards the comeback — so it can't land, out of place, after the next leave.
        void handleSendMessageRef.current?.(HANDOFF_RETURN_MARKER, undefined, true, undefined, false, undefined, escalateGenRef.current);
      }, 150);
    };

    // TRAC-732: the backend declined the handoff because NO live Tire Expert can take it
    // (outside hours, or everyone busy). Roll back the optimistic connecting UI (we never
    // actually started a handoff) and tell the customer.
    //
    // TRAC-1006: told HERE, not by the agent. This used to send the agent a hidden
    // "[SYSTEM EVENT — LIVE CHAT UNAVAILABLE]" turn and let it decline in its own voice,
    // which fails exactly when the agent is down: an after-hours customer during an outage
    // got "Something went wrong" instead of when we reopen. The copy lives beside the phone
    // tree (handoffDeclinedMessage) and needs no network.
    const declineHandoffNoAgents = (connectingId: string, reason: string | null, hours: string | null,
                                    provider: string | null = null, testRoute = false): void => {
      connectingMsgIdRef.current = null;
      setHandoffActivity(null);
      setMessages((prev) => prev.filter((m) => m.id !== connectingId)); // drop the "Connecting…" turn
      collapseUnjoinedBand();        // remove the pending "Connecting to a Tire Expert…" band header
      setHandoff('idle');
      chatAgentService.stopHandoffListener();
      // The decline is fresher than the 60s availability poll: nobody can take a chat right
      // now. Without this an outage screen that returns after the decline still offers
      // "Talk to a Tire Expert" -- straight back into the same decline -- instead of the
      // phone number.
      // TRACI-1055: a Connect desk refusal says nothing about OUR hours, so the band reads
      // "no one available" and keeps our own reopen time, never the desk's.
      const viaConnect = provider === 'connect';
      setLiveChatAvail((a) => ({
        ...a,
        state: reason === 'closed_hours' && !viaConnect ? 'closed_hours' : 'closed_no_agents',
        hours: viaConnect ? a.hours : hours ?? a.hours,
        failOpen: false,
      }));
      setMessages((prev) => {
        const next = [...prev, {
          id: `declined-${Date.now()}`,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isClientOnly: true,
          parts: [{ type: 'text', content: handoffDeclinedMessage(reason, hours, provider, testRoute) }],
        } as Message];
        chatAgentService.saveMessagesToSession(next);
        return next;
      });
    };

    // TRAC-965: Adobe's "live chat ended" — only for a conversation we said "connected" for.
    // A rep who joined but never got named (the funnel counts that join; Adobe's connected
    // keys on the identity) would otherwise produce an end with no start, which reads as a
    // bug in Adobe. Those orphans are logged so the undercount is measurable. Every caller
    // is already gated on the handoff having been active; this adds the pairing gate.
    const emitLiveChatEnded = (reason: string) => {
      const sid = chatAgentService.agentSessionId;
      if (liveChatConnectedEmitted(sid)) {
        const msg = postLiveChatEvent(window, 'live_chat_ended', sid);
        logger.info('[TRAC-965] live chat ended', reason, msg ?? '(not embedded — no postMessage sent)');
      } else {
        logger.info('[TRAC-965] live chat ended with no connected — not sent to Adobe', reason);
      }
      clearLiveChatConnected();
    };

    const handleHandoffStatus = (state: string, name?: string, avatar?: string, agentId?: string, agentEmployeeId?: string, reason?: string) => {
      // TRAC-732: ignore any handoff event that arrives while we're NOT in a handoff.
      if (handoffStateRef.current === 'idle') return;
      if (name || avatar) updateAgentInfo(name, avatar);
      // TRAC-732 follow-up: on the real-identity event (agent_named), forward the connected
      // rep to the host page for sales-credit attribution. No-op for every other state, when
      // not embedded, or without a name. Re-fires on transfer (a fresh agent_named).
      postLiveAgentConnected(window, state, handoffAgentNameRef.current, chatAgentService.agentSessionId, agentId, agentEmployeeId);
      // TRAC-965: Adobe's "live chat connected" keys on the same moment — a real identity —
      // but is capped at one per request: a transfer re-credits the next rep above (last rep
      // wins sales credit) and is NOT a second connection here, so the ask-vs-arrive gap
      // counts conversations, not reps. The latch is written beside the session so the
      // matching "ended" can land on a later page.
      const repName = (handoffAgentNameRef.current || '').trim();
      if (state === 'agent_named' && repName && repName !== 'Tire Rack team'
          && !liveChatConnectedEmitted(chatAgentService.agentSessionId)) {
        markLiveChatConnected(chatAgentService.agentSessionId);
        const msg = postLiveChatEvent(window, 'live_chat_connected', chatAgentService.agentSessionId);
        logger.info('[TRAC-965] live chat connected', msg ?? '(not embedded — no postMessage sent)');
      }
      if (state === 'connecting' || state === 'queued') {
        setHandoff(state === 'queued' ? 'queued' : 'connecting');
      } else if (state === 'agent_joined') {
        // The only outcome that counts as success: somebody real is here. The join
        // event often carries the "Tire Rack team" placeholder (real identity arrives
        // later via agent_named) — filter it here too: an absent specialist is honest,
        // a placeholder in the traces reads as a rep literally named that.
        handoffFunnel.handoffJoined(
          undefined,
          name && name !== 'Tire Rack team' ? name : liveRepName(),
        );
        setHandoff('active');
        setHandoffActivity('awaiting'); // show "Waiting on {name}" until they type/reply
        // A NEW rep is joining — their avatar is unknown until their profile resolves, so
        // clear any prior rep's avatar now. updateAgentInfo only SETS an avatar (never
        // clears), so without this a transfer to a rep with no photo would keep showing
        // the previous rep's image in the header. (TRAC-732)
        handoffAgentAvatarRef.current = null;
        setHandoffAgentAvatar(null);
        // Flip the "Connecting…" turn to "Connected ✅" — or "Transferred ✅" if this
        // connect is the far side of a transfer.
        const wasTransfer = pendingTransferRef.current;
        pendingTransferRef.current = false;
        const connectingId = connectingMsgIdRef.current;
        if (connectingId) {
          // Emoji trailing — a leading emoji gets parsed as a markdown list marker.
          setMessages((prev) => prev.map((m) =>
            m.id === connectingId ? { ...m, content: wasTransfer ? 'Transferred ✅' : 'Connected ✅' } : m,
          ));
        }
        // The band header was already appended (pending, "While we wait") at escalate
        // time. Flip it to "Chat with {name} started" the moment the rep connects.
        setMessages((prev) => prev.map((m) =>
          (m.responder === 'divider' && m.dividerKind === 'enter-human' && m.dividerPending)
            ? { ...m, dividerPending: false,
                agentName: handoffAgentNameRef.current,
                agentAvatar: handoffAgentAvatarRef.current || undefined }
            : m,
        ));
        persistTranscript();
      } else if (state === 'agent_typing') {
        setHandoffActivity('typing');
      } else if (state === 'agent_idle') {
        setHandoffActivity((a) => (a === 'typing' ? 'awaiting' : a));
      } else if (state === 'agent_named') {
        // name already applied via updateAgentInfo; header + message labels now show it
      } else if (state === 'transferred') {
        // TRAC-732: LP transferred the chat to another skill — the rep was unassigned and
        // the customer is back in the queue for the next person. Close the CURRENT human
        // band like a normal end (but we do NOT go back to the AI — the handoff is still
        // active and the poll keeps running), then reopen the same "connecting / waiting
        // for a Tire Expert" state so the next rep flows in as a fresh band, exactly like
        // the first connect.
        if (handoffStateRef.current === 'active') {
          appendDivider('end-human');   // closes the current red band ("Chat with {name} ended")
        } else {
          collapseUnjoinedBand();       // rep never fully engaged the band — just collapse it
        }
        // Clear the prior rep's identity BEFORE opening the new band so it never shows the old name.
        handoffAgentNameRef.current = 'Tire Rack team';
        setHandoffAgentName('Tire Rack team');
        handoffAgentAvatarRef.current = null;
        setHandoffAgentAvatar(null);
        // Back to "waiting for a rep" — mirror the escalate band-open so the customer lands
        // at the exact same visual point as the first connect. Flag it as a transfer so the
        // copy reads "Transferring…" now and "Transferred ✅" when the next rep connects.
        pendingTransferRef.current = true;
        setHandoff('queued');
        const reconnectingId = `connecting-${Date.now()}`;
        connectingMsgIdRef.current = reconnectingId;
        setMessages((prev) => [...prev, {
          id: reconnectingId,
          role: 'user',
          content: 'Transferring you to a Tire Expert now…',
          timestamp: new Date(),
          isClientOnly: true,
          isConnectingTurn: true,
        } as Message]);
        appendDivider('enter-human', { pending: true });
        persistTranscript();
        setHandoffActivity('awaiting');
        scheduleScrollTurnToTop(true);
      } else if (state === 'ended' || state === 'error' || state === 'voided') {
        // TRAC-732 intent model: 'voided' = the backend severed this handoff because the
        // customer's latest intent was to leave (reconcile-or-sever). Treat it like a clean end —
        // close the band and hand back to the AI, no error alarm.
        const wasActive = handoffStateRef.current === 'active';
        // TRAC-1006: the page heard this from the bridge; the backend has not said so. Keep
        // the outage screen's handoff dismissal until it does (confirmHandoffOver below).
        setHandoff('idle', { holdOverlayDismissal: true });
        setHandoffActivity(null);
        chatAgentService.stopHandoffListener();
        // TRAC-732 funnel: this end is only reported from the two places the CUSTOMER ended
        // it (Leave Live Chat, Clear conversation). A chat the rep closed -- the common way a
        // live chat ends -- recorded nothing, so "ended" on the funnel undercounted. Same
        // event, from the other side; 'error' is named as such so a bridge death does not
        // read as a rep politely closing. 'voided' is the customer's own leave arriving via
        // the intent model and is already counted by the tab that pressed the button.
        if (wasActive && state !== 'voided') {
          // TRACI-1111: the backend now names why it ended (quiet page, lost connection, rep
          // close...); a missing or unknown reason keeps the old 'rep_left' label.
          const endReason = state === 'error' ? 'error' : handoffFunnel.handoffEndReason(reason);
          handoffFunnel.handoffEnded(endReason, liveRepName());
          emitLiveChatEnded(endReason);
        }
        if (wasActive) {
          // A human was in the chat — close the red band with its bottom edges.
          appendDivider('end-human');  // in-band "Chat with {name} ended" — closes the red band
          showConversationSurvey();    // TRAC-732: survey sits directly under the closed band
          appendDivider('back-to-ai'); // big "Back with the AI tire agent" marker below the band
          if (state === 'error') {
            appendSystemMessage(`The live chat ended unexpectedly. The AI tire agent is back with you.`);
          } else {
            sendRecapToAgent();        // hand the human-chat turns to TRACI for its comeback
          }
        } else {
          // Ended/errored before any human joined — collapse the band and explain.
          collapseUnjoinedBand();
          if (state === 'error') {
            // TRAC-732: the background LivePerson connection errored (bridge crash / sever)
            // before a rep joined — record it so this async failure path is findable too.
            logLpFailure('handoff_status_error', {
              reason: 'bridge_error_event',
              state,
              sessionId: chatAgentService.agentSessionId,
            });
          }
          // CPOC-150: the ask is over, so the 60 s "still trying" notice must not follow it.
          const endedAsk = handoffFunnel.handoffAskedAt();
          if (endedAsk !== null) slowNoticeForRef.current = endedAsk;
          appendSystemMessage(state === 'error'
            ? `Sorry — we couldn't reach someone just now. The AI tire agent is still here to help.`
            : reason === 'unjoined'
              ? handoffUnjoinedMessage(attemptDeptRef.current ?? connectingTurnDepartment(messagesRef.current))
              : `The live chat ended. The AI tire agent is back with you.`);
        }
        persistTranscript();
        if (overlayDismissedForHandoffRef.current) confirmHandoffOver();
      }
    };
    // TRAC-1006: a bridge ended/error/voided event closed the handoff on this page, but the
    // outage screen's dismissal is released only when /agent/handoff-state confirms it --
    // a LivePerson bridge that crashed and was rebuilt server-side is not the end of the
    // conversation, and un-dismissing on it put the screen back over a live rep. The ask
    // retries on the resume schedule, so it holds while the backend is unreachable. It only
    // ever RELEASES: an "active" answer means "not confirmed yet" (see confirmHandoffEnded
    // for why restoring from here left a dead handoff looking live).
    //
    // The first ask waits a beat: the bridge broadcasts `ended` BEFORE it flips the
    // session's mode to ai (bridge.py _run), so an immediate ask can read "still live".
    const HANDOFF_END_CONFIRM_DELAY_MS = 1000;
    const confirmHandoffOver = () => {
      const gen = escalateGenRef.current;
      const superseded = () => unmountedRef.current
        || escalateGenRef.current !== gen                 // a new handoff started meanwhile
        || handoffEngaged(handoffStateRef.current)         // …or this one was restored
        || !overlayDismissedForHandoffRef.current;         // …or something else released it
      setTimeout(() => {
        if (superseded()) return;
        void chatAgentService.confirmHandoffEnded({ isCancelled: superseded }).then((over) => {
          if (!over || superseded()) return;
          overlayDismissedForHandoffRef.current = false;
          setAnnouncementDismissed(false);
          persistHandoffAttemptRef.current();
        });
      }, HANDOFF_END_CONFIRM_DELAY_MS);
    };
    /**
     * TRAC-732: the conversation so far, as the rep (and the summariser) receive it.
     * `reasonLabel` is a routing-option click, which never becomes a chat message.
     */
    const buildHandoffTranscript = (reasonLabel?: string) => {
      // Assistant text lives in `parts` (type 'text'), not `content` — pull it out so
      // TRACI's replies are included in the handoff transcript.
      const messageText = (m: Message): string => {
        if (m.displayContent) return m.displayContent;
        if (m.content) return m.content;
        if (m.parts) {
          return m.parts
            .filter((p) => p.type === 'text')
            .map((p) => (p as { content?: string }).content || '')
            .join('\n')
            .trim();
        }
        return '';
      };
      // Read the LIVE messages via the ref, not the `messages` closure. On the
      // conversational path escalateToHuman fires from a stale streaming closure, so
      // `messages` is missing the customer's just-sent transfer-request turn.
      // TRAC-406: mark the customer's own lines typed INSIDE a live chat (between the enter and
      // end dividers), so the backend can tell what they asked after the last chat ended.
      let inLiveChat = false;
      const transcript: Array<{ role: string; text: string; name?: string; live?: boolean }> = [];
      for (const m of messagesRef.current) {
        if (m.responder === 'divider') {
          if (m.dividerKind === 'enter-human') inLiveChat = true;
          else if (m.dividerKind === 'end-human' || m.dividerKind === 'back-to-ai') inLiveChat = false;
          continue;
        }
        if (m.isClientOnly) continue;
        const text = messageText(m);
        if (!text) continue;
        if (m.responder === 'human') transcript.push({ role: 'human_agent', text, name: m.agentName });
        else if (inLiveChat && m.role === 'user') transcript.push({ role: m.role, text, live: true });
        else transcript.push({ role: m.role, text });
      }
      // TRAC-732: a routing-option click (e.g. "Help with a Hazard Claim") never becomes
      // a chat message — the buttons just disable — so without this the rep's transcript
      // and its summary are blind to the customer's stated reason. Appended here (not via
      // setMessages) because messagesRef only updates on the next render, after this runs.
      if (reasonLabel) transcript.push({ role: 'user', text: reasonLabel });
      return transcript;
    };
    /**
     * TRAC-1066: the network half of an escalate — the request itself and everything that
     * answers it (queue, decline, failure). Lifted out of escalateToHuman so the page that
     * INHERITS a press can send the same request and handle the same answers: a customer
     * who pressed the button and changed tirerack.com page before it left the browser is
     * otherwise waiting on a handoff nobody ever asked for.
     */
    const sendEscalate = async (
      transcript: ReturnType<typeof buildHandoffTranscript>,
      handoffDept: string | null | undefined,
      connectingId: string,
      initiatedBy?: HandoffInitiator | null,
    ): Promise<void> => {
      try {
        await chatAgentService.initSession();
        // TRAC-1006: a first-ever handoff has no session id until initSession, so the
        // attempt could not be keyed when the state flipped to connecting.
        //
        persistHandoffAttempt();
        // Escalate FIRST, THEN start polling — start_handoff seeds a fresh state; starting
        // the listener before that race let its immediate since=0 poll replay the PREVIOUS
        // handoff's events on a 2nd handoff.
        // TRAC-1066: the record stops saying `pressed` on the line before the request goes
        // out, and that line is inside the service: escalateToHuman waits on the LivePerson
        // session (up to 8 s, TRACI-629) and the request headers first, which is where the 1.3 to 4.3 s
        // gap measured on dev actually is. Marking `sent` out here, before that wait, left a
        // record claiming a request that never left (found on dev, 21 Sep 2026). sessionStorage
        // is written synchronously, so a navigation that kills the fetch mid-air still leaves
        // `sent` behind, and a record that still says `pressed` is proof nothing left.
        const result = await chatAgentService.escalateToHuman(transcript, null, handoffDept ?? undefined, () => {
          escalateRequestRef.current = 'sent';
          persistHandoffAttempt();
        }, initiatedBy ?? undefined);
        // TRAC-732 intent model: the backend refused this connect because a newer action — a
        // leave the customer tapped right after — already won the race. There's nothing to
        // start; the customer's last intent was to leave, so make sure the page is on the AI.
        if (result.status === 'superseded') {
          chatAgentService.stopHandoffListener();
          if (handoffStateRef.current === 'connecting') setHandoff('idle');
          return;
        }
        // Belt-and-suspenders for a same-page race the server didn't catch: endHandoffByUser()
        // flips state to 'idle', so if we're no longer 'connecting' the customer bailed while
        // this was in flight — undo whatever the backend just (re)started; their leave wins.
        if (handoffStateRef.current !== 'connecting') {
          void chatAgentService.endHandoff({ notifyAgentLeft: false });
          chatAgentService.stopHandoffListener();
          return;
        }
        // Nobody available — the backend did NOT start a handoff. Roll back and let
        // TRACI deliver the decline in its own voice.
        if (result.status === 'no_agents_available') {
          // The server already recorded WHY. This just stops the browser's clock, so
          // a decline is not left looking like somebody still waiting.
          handoffFunnel.handoffSettled();
          declineHandoffNoAgents(connectingId, result.reason, result.hours, result.provider, result.testRoute);
          return;
        }
        const resolvedDept = result.department;
        // TRAC-732 (per-department feedback): remember which team THIS live chat is with,
        // so when it ends the survey card grades the right department (falls back to the
        // dept we requested if the backend didn't echo one).
        // TRAC-406: never carry an earlier chat's team forward; this chat's own or nothing.
        activeHandoffDeptRef.current = resolvedDept ?? handoffDept ?? null;
        // When the dept wasn't known up front (classifier path), name the team now —
        // but only while still connecting (don't clobber a "Connected ✅" flip).
        if (resolvedDept) {
          attemptDeptRef.current = resolvedDept;   // TRAC-1007: the slow-wait message's number
          persistHandoffAttempt();
          // TRAC-1049: and on the SHARED attempt, so every other tab of this conversation
          // names the same team at the same moment this heading does. Without it, the only
          // record of the team is this tab's sessionStorage and this tab's React state.
          handoffFunnel.handoffDepartmentConfirmed(resolvedDept);
          setMessages((prev) => prev.map((m) =>
            m.id === connectingId && typeof m.content === 'string' && m.content.startsWith('Connecting')
              ? { ...m, content: connectingLabel(resolvedDept) } : m,
          ));
          persistTranscript();
        }
        // TRACI-1053 (CPOC-109): tell a tester this handoff went to the Connect desk. A real
        // shopper (share dial, testRoute false) gets the same wait as a LivePerson handoff.
        if (result.provider === 'connect' && result.testRoute) {
          setMessages((prev) => {
            const next = withConnectDeskNotice(prev, connectingId);
            if (next !== prev) chatAgentService.saveMessagesToSession(next);
            return next;
          });
        }
        chatAgentService.startHandoffListener({
          onHumanMessage: (text, name, html) => appendHumanMessage(text, name, html),
          onStatus: (state, name, avatar, agentId, agentEmployeeId, reason) => handleHandoffStatus(state, name, avatar, agentId, agentEmployeeId, reason),
        });
      } catch (err) {
        // TRAC-732: capture WHY the connect failed (the old bare catch discarded it). A
        // cross-origin/third-party iframe — the extension embed — can have the connect
        // fetch blocked or aborted even though the backend succeeds; this forwards the
        // reason + browser context to the backend so it's findable in Datadog ([LP_FAIL]).
        const anyErr = err as { netError?: boolean; httpStatus?: number; stage?: string; name?: string; message?: string; elapsedMs?: number };
        logLpFailure('connect_failed', {
          reason: anyErr?.netError ? 'network_or_cors' : (anyErr?.httpStatus ? `http_${anyErr.httpStatus}` : 'exception'),
          stage: anyErr?.stage ?? 'unknown',
          httpStatus: anyErr?.httpStatus ?? null,
          name: anyErr?.name,
          message: String(anyErr?.message ?? '').slice(0, 200),
          elapsedMs: anyErr?.elapsedMs ?? null,
          sessionId: chatAgentService.agentSessionId,
        });
        // TRACI-1092: a request that got NO answer may still have reached the backend and
        // started the handoff (a frame remount mid-request, a dropped connection). Before
        // apologising, ask the backend for a bounded window, keeping "Connecting…" on screen.
        // A refusal the browser did see (http_403, a session that never started) still fails
        // at once below: those are real failures, and nothing started.
        if (anyErr?.netError && anyErr?.stage === 'escalate' && handoffStateRef.current === 'connecting') {
          const started = await chatAgentService.confirmHandoffStarted({
            isCancelled: () => handoffStateRef.current !== 'connecting',
          });
          // The customer left while we checked: their leave already settled the page.
          if (started === null) return;
          if (started) {
            // Not a failure: no handoff_failed. The funnel stays waiting, so the rep's join
            // still files handoff_joined; this line says the answer was lost on the way back.
            logLpFailure('connect_recovered', {
              stage: 'escalate',
              elapsedMs: anyErr?.elapsedMs ?? null,
              sessionId: chatAgentService.agentSessionId,
            });
            activeHandoffDeptRef.current = handoffDept ?? null;
            persistHandoffAttempt();
            // startHandoffListener stops any earlier listener first, so a resume that also
            // started one cannot leave two polls rendering the rep twice.
            chatAgentService.startHandoffListener({
              onHumanMessage: (text, name, html) => appendHumanMessage(text, name, html),
              onStatus: (state, name, avatar, agentId, agentEmployeeId, reason) => handleHandoffStatus(state, name, avatar, agentId, agentEmployeeId, reason),
            });
            return;
          }
        }
        // TRAC-862: roll the optimistic connecting UI back BEFORE announcing the
        // failure.
        //
        // This used to only flip state to idle and append the apology, which left
        // two things on screen that contradicted it: the "Connecting you to a Tire
        // Expert now…" turn, and the pending "While we wait" band header — a band
        // that, once pending, only ever resolves on a successful join. So a failed
        // connect read as "connecting… / while we wait / sorry, we couldn't connect
        // you", all at once, and the band stayed open forever.
        //
        // declineHandoffNoAgents() above is the same situation (handoff never
        // happened) and already had the correct rollback; this path simply never
        // got it. Same four steps, same order.
        const failedConnectingId = connectingMsgIdRef.current;
        connectingMsgIdRef.current = null;
        setHandoffActivity(null);
        if (failedConnectingId) {
          setMessages((prev) => prev.filter((m) => m.id !== failedConnectingId));
        }
        collapseUnjoinedBand();
        persistTranscript();
        setHandoff('idle');
        chatAgentService.stopHandoffListener();
        // Their [LP_FAIL] line above says WHY for somebody reading Datadog; this makes
        // it a number on the dashboard. Same moment, two different jobs.
        handoffFunnel.handoffFailed(handoffFunnel.describeConnectFailure(err));
        appendSystemMessage(`We couldn't reach a person just now. I'm still here — ask me anything, or call 1-800-428-8355 (Sales ext. 4370, Customer Care ext. 4360).`);
      }
    };
    // TRAC-1212: `initiatedBy` is who started it, recorded on the ask and on the server's
    // records. Every caller says; one that cannot records nothing rather than a guess.
    const escalateToHuman = (deptOverride?: string, reasonLabel?: string, initiatedBy?: HandoffInitiator, requestReason?: string) => {
      logger.info(`[HANDOFF] escalateToHuman invoked — state=${handoffStateRef.current}`);
      agentEscalateDueRef.current = false; // TRAC-475: from here the real attempt state is written
      // Bail only when a handoff is genuinely in progress; allow (re)starting from any
      // terminal state (EmbedPage's button calls this on BOTH 'idle' and 'ended').
      const st = handoffStateRef.current;
      if (st === 'connecting' || st === 'queued' || st === 'active') {
        logger.warn(`[HANDOFF] escalate ignored — already ${st}`);
        return;
      }
      // TRAC-475: this page is asking for itself, so there is no inherited press left for the
      // resume check to re-issue (TRAC-1066). Without this, a "no handoff yet" answer landing
      // while this request waits to leave would send a second escalate on top of it.
      reissuedEscalateRef.current = true;
      // TRAC-732: re-engaging a rep invalidates any AI "welcome back" comeback still streaming
      // from the last time they left — so it never lands out of place after this new handoff.
      escalateGenRef.current += 1;
      // Kill any in-flight AI stream (e.g. a "welcome back" reply) so the second
      // handoff is the whole action, not something streaming around the new band.
      chatAgentService.closeActiveConnection();
      setRoutingPending(false); // they picked / we're connecting — unlock the button
      // Connecting is engagement by any measure, whichever path got us here (a door, the
      // agent's own handoff, the soft-handoff card). Belt and braces with handleCardSelect
      // so the button can never still be its full entry size above a live queue.
      setBannerInteracted(true);
      setIsLoading(false);
      setIsStreaming(false);
      // Drop a half-streamed AI reply (return-to-AI recap), but NOT the routing prompt.
      setMessages((prev) => prev.filter((m) => !(m.role === 'assistant' && m.isStreaming && !m.isRoutingPrompt)));
      // Resolve the department NOW so the connecting turn names the right team.
      const handoffDept = deptOverride ?? pendingHandoffDeptRef.current;
      pendingHandoffDeptRef.current = null;
      attemptDeptRef.current = handoffDept ?? null;   // TRAC-1007: a new attempt starts clean
      attemptInitiatorRef.current = initiatedBy ?? null;   // TRAC-1212: and says who started it
      agentHandoffInitiatorRef.current = null;             // consumed: the next agent ask sets its own
      // TRAC-406: the survey names THIS chat's team; an earlier chat's team must not leak in.
      activeHandoffDeptRef.current = handoffDept ?? null;
      slowNoticeForRef.current = null;
      // TRAC-1066: pressed, not sent. setHandoff('connecting') below writes the attempt
      // record, and until the request actually leaves (sendEscalate) that record has to say
      // so — otherwise the next page cannot tell a request in flight from one never made.
      escalateRequestRef.current = 'pressed';
      // Fresh handoff → clear the prior specialist's identity so THIS band shows the new person.
      handoffAgentNameRef.current = 'Tire Rack team';
      setHandoffAgentName('Tire Rack team');
      handoffAgentAvatarRef.current = null;
      setHandoffAgentAvatar(null);
      pendingTransferRef.current = false; // a fresh connect is never a transfer
      setHandoff('connecting');
      // TRAC-732: the clock starts HERE, on the press, not when the server accepts.
      // The wait a customer sits through begins with the button.
      handoffFunnel.handoffRequested(handoffDept ?? undefined, initiatedBy, requestReason);
      // TRAC-965: Adobe's "live chat requested" — the press, not the server's accept. A new
      // ask also re-arms "connected" (capped at one per request), so a second conversation
      // in the same session counts again.
      clearLiveChatConnected();
      logger.info('[TRAC-965] live chat requested',
        postLiveChatEvent(window, 'live_chat_requested', chatAgentService.agentSessionId) ?? '(not embedded — no postMessage sent)');
      // Render the connecting turn as a real user-style turn — big, left-aligned,
      // scrolled to the top, exactly like a submitted message.
      const connectingId = `connecting-${Date.now()}`;
      connectingMsgIdRef.current = connectingId;
      setMessages((prev) => [...prev, {
        id: connectingId,
        role: 'user',
        // Generic on purpose — the team name is filled in below once the backend confirms
        // which team ACTUALLY gets the chat, so we never flash an optimistic team.
        content: connectingLabel(null),
        timestamp: new Date(),
        isClientOnly: true,
        isConnectingTurn: true, // anchors its own turn for scroll/min-height
      } as Message]);
      // Open the human-chat band immediately (pending header "Connecting to a Tire
      // Expert…") so messages the customer queues BEFORE a human joins render INSIDE it.
      appendDivider('enter-human', { pending: true });
      persistTranscript();
      setHandoffActivity('awaiting');
      scheduleScrollTurnToTop(true); // releaseLockAfter — escalate has no stream to release it
      // Ensure the session exists first (idempotent), then escalate + start the listener.
      void sendEscalate(buildHandoffTranscript(reasonLabel), handoffDept, connectingId, initiatedBy);
    };
    const endHandoffByUser = () => {
      if (handoffStateRef.current === 'idle') return;
      leaveGenRef.current += 1;
      const wasActive = handoffStateRef.current === 'active';
      // TRAC-732: leaving BEFORE a rep arrived is giving up; leaving after is the
      // conversation finishing. Same button, opposite meanings -- counting both as an
      // ending would hide every wait that ran long enough for somebody to walk away.
      if (wasActive) {
        handoffFunnel.handoffEnded('customer_left', liveRepName());
        emitLiveChatEnded('customer_left');
      } else {
        handoffFunnel.handoffAbandoned('cancelled');
      }
      // Did anything actually get said in the band? — a rep message, or a customer
      // message sent while connecting/waiting/live.
      const hadExchange = messages.some((m) =>
        m.responder === 'human' ||
        (m.inHumanZone && m.role === 'user' && (m.displayContent || m.content || '').trim())
      );
      setHandoff('idle');
      setHandoffActivity(null);
      chatAgentService.stopHandoffListener(); // stop polling — we've left; no more LP events
      if (wasActive && hadExchange) {
        // A real conversation happened — close the red band with its bottom edges.
        appendDivider('end-human');  // in-band "Chat with {name} ended" — closes the red band
        showConversationSurvey();    // TRAC-732: survey inside the band (customer-ended path)
        appendDivider('back-to-ai'); // big "Back with the AI tire agent" marker below the band
        // NOTE: no scheduleScrollTurnToTop() here — it anchors the last USER message (above
        // the divider) and fights the end-of-chat framer, which owns the scroll now and
        // glides the "Chat with {name} ended" divider to the top instead.
      } else {
        // Nothing to preserve: never connected, OR a rep joined but no message exchanged.
        collapseUnjoinedBand();
        const connId = connectingMsgIdRef.current;
        if (connId) setMessages((prev) => prev.filter((m) => m.id !== connId));
        connectingMsgIdRef.current = null;
        const userText = wasActive ? 'Left the live chat' : 'Left the live chat queue';
        const reply = wasActive
          ? `No problem — you're back with me. Is there anything else I can help you with?`
          : `No problem — you're back with me now instead of waiting for a live agent. ` +
            `Is there anything else I can help you with?`;
        // Same principle as the cancel: leaving is a system state change the customer made, not
        // something they "said" — render it as a big-black system marker, not a customer bubble.
        void handleSendMessageRef.current?.(userText, undefined, false, undefined, true, {
          parts: [{ type: 'text', content: reply }],
        }, undefined, true);
      }
      // AWAIT the teardown before the recap: if the backend hasn't popped this handoff
      // yet, the recap would route to the LP agent instead of the AI.
      void (async () => {
        try { await chatAgentService.endHandoff({ notifyAgentLeft: wasActive }); }
        finally { if (wasActive && hadExchange) sendRecapToAgent(); }
      })();
    };
    // TRAC-732: single teardown so a handoff never bleeds into the next session.
    // Call BEFORE the session id rolls over so endHandoff() targets the old session.
    const resetHandoff = () => {
      leaveGenRef.current += 1;
      agentEscalateDueRef.current = false; // TRAC-475: a cleared chat owes nobody a connect
      // TRAC-732 funnel: clearing the conversation is a way out too. Left unrecorded, a
      // clear mid-wait was invisible on the Live Person tab AND left the funnel armed, so
      // the next page leave -- on whatever session was current by then -- was filed as a
      // give-up that never happened.
      if (handoffStateRef.current === 'active') {
        handoffFunnel.handoffEnded('cleared', liveRepName());
        emitLiveChatEnded('cleared');
      } else {
        handoffFunnel.handoffAbandoned('cleared');
        clearLiveChatConnected(); // the session rolls over — nothing left to pair
      }
      chatAgentService.endHandoff();   // stops the poll + ends the backend handoff
      connectingMsgIdRef.current = null;
      setRoutingPending(false);        // clear the "Cancel request" lock on a session reset
      setHandoffActivity(null);
      handoffAgentNameRef.current = 'Tire Rack team';
      setHandoffAgentName('Tire Rack team');
      handoffAgentAvatarRef.current = null;
      setHandoffAgentAvatar(null);
      setHandoff('idle');              // dispatches the window event -> EmbedPage header resets
    };

    // TRAC-732: keep the hook's escalate bridge pointing at the latest closure.
    useEffect(() => {
      escalateToHumanRef.current = (initiatedBy?: HandoffInitiator) => escalateToHuman(undefined, undefined, initiatedBy);
      // TRACI-916: same path as the card's Connect (handleCardSelect, __soft_connect__).
      softAckConnectRef.current = (department: string) => {
        removeSoftHandoff();
        escalateToHuman(department, undefined, agentHandoffInitiatorRef.current ?? 'ai_offered', 'soft_ack');
      };
    });
    // TRACI-628: staff sent this chat to a rep from the live chat monitor. Tell the shopper
    // why it is happening, then run the same escalate their own button runs.
    const lastMessageAt = messages.length ? new Date(messages[messages.length - 1].timestamp).getTime() : null;
    useStaffHandoffPoll({
      sessionId: agentSessionIdState,
      hasShopperMessage: messages.some((m) => m.role === 'user' && !m.isConnectingTurn),
      handoffIdle: handoffState === 'idle' || handoffState === 'ended',
      busy: isLoading || isStreaming,
      lastActivityAt: Number.isFinite(lastMessageAt) ? lastMessageAt : null,
      check: (sid) => chatAgentService.checkStaffHandoff(sid),
      onSend: (send) => {
        logger.info(`[TRACI-628] staff sent this chat to a rep (team: ${send.department ?? 'chat picks'})`);
        // escalateToHuman refuses while a handoff is in progress; no notice for a refused one.
        const st = handoffStateRef.current;
        if (st !== 'idle' && st !== 'ended') return;
        setMessages((prev) => [...prev, {
          id: `staff-sent-${Date.now()}`,
          role: 'assistant',
          content: STAFF_SENT_NOTICE,
          timestamp: new Date(),
          isClientOnly: true,
        } as Message]);
        // The team staff chose routes the handoff (its LivePerson skill or Connect queue);
        // none lets the chat pick, exactly as the shopper's own press does.
        if (send.department) escalateToHuman(send.department, undefined, 'staff_sent');
        else escalateToHumanRef.current('staff_sent');
      },
    });
    // TRAC-475: the replay of a reply a reload cut short carries the agent's handoff too. Treated
    // exactly like the live stream: remembered at once, started 400 ms after the reply settles.
    replayHandoffRef.current = {
      onHandoff: (escalation, department) => {
        if (escalation !== 'immediate') return;
        const dept = (department || '').trim();
        if (dept) pendingHandoffDeptRef.current = dept;
        agentEscalateDueRef.current = true;
        persistHandoffAttemptRef.current();
      },
      onDone: () => {
        if (agentEscalateDueRef.current) setTimeout(() => escalateToHumanRef.current(), 400);
      },
    };

    // TRAC-732 follow-up (dev/SIT): `?simulateAgentConnect=<name>` fires a synthetic
    // live_agent_connected postMessage on load — no live rep needed — so the web team can
    // build/verify their attribution listener. Runs the exact same postMessage path as a
    // real agent_named. Posts only when embedded (parent frame); logs either way so a
    // standalone dev load still confirms it fired.
    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const simName = params.get('simulateAgentConnect');
      if (!simName) return;
      // Optional: simulate the full payload — ?simulateAgentId=jsmith&simulateAgentEmployeeId=E123
      const msg = postLiveAgentConnected(
        window, 'agent_named', simName, chatAgentService.agentSessionId,
        params.get('simulateAgentId') || undefined,
        params.get('simulateAgentEmployeeId') || undefined,
      );
      logger.info('[TRAC-732] simulateAgentConnect fired',
        msg ? msg : '(not embedded in a parent frame — no postMessage sent)');
    }, []);

    // TRAC-965 (dev/SIT): `?simulateLiveChat=available,requested,connected,ended` (or `all`)
    // fires the four Adobe live-chat moments in that order on load — no LivePerson session
    // needed — so the web team can build/verify their listener and /analytics-test can show
    // them arriving. Same postMessage path as the real moments; posts only when embedded,
    // logs either way. Does not touch the real latches. Ref-guarded so React's dev-only
    // StrictMode double effect does not fire the sequence twice.
    const simulatedLiveChatRef = useRef(false);
    useEffect(() => {
      const raw = new URLSearchParams(window.location.search).get('simulateLiveChat');
      if (!raw || simulatedLiveChatRef.current) return;
      simulatedLiveChatRef.current = true;
      const wanted = raw === 'all'
        ? [...LIVE_CHAT_EVENTS]
        : raw.split(',').map((s) => `live_chat_${s.trim()}`);
      for (const ev of wanted) {
        if (!isLiveChatEvent(ev)) { logger.warn('[TRAC-965] simulateLiveChat: unknown moment', ev); continue; }
        const msg = postLiveChatEvent(window, ev, chatAgentService.agentSessionId);
        logger.info('[TRAC-965] simulateLiveChat fired',
          msg ? msg : `${ev} (not embedded in a parent frame — no postMessage sent)`);
      }
    }, []);
    // ─────────── end TRAC-732 handoff helpers ───────────

    /**
     * Handle Enter key press
     */
    const handleKeyPress = (
      e: React.KeyboardEvent<HTMLTextAreaElement>,
    ): void => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        // TRAC-1124: on a phone the keyboard goes once the message goes. Only when this
        // Enter really sends (the same gates as the send button), so Return pressed while
        // a reply is still streaming does not close the keyboard with nothing sent.
        if (
          inputValue.trim() && !isLoading && !isStreaming && !pendingSelection &&
          retryAttempt === null && !isMessageTooLong(inputValue)
        ) {
          dismissPhoneKeyboard(inputRef.current);
        }
        handleSendMessage();
      }
    };

    /**
     * Format timestamp
     */
    const formatTime = (timestamp: Date): string => {
      return timestamp.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    };

    /**
     * Clear session and reset chat
     * TRAC-468: Uses clearAllUserState (nuclear option) to ensure no orphaned
     * storage survives — feedback keys, session sync data, sessionStorage, etc.
     */
    const handleClearSession = (): void => {
      // TRAC-732: tear down any live handoff BEFORE the session id rolls over, so
      // endHandoff() targets the old session's LP conversation.
      resetHandoff();
      chatAgentService.clearAllUserState();
      chatAgentService.initSession(); // TRAC-468: Re-initialize session for experiment assignments
      resetTrackingState(); // TRAC-258: Reset interaction tracking dedup state
      // TRAC-637: Tell the persistence hook to drop its cached welcome so the
      // rendered welcome reverts to optimistic immediately, no stale flash.
      setWelcomeClearSignal((n) => n + 1);
      // TRAC-468: Clear expiry flag so welcome message returns on explicit clear
      resetSessionExpiryState();
      setMessages([]);
      setInputValue("");
      setIsLoading(false);
      setIsStreaming(false);
      hasLoadedFromStorageRef.current = false;

      // TRAC-232: Reset YMMV mode (full cleanup)
      resetYmmvState();

      // TRAC-277: Reset vehicle header bar
      setActiveVehicle(null);
      setVehicleHistory([]);
      pendingVehicleContextRef.current = null;

      // Un-latch the mode banner so a cleared conversation shows the entry BUTTON again
      // (the squish is otherwise one-way — no grow-back on scroll).
      setBannerInteracted(false);
      setBannerScrolled(false);

      // TRAC-468: Reset page context state to match service-level clear.
      // clearAllUserState() nulls _pageContext in the service singleton (listeners
      // are preserved). Set pageContextReady=true so the default welcome message
      // renders immediately — if new page context arrives, the live listener updates it.
      setPageContext(null);
      setPageContextReady(true);
      // Re-arm the embed's once-per-load context guard and re-request from the
      // parent, so the cleared session regains the page's vehicle/size context
      // (header chip + contextual welcome) instead of staying generic forever.
      window.dispatchEvent(new CustomEvent('traci:context-refresh'));

      // Reset scroll state and wrapper minHeight to prevent stale layout
      setShowScrollButton(false);
      shouldAutoScrollRef.current = true;
      lockAutoScrollRef.current = false;
      if (lastTurnWrapperRef.current) {
        lastTurnWrapperRef.current.style.minHeight = '';
      }
      // TRAC-478: Scroll container back to top. Without this, scrollTop retains its
      // value from the cleared conversation — then the welcome renders above the
      // viewport (ugly) AND measurePrompts reads a negative gridTop and thinks the
      // whole screen is available, so it shows all 5 prompts instead of viewport-fit.
      if (embedScrollRef.current) {
        embedScrollRef.current.scrollTop = 0;
      }

      setHighlightInput(true);
      inputRef.current?.focus({ preventScroll: true });

      setTimeout(() => {
        setHighlightInput(false);
      }, 1500);
    };

    // TRAC-232/497: YMMV search/select handlers extracted to useYMMV.

    // Expose methods to parent components
    // Must be after handleClearSession is defined
    // TRAC-497: keep the YMMV hook's handleSendMessage ref current (see useYMMV).
    useEffect(() => {
      handleSendMessageRef.current = handleSendMessage;
    });

    useImperativeHandle(ref, () => ({
      handleClearSession,
      focusInput: () => {
        inputRef.current?.focus({ preventScroll: true });
      },
      openDevSettings: () => setShowDevSettings(true),
      sendMessage: (message: string) => {
        handleSendMessage(message);
      },
      // TRAC-732: let the external header (EmbedPage) start/end the live-human handoff.
      // TRAC-1212: the header's live-chat button — the shopper pressed it.
      escalateToHuman: () => { escalateToHuman(undefined, undefined, 'shopper_button'); },
      endHandoff: () => { endHandoffByUser(); },
      // TRAC-790: somebody opened the chat asking for a person. Recorded, not acted on
      // — see the effect that drains it, which has to wait for the opening to settle
      // before it can tell whether the request has already been answered.
      requestHuman: () => setHumanRequested(true),
    }), [handleClearSession, handleSendMessage]);

    /**
     * Handle retry for a specific error message
     * If this is the first message in the chat, reset the chat completely
     * Otherwise, remove the error message and resend the last user message
     */
    // TRAC-862: the auto-retry resend is supplied BY THE FAILING TURN (see
    // scheduleAutoRetry), not reconstructed here.
    //
    // What used to live here scanned the transcript for the newest message with
    // `hasError`, and bailed with a bare `return` when it found none:
    //
    //     const latestError = errors[errors.length - 1];
    //     if (!latestError) return;          // ← silent no-op
    //
    // The error bubble is rendered inside a 300ms fade-out setTimeout, and some
    // failures never render one at all, so the handler routinely found nothing
    // and returned. The scheduler had already flipped the banner to "trying
    // again", so the UI claimed a retry while ZERO requests went out. Verified
    // against the network log: four POSTs for four hand-typed messages and not
    // one retry. That is the whole of "the counter stayed at 1" — the retry was
    // never running, so it could never reach attempt 2, exhaustion, or the
    // emergency overlay.
    //
    // Reconstructing intent from rendered UI was the mistake. The turn that
    // failed already knows the exact message to resend, so it hands it over
    // directly and this ref is only ever a carrier.

    const createRetryHandler = (errorMessageId: string) => {
      return () => {
        // Find the last user message before this error
        // Use messagesRef.current to get latest messages (avoids stale closure)
        const currentMessages = messagesRef.current;
        const errorIndex = currentMessages.findIndex(
          (msg) => msg.id === errorMessageId,
        );

        if (errorIndex === -1) return;

        // Find the user message that caused this error (should be right before it)
        let lastUserMessage: Message | undefined;
        for (let i = errorIndex - 1; i >= 0; i--) {
          if (currentMessages[i].role === "user") {
            lastUserMessage = currentMessages[i];
            break;
          }
        }

        if (!lastUserMessage) return;

        // Check if this is the first user message (i.e., only 2 messages: user + error)
        const userMessages = currentMessages.filter(
          (msg) => msg.role === "user",
        );
        const isFirstMessage = userMessages.length === 1;

        if (isFirstMessage) {
          // Reset chat completely and resend
          chatAgentService.clearAllUserState();
          setMessages([]);
          setIsLoading(false);
          setIsStreaming(false);
          hasLoadedFromStorageRef.current = false;
          setShowScrollButton(false);
          shouldAutoScrollRef.current = true;

          // Resend the message after a brief delay to allow state to clear
          setTimeout(() => {
            handleSendMessage(
              lastUserMessage!.content,
              lastUserMessage!.displayContent || undefined,
            );
          }, 100);
        } else {
          // Remove the error message and resend
          setMessages((prev) => {
            const withoutError = prev.filter(
              (msg) => msg.id !== errorMessageId,
            );
            chatAgentService.saveMessagesToSession(withoutError);
            return withoutError;
          });

          // Resend the last user message (skipUserMessage=true since it's already there)
          setTimeout(() => {
            handleSendMessage(
              lastUserMessage!.content,
              lastUserMessage!.displayContent || undefined,
              true, // Skip adding user message again
            );
          }, 100);
        }
      };
    };

    // Handle dev settings chunk timeout change (TRAC-81)
    const handleChunkTimeoutChange = (seconds: number | null): void => {
      setChunkTimeout(seconds);
      chatAgentService.setTestChunkTimeout(seconds);
    };

    // TRAC-468 / TRACI-1106: Simulate session expiry: the server forgets this shopper's
    // session now, and the next message takes the real expired-session path.
    const handleSimulateSessionExpiry = useCallback(async () => {
      chatAgentService.simulateSessionExpiry();
    }, []);

    // TRAC-732: sever the LivePerson bridge server-side to reproduce a redeploy mid-handoff.
    // Nothing changes locally on purpose — we want to watch the page react to the backend
    // exactly as it would after a real deploy (reconnect, or end cleanly into the survey).
    const handleSeverHandoffBridge = useCallback(async (dropResume: boolean) => {
      try {
        await chatAgentService.severHandoffBridge({ dropResume });
      } catch (e) {
        logger.error('[HANDOFF] dev sever failed', e);
        throw e; // let the drawer surface it — a silent no-op would look like "the fix worked"
      }
    }, []);

    // Load messages from storage on mount (or demo messages if in demo mode)
    // Also attempt to resume any interrupted streams
    useEffect(() => {
      if (!hasLoadedFromStorageRef.current) {
        if (isDemoMode) {
          // Load demo conversation
          setMessages(demoMessages as Message[]);
        } else {
          // Load from session storage
          // TRACI-865: a soft-handoff card offered over 30 minutes ago is a dead offer; drop it
          // rather than show "You're not connected yet" on every later page load.
          const storedMessages = chatAgentService.getMessagesFromSession().filter((m: Message) =>
            !m.parts?.some((pt) => pt.type === 'soft_handoff' && Date.now() - (pt.offeredAt ?? 0) > 30 * 60_000));
          if (storedMessages.length > 0) {
            // TRAC-468: Check for interrupted stream — defer replay to useEffect 2
            const streamState = chatAgentService.getStreamState();
            // TRAC-732: NEVER replay an "interrupted AI stream" when the restored conversation is
            // in a live handoff. The AI isn't streaming during a handoff, but a stale isActive
            // flag from before the escalate would fire the replay — which re-streams the last AI
            // turn (the greeting) and appends a fresh assistant message (with a feedback tray)
            // EVERY reload, stacking duplicates. The handoff poll owns message delivery here.
            const inHandoff = storedMessages.some(m =>
              m.responder || m.inHumanZone || m.isConnectingTurn || m.isRoutingPrompt);
            // TRAC-850: a persisted "active" stream older than ~2 minutes is a dead
            // turn (real turns finish in well under that) — replaying it just locks
            // the input behind a spinner. Treat it as interrupted instead.
            const streamFresh =
              streamState != null && Date.now() - (streamState.timestamp ?? 0) < 120_000;
            const hasActiveStream = streamState?.isActive === true && streamFresh && !inHandoff;
            if (inHandoff) {
              chatAgentService.updateStreamState(null, 0, false); // clear the stale flag for good
            }

            if (hasActiveStream) {
              logger.debug('[TRAC-468] Interrupted stream detected — deferring replay to useEffect 2');
              setNeedsReplay(true);
              setReplayStreamState(streamState);
              setMessages(storedMessages);
              setIsStreaming(true);
              setIsLoading(true);
            } else {
              // No active stream — mark any streaming messages as interrupted
              chatAgentService.updateStreamState(null, 0, false);
              const fixedMessages = storedMessages.map((msg: Message) => {
                if (msg.isStreaming) {
                  return { ...msg, isStreaming: false, wasInterrupted: true };
                }
                return msg;
              });

              const hadInterrupted = fixedMessages.some(
                (msg: Message) => msg.wasInterrupted,
              );
              if (hadInterrupted) {
                chatAgentService.saveMessagesToSession(fixedMessages);
              }

              // TRAC-638: After loading stored messages on iframe reload /
              // PDP nav, scroll the latest user message to the top of
              // viewport — same UX as sending a new message live. The
              // actual scroll fires in a separate useEffect that waits for
              // pageContextReady (so the welcome is in displayMessages and
              // the layout is stable before we measure offsets).
              //
              // Hide the chat (isInitialScrollPending=true) during the
              // settling window so the user doesn't see scrollTop=0 before
              // the scrollIntoView fires.
              shouldAutoScrollRef.current = false;
              lockAutoScrollRef.current = true;
              setIsInitialScrollPending(true);
              setPendingInitialScroll(true);
              setMessages(fixedMessages);
            }

            // TRAC-277: Restore vehicle header from stored messages
            const restoredHistory: ActiveVehicle[] = [];
            for (const msg of storedMessages) {
              if (msg.role !== "assistant" || !msg.parts) continue;
              for (const part of msg.parts) {
                const vehicle = extractVehicleFromPart(part, "session_restore");
                if (vehicle) {
                  const existingIdx = restoredHistory.findIndex((v) => {
                    if (v.year !== vehicle.year) return false;
                    if (v.make.toLowerCase() !== vehicle.make.toLowerCase()) return false;
                    const fullA = [v.model, v.variant].filter(Boolean).join(" ").toLowerCase();
                    const fullB = [vehicle.model, vehicle.variant].filter(Boolean).join(" ").toLowerCase();
                    if (fullA === fullB) return true;
                    const [shorter, longer] = fullA.length <= fullB.length ? [fullA, fullB] : [fullB, fullA];
                    return longer.startsWith(shorter + " ");
                  });
                  if (existingIdx >= 0) {
                    restoredHistory[existingIdx] = { ...restoredHistory[existingIdx], ...vehicle, id: restoredHistory[existingIdx].id };
                  } else {
                    restoredHistory.push(vehicle);
                  }
                }
              }
            }
            if (restoredHistory.length > 0) {
              setVehicleHistory(restoredHistory);
              setActiveVehicle(restoredHistory[restoredHistory.length - 1]);
            }
          }
        }
        hasLoadedFromStorageRef.current = true;
        transcriptRestoredRef.current = true;
      }
    }, [isDemoMode, extractVehicleFromPart]);

    // TRAC-732 #15: resume a live handoff on load. A refresh OR a navigation (the embedded
    // widget is rebuilt on every page) used to restore only the band's dividers from storage
    // while the live plumbing — handoff state + the poll — was lost. Result: the customer's
    // messages still routed to the rep (backend stayed in mode=human) but the rep's replies had
    // no listener and never rendered. Here we ask the backend if we're mid-handoff and rehydrate
    // the state + poll so the live chat truly continues. Runs once on mount.
    //
    // TRAC-1006: the service now retries an unanswered check (backoff, then every 15s while
    // the persisted attempt is open) instead of returning null once, and the page no longer
    // waits on it to protect the handoff: state and dismissal were seeded from the persisted
    // attempt before the first render. `null` here means it gave up or was cancelled, and the
    // seeded state stands.
    const handoffResumedRef = useRef(false);
    // TRAC-1066: the re-asks above, and the timer carrying them, so unmount stops them.
    const connectingSettleTriesRef = useRef(0);
    const connectingSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /**
     * TRAC-1066: issue the escalate the previous page never got to send, from the page the
     * customer is on now. Called only while the backend says there is no handoff for this
     * session, so a request that DID land is never doubled.
     *
     * Everything after the request — the queue, a closed-hours or no-agents decline, a
     * connect failure — is the same code the press runs, because it is the same function.
     * The ask itself is not re-counted: the funnel and Adobe recorded it on the press.
     */
    const reissueEscalate = () => {
      const dept = attemptDeptRef.current;
      // The stored transcript usually carries the "Connecting you to a Tire Expert now…" turn
      // and its pending band header; this puts back whichever is missing (the same repair the
      // active branch makes) and hands the decline/failure rollback the turn to remove.
      const fixed = reconcileHandoffTranscript(
        messagesRef.current, { active: true, mode: 'waiting', agentName: null }, Date.now(), dept,
      );
      if (fixed.changed) {
        messagesRef.current = fixed.messages;
        setMessages(fixed.messages);
        chatAgentService.saveMessagesToSession(fixed.messages);
      }
      connectingMsgIdRef.current = fixed.connectingId;
      enterHumanDivIdRef.current = fixed.enterHumanDivId;
      setHandoffActivity('awaiting');
      // TRAC-475: an ask the AGENT made and a reload cut short never reached escalateToHuman,
      // so neither the funnel nor Adobe heard it. A press always leaves the funnel's shared
      // record behind, so only an ask with no record is counted here, and never twice.
      // TRAC-1212: who started it comes from the attempt record the previous page wrote.
      const initiatedBy = attemptInitiatorRef.current ?? undefined;
      if (handoffFunnel.handoffAskedAt() === null) {
        handoffFunnel.handoffRequested(dept ?? undefined, initiatedBy);
        clearLiveChatConnected();
        logger.info('[TRAC-965] live chat requested',
          postLiveChatEvent(window, 'live_chat_requested', chatAgentService.agentSessionId) ?? '(not embedded — no postMessage sent)');
      }
      void sendEscalate(buildHandoffTranscript(), dept, fixed.connectingId ?? '', initiatedBy);
    };
    const resumeHandoff = async (opts: { isCancelled?: () => boolean } = {}): Promise<void> => {
        const state = await chatAgentService.resumeHandoffIfActive({
          onHumanMessage: (text, name, html) => appendHumanMessage(text, name, html),
          onStatus: (s, name, avatar, agentId, agentEmployeeId, reason) => handleHandoffStatus(s, name, avatar, agentId, agentEmployeeId, reason),
        }, opts);
        // Re-checked here as well as inside the service: the answer may have been on its way
        // back when the customer left or started over.
        if (!state || opts.isCancelled?.()) return;
        if (state.active) {
          // TRAC-919: make the transcript tell the story of the handoff we are resuming.
          // Whatever was stored -- maybe just the department menu, if the page changed
          // before anything else was saved -- gets its connecting turn and band header
          // back, read as connected if a rep is already in the room; and the ids are
          // re-armed so a join that lands on THIS page flips them like it always did.
          // TRAC-1049: a tab that did not press the button has no team of its own — the per-tab
          // attempt record is another tab's. The shared attempt carries the team the backend
          // confirmed, which is what lets this tab's heading (and so the still-trying line under
          // it, and the space that line holds) name exactly what the tab that asked names.
          const shared = handoffFunnel.handoffAskedDepartment();
          if (shared.department && !attemptDeptRef.current) attemptDeptRef.current = shared.department;
          // Only a team the backend has CONFIRMED may be named in the heading — see
          // handoffDepartmentConfirmed. The line under it may use the pick, which is what the
          // tab that pressed the button has always done with its own.
          const fixed = reconcileHandoffTranscript(
            messagesRef.current, state, Date.now(), shared.confirmed ? shared.department : null,
          );
          if (fixed.changed) {
            messagesRef.current = fixed.messages;
            setMessages(fixed.messages);
            chatAgentService.saveMessagesToSession(fixed.messages);
          }
          connectingMsgIdRef.current = state.mode === 'human' ? null : fixed.connectingId;
          enterHumanDivIdRef.current = fixed.enterHumanDivId;
          // Restore the live state so the input, send-routing, and band all behave as connected.
          const nm = state.agentName || 'Tire Rack team';
          handoffAgentNameRef.current = nm;
          setHandoffAgentName(nm);
          if (state.mode === 'human') {
            setHandoff('active');
            // Only show "Waiting on {name}…" if the CUSTOMER spoke last (ball is in the rep's
            // court). If the rep's message is the most recent one — the common refresh case —
            // it's the customer's turn, so no waiting indicator. Find the last band message.
            const msgs = messagesRef.current;
            let lastSpeaker: 'rep' | 'customer' | null = null;
            for (let i = msgs.length - 1; i >= 0; i--) {
              const m = msgs[i];
              if (m.responder === 'human') { lastSpeaker = 'rep'; break; }
              if (m.role === 'user' && m.inHumanZone) { lastSpeaker = 'customer'; break; }
            }
            setHandoffActivity(lastSpeaker === 'customer' ? 'awaiting' : null);
          } else {
            // Still connecting / queued — no rep yet, so the customer is genuinely waiting.
            setHandoff('queued');
            setHandoffActivity('awaiting');
          }
        } else {
          // TRAC-1066: `connecting` is the one engaged state the BACKEND has not confirmed yet,
          // and it is the state a customer is in for the seconds right after pressing the outage
          // screen's route to a person. Taking a not-active answer as truth here undid everything
          // TRAC-1006 does: it dropped the seeded handoff, released the dismissal the press had
          // taken, and handed the outage screen back on top of a chat that was on its way to a
          // rep — with "Talk to a Tire Expert" offered over a request already in flight. The page
          // the customer pressed on never asks this question; it waits for the status events. So a
          // connecting attempt waits too, and asks again. Only a state the backend itself
          // confirmed (queued / active) may be closed by a not-active answer.
          // TRAC-1066 (the second hole, found on dev at 3:55 PM ET on 21 Sep 2026): waiting
          // is only right when there is something to wait FOR. `connecting` goes on screen the
          // instant the button is pressed, but the request leaves only after initSession
          // resolves — 1.3 s to 4.3 s later on dev, longer during the outage this exists for.
          // A tirerack.com navigation inside that gap means no escalate was ever sent, and this
          // page would poll for 30 s about a handoff nobody asked for and then quietly give up
          // on a customer who pressed the button. So send it, from here, once.
          //
          // Only `pressed` is re-issued. A request we cannot prove never left is left alone: a
          // second escalate makes the backend tear the first handoff down and restart it
          // (handoff_manager.start_handoff), which would retract and re-ping a rep who was
          // already on their way. Absent (a record written before this change) reads the same
          // as `sent`, so those behave exactly as they did.
          if (
            handoffStateRef.current === 'connecting'
            && escalateRequestRef.current === 'pressed'
            && !reissuedEscalateRef.current
            && !opts.isCancelled?.()
          ) {
            reissuedEscalateRef.current = true;
            reissueEscalate();
            // The settling window belongs to the request that actually left, so it restarts
            // from here rather than counting the tries spent waiting for a ghost.
            connectingSettleTriesRef.current = 0;
          }
          if (
            handoffStateRef.current === 'connecting'
            && connectingSettleTriesRef.current < CONNECTING_SETTLE_TRIES
          ) {
            connectingSettleTriesRef.current += 1;
            connectingSettleTimerRef.current = setTimeout(() => {
              connectingSettleTimerRef.current = null;
              if (opts.isCancelled?.()) return;
              void resumeHandoff(opts);
            }, CONNECTING_SETTLE_RECHECK_MS);
            return;
          }
          const wasEngaged = handoffEngaged(handoffStateRef.current);
          // Not mid-handoff, but the restored transcript shows an OPEN human band — the chat
          // ended (rep closed it, or it timed out) while we were away. Close it visually so the
          // customer isn't stuck looking at a live-looking band that's actually dead.
          const hasOpenBand = messagesRef.current.some((m) => m.inHumanZone)
            && !messagesRef.current.some((m) => m.dividerKind === 'back-to-ai' || m.dividerKind === 'end-human');
          if (hasOpenBand) {
            handoffStateRef.current = 'active'; // so appendDivider treats it as closing a real band
            appendDivider('end-human');
            // TRAC-732: ask for feedback here too. This branch is reached when the chat ended
            // while the page was CLOSED — you come back, reload, and find out it's over. That
            // used to close the band silently, so the sessions most worth hearing about (the
            // ones that ended badly enough that you walked away) were the only ones never
            // surveyed. Mirror the live 'ended' path exactly: end-human -> survey -> back-to-ai.
            showConversationSurvey();
            appendDivider('back-to-ai');
            handoffStateRef.current = 'idle';
            appendSystemMessage('This live chat has ended.');
            // TRAC-965: the funnel records no end here, and neither does Adobe. Log the
            // orphan (a "connected" may have gone out on the page that was open at the
            // time) so the undercount is measurable, and drop the latch.
            if (liveChatConnectedEmitted(chatAgentService.agentSessionId)) {
              logger.info('[TRAC-965] live chat ended while the page was away — not sent to Adobe');
            }
            clearLiveChatConnected();
          }
          // TRAC-1006: the backend has confirmed it is over. Put the page's state in line with
          // that (it may have been seeded as live), and release a handoff dismissal -- the
          // outage screen comes back if the outage is still on, because now it is true.
          if (wasEngaged) {
            setHandoff('idle');
          } else if (overlayDismissedForHandoffRef.current) {
            overlayDismissedForHandoffRef.current = false;
            setAnnouncementDismissed(false);
          }
        }
    };
    useEffect(() => {
      if (isDemoMode || handoffResumedRef.current) return;
      handoffResumedRef.current = true;
      // TRAC-1006: the check can now keep retrying for minutes. If the customer starts a new
      // handoff meanwhile, a late answer about this one must not be applied over it.
      const gen = escalateGenRef.current;
      const leaveGen = leaveGenRef.current;
      void resumeHandoff({
        isCancelled: () => unmountedRef.current
          || escalateGenRef.current !== gen
          || leaveGenRef.current !== leaveGen,
      });
      // TRAC-1006: the page header (EmbedPage) only hears about handoff state from
      // dispatchHandoff, which only setHandoff calls. A page seeded mid-handoff would otherwise
      // show a live band under a plain AI header until the resume check answers -- minutes,
      // in the outage this exists for. Deferred a tick: this child effect runs before the
      // parent's listener is attached.
      if (handoffEngaged(handoffStateRef.current)) setTimeout(dispatchHandoff, 0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDemoMode]);

    // TRAC-1050: the restore landing's settle window. It outlives the effect that
    // starts it (see below), so only unmount stops it.
    const restoreSettleStopRef = useRef<(() => void) | null>(null);
    useEffect(() => () => { restoreSettleStopRef.current?.(); }, []);

    // TRAC-638: Fire the initial scroll-to-latest once pageContextReady is
    // true (welcome is in displayMessages so layout is stable). On real
    // TR.com the parent sends pageContext within milliseconds; on `/embed`
    // direct (no parent) it takes the 3s timeout. We keep the chat hidden
    // (opacity:0) during this wait so the user never sees a wrong scroll.
    useEffect(() => {
      if (!pendingInitialScroll || !pageContextReady) return;
      // One additional frame for displayMessages to render with welcome.
      const t = setTimeout(() => {
        // TRAC-732: if we're loading straight into the end-of-chat survey state, the
        // framing effect owns the scroll (it glides the "Chat ended" divider to the top).
        // Anchoring the last user message here would fight it — so skip the scroll but
        // still clear the init flags below.
        //
        // TRAC-1050: everything else lands via scrollRestoredHistoryIntoView, which
        // keeps this effect's original framing only while the tail below the last user
        // message fits on screen, and otherwise puts the NEWEST message in view — a rep
        // exchange, a queue wait, or one long answer all used to leave the customer
        // looking at old history. It also holds that landing while the handoff poll and
        // late-loading cards change the height underneath it.
        if (!showEndOfChatSpacer) {
          // The settle window deliberately OUTLIVES this effect: clearing
          // pendingInitialScroll on the next line re-runs it, and hanging the stop
          // on this cleanup would kill the window in the same tick — which is
          // exactly how a late layout shift (the welcome, an announcement banner)
          // once left the landing stranded mid-history. Unmount stops it instead.
          restoreSettleStopRef.current = scrollRestoredHistoryIntoView();
        }
        setIsInitialScrollPending(false);
        setPendingInitialScroll(false);
      }, 100);
      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingInitialScroll, pageContextReady]);

    // TRAC-638: Safety fallback — if pageContextReady never flips (network
    // hang, unusual embed without a parent), reveal the chat anyway after
    // 4s so the user doesn't see a blank surface forever.
    useEffect(() => {
      if (!pendingInitialScroll) return;
      const t = setTimeout(() => {
        setIsInitialScrollPending(false);
        setPendingInitialScroll(false);
      }, 4000);
      return () => clearTimeout(t);
    }, [pendingInitialScroll]);

    // TRAC-468 replay effect (interrupted-stream replay) extracted to useSessionExpiry.

    // Fetch announcement settings on mount and poll every 30s.
    //
    // TRAC-459: Client-side fallback. If the announcement endpoint is
    // unreachable (2 consecutive 5xx / network / timeout failures) AND the
    // browser reports it's online AND the server-side feature flag is on,
    // we show the emergency overlay locally. Covers the "backend is totally
    // unreachable" failure mode — the server-side auto-maintenance chain
    // can't signal that case because the message pathway IS the backend.
    //
    // 2026-05-05 follow-up — two real bugs surfaced from a phone test during
    // a bilateral FIS drill:
    //   1. Fresh-load gap: with no fetch timeout, a hanging fetch didn't
    //      register as a failure for the browser's default TCP-retry window
    //      (>120s on mobile). A customer loading the page during bilateral
    //      failure waited far too long for the banner. Fix:
    //      AbortController with FETCH_TIMEOUT_MS so failures register fast
    //      and the 2-failure gate triggers in ~25s instead of >120s.
    //   2. Recovery flicker: as both regions recover at slightly different
    //      speeds, polls succeed/fail intermittently and the banner toggled
    //      ON/OFF within seconds. Fix: hysteresis on the fallback-clear
    //      path — require N consecutive healthy polls (server has no real
    //      announcement) before exiting fallback. Mirror of the engagement
    //      gate.
    useEffect(() => {
      const FALLBACK_THRESHOLD = 2;
      // Mirror of FALLBACK_THRESHOLD on the clear side — require N consecutive
      // healthy polls (server returns 200 + enabled=false) before exiting
      // fallback. Stops banner-flicker during partial recovery.
      const FALLBACK_CLEAR_THRESHOLD = 2;
      // Conservative timeout: announcement endpoint is a single DDB Get + a
      // small JSON merge; in practice it responds in <500ms. 8s gives a
      // generous margin for slow mobile networks while still catching hangs
      // from a true blackhole before the next 15s poll fires. With the
      // 2-failure gate, banner engages in ~25s on a fresh load during
      // bilateral failure (vs >120s pre-fix).
      const FETCH_TIMEOUT_MS = 8000;
      const FALLBACK_MESSAGE = AGENT_UNAVAILABLE_MESSAGE;
      let consecutiveFailures = 0;
      // Counts consecutive "server is healthy AND has no real announcement"
      // polls while we're in fallback. Resets on any failure or when a real
      // server announcement appears. Fallback exits only when this hits
      // FALLBACK_CLEAR_THRESHOLD.
      let recoveryConfirmations = 0;
      let fallbackActive = false;
      let fallbackFlagEnabled = true; // Safety net defaults on; overridden by server setting.

      // One-time fetch of the feature flag. Admins rarely toggle this; if they
      // do, a page refresh picks up the change.
      (async () => {
        try {
          const flagRes = await fetch(
            `${getBackendApiUrl()}/api/settings/client-fallback-overlay`,
          );
          if (flagRes.ok) {
            const flagData = await flagRes.json();
            fallbackFlagEnabled = flagData.enabled !== false;
          }
        } catch {
          // Fail-open — leave the safety net on if we can't reach the flag endpoint.
        }
      })();

      // Helper: is the backend reporting full readiness? Returns true
      // ONLY when /api/system/ready returns 200. Any other outcome
      // (5xx, timeout, network error) returns false — the banner stays
      // up until the backend explicitly confirms it's ready. 3s timeout
      // bounds how long we wait per-poll for an answer; if /system/ready
      // is itself unreachable, that's a recovery-not-yet-ready signal.
      const checkSystemReady = async (): Promise<boolean> => {
        const ctrl = new AbortController();
        const tid = setTimeout(() => ctrl.abort(), 3000);
        try {
          const r = await fetch(`${getBackendApiUrl()}/api/system/ready`, {
            signal: ctrl.signal,
          });
          return r.ok;
        } catch {
          return false;
        } finally {
          clearTimeout(tid);
        }
      };

      // TRAC-1006: nothing this poll sees may clear a dismissal the customer made to reach a
      // person -- not a changed message, not the server clearing, not the client fallback
      // engaging after two failed polls. That dismissal belongs to the handoff and is released
      // only when the handoff is confirmed over (setHandoff / resumeHandoff). The fallback
      // also cannot put the screen over a handoff persisted as live: the gate suppresses it on
      // the seeded handoffState.
      const undismissUnlessHeldForHandoff = () => {
        if (!overlayDismissedForHandoffRef.current) setAnnouncementDismissed(false);
      };

      const fetchAnnouncement = async (): Promise<void> => {
        let isInfraFailure = false;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
        try {
          const response = await fetch(
            `${getBackendApiUrl()}/api/settings/announcement`,
            { signal: controller.signal },
          );
          // Piggyback the served-region header off this GA-routed poll so we
          // can drop the dedicated /health probe in useServedRegion.
          // X-Served-Region is stamped on every response by api.py middleware.
          const served = response.headers.get("X-Served-Region");
          if (served) servedRegionService.set(served);
          if (response.ok) {
            consecutiveFailures = 0;
            // TRAC-1052: a tester's own-browser outage screen rewrites the
            // response here and nowhere else, so everything below runs exactly
            // as it does for a real outage. Hard off on prod builds.
            const previewOn = outagePreviewOn();
            const raw: AnnouncementSettings & {
              failover_enabled?: boolean;
              active_chaos_drill_id?: string | null;
            } = await response.json();
            // The marker says "only you see this" -- never on top of a real
            // emergency the server is already announcing to everyone.
            setOutagePreviewForced(
              previewOn && !(raw.enabled && raw.mode === "emergency"),
            );
            const data = applyOutagePreview(raw, previewOn);
            const mode = data.mode || "informational";
            // TRAC-550 B6: same response carries the pin-flip kill switch.
            // Default true so the failover system stays on if the field is
            // missing (older backends).
            regionFailoverService.setFailoverEnabled(data.failover_enabled ?? true);
            // TRAC-548: pump the active chaos drill_id so participating
            // sessions inject the right header on region-health probes.
            chaosParticipationService.setDrillId(data.active_chaos_drill_id ?? null);

            const serverHasAnnouncement = !!(data.enabled && data.message);

            if (fallbackActive) {
              if (serverHasAnnouncement) {
                // Server has its own announcement — let it take over
                // immediately; the operator/auto-maintenance has explicitly
                // asserted state, no hysteresis needed.
                fallbackActive = false;
                recoveryConfirmations = 0;
                // fall through to set state from server below
              } else {
                // Server is healthy but says no announcement. Before
                // clearing the fallback banner, verify the system is
                // fully ready end-to-end via /api/system/ready. That
                // endpoint returns 200 ONLY when:
                //   - auto_maintenance is clear (server-side signal)
                //   - banner-visibility canary alarm is OK (no
                //     external observer is seeing a banner)
                //   - user-flow-failing alarm is OK (e2e canary is
                //     succeeding — chat verified working end-to-end)
                // Banner stays up until ALL of these are confirmed
                // healthy. No FE-side counting, no timing windows
                // beyond this single deterministic check.
                const systemReady = await checkSystemReady();
                if (!systemReady) {
                  // Hold fallback. We'll re-check next poll. The
                  // /system/ready endpoint is the single source of
                  // truth for "is the system fully recovered" — when
                  // it returns 200, the banner clears immediately.
                  recoveryConfirmations = 0; // unused but kept for diff cleanliness
                  return;
                }
                fallbackActive = false;
                recoveryConfirmations = 0;
                // TRAC-862: never clear a client-owned exhaustion overlay.
                if (!retryExhaustedOverlayRef.current) {
                  setAnnouncementMessage(null);
                  undismissUnlessHeldForHandoff();
                }
                return;
              }
            }

            // Not in fallback (or just exited because server has its own
            // announcement). Set state from the server response.
            if (serverHasAnnouncement) {
              const now = new Date();
              // Emergency mode ignores date scheduling
              const startOk =
                mode === "emergency" || !data.startDate || new Date(data.startDate) <= now;
              const endOk =
                mode === "emergency" || !data.endDate || new Date(data.endDate) >= now;
              if (startOk && endOk) {
                // Reset dismiss state only when the message or mode actually changes,
                // not on every 15s poll (TRAC-482 lets Cognito users dismiss emergency)
                setAnnouncementMessage(prev => {
                  if (prev !== data.message) undismissUnlessHeldForHandoff();
                  return data.message;
                });
                setAnnouncementMode(prev => {
                  if (prev !== mode) undismissUnlessHeldForHandoff();
                  return mode;
                });
              } else if (!retryExhaustedOverlayRef.current) {
                // TRAC-862: out-of-window server announcement — but don't take
                // the client's exhaustion overlay down with it.
                setAnnouncementMessage(null);
              }
            } else if (!retryExhaustedOverlayRef.current) {
              // TRAC-862: server has nothing to say. That is the NORMAL case
              // while the overlay is client-owned, so this branch is precisely
              // what used to erase it one poll after it appeared.
              setAnnouncementMessage(null);
              undismissUnlessHeldForHandoff();
            }
          } else if (response.status >= 500) {
            // 5xx only — 4xx responses stay in the normal error channel (they're
            // request-specific problems, not a sign of total unavailability).
            isInfraFailure = true;
          }
        } catch (error) {
          // Network error / timeout / fetch abort (e.g. our own
          // AbortController firing) — treat as infra failure.
          isInfraFailure = true;
          logger.warn("Failed to fetch announcement settings:", error);
        } finally {
          clearTimeout(timeoutId);
        }

        if (isInfraFailure) {
          // Any failure resets the recovery-confirmation counter so flap
          // patterns don't accumulate stale credit toward fallback exit.
          recoveryConfirmations = 0;
          consecutiveFailures++;
          if (
            fallbackFlagEnabled &&
            !fallbackActive &&
            consecutiveFailures >= FALLBACK_THRESHOLD &&
            typeof navigator !== "undefined" &&
            navigator.onLine
          ) {
            fallbackActive = true;
            // TRAC-1052: this overlay is real, not the tester's preview.
            setOutagePreviewForced(false);
            setAnnouncementMessage(FALLBACK_MESSAGE);
            setAnnouncementMode("emergency");
            undismissUnlessHeldForHandoff();
          }
        }
      };

      fetchAnnouncement();
      // 15s poll — fast enough that the auto-maintenance banner appears for
      // an active user within ~15s of the DDB row flipping to enabled=true,
      // without forcing a refresh. 30s was perceived as too slow for an
      // active outage. Endpoint is cheap (single DDB Get + announcement merge).
      // TRACI-849: only while the tab is visible; a hidden tab fetches once on return.
      return startVisibleInterval(() => void fetchAnnouncement(), 15_000);
    }, []);

    // Fetch survey settings on mount
    useEffect(() => {
      const fetchSurveySettings = async (): Promise<void> => {
        try {
          const response = await fetch(
            `${getBackendApiUrl()}/api/settings/survey`,
          );
          if (response.ok) {
            const data = await response.json();
            setSurveyEnabled(data.enabled !== false); // Default to true if not set
          }
        } catch (error) {
          logger.warn("Failed to fetch survey settings:", error);
          // Default to showing survey button if fetch fails
        }
      };
      fetchSurveySettings();
    }, []);

    // Auto-scroll when messages change (streaming updates, page load).
    // User-message smooth scroll is handled in handleSendMessage, not here.
    useEffect(() => {
      // TRACI-256: claimed before any early return, so a skipped request can't go stale.
      const repReveal = claimRepReveal();
      // TRAC-797: Pre-conversation there is nothing to scroll to, and the
      // wrapper is overflow-hidden (embedMessagesWrapperNoScroll) so any
      // programmatic scroll is unrecoverable by the user. Without this guard,
      // Clear conversation (setMessages([]) + shouldAutoScroll=true) fell
      // through to scrollToLatestMessage's scroll-to-bottom branch and
      // clipped the top of the welcome when the prompt grid overflowed.
      if (messages.length === 0) return;
      // TRAC-732: once the end-of-chat survey block is the tail, the framing effect below
      // owns the scroll (it glides the "Chat with {name} ended" divider to the top). The
      // default auto-scroll anchors the last USER message instead, which fights that glide
      // and lands the view short — so defer to the framer here.
      if (showEndOfChatSpacer) return;
      // TRAC-1132 PR 2: on a phone one function decides (bottom, reply top, or stay put).
      if (isPhoneTurn()) { settleScroll(); return; }
      // TRACI-256: a live rep's reply owns this render (the send-time anchor left
      // shouldAutoScrollRef off, so nothing else would bring it on screen).
      if (repReveal) { revealRepReply(repReveal); return; }
      if (shouldAutoScrollRef.current) {
        scrollToLatestMessage();
        requestAnimationFrame(() => {
          if (shouldAutoScrollRef.current) {
            scrollToLatestMessage();
          }
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, isLoading]);

    // TRAC-862: re-anchor after the retry indicator disappears.
    //
    // The indicator sits in flow between the message list and the input, so
    // mounting and unmounting it changes the scroll container's clientHeight —
    // and therefore its maxScroll. The anchor routine pins the last-turn
    // wrapper's minHeight so there is always enough scrollHeight to bring the
    // user's message to the top; that measurement is taken WITH the indicator
    // present, and the ResizeObserver that re-pins it on late resizes has long
    // since stopped by the time a retry chain finishes. Result: the container
    // clamps at max scroll ~24px short, and the previous turn's feedback row
    // peeks above the user's message.
    //
    // Measured: plain turn ends with the user message at 0px from the top;
    // a recovered retry ended at 24px with scrollTop already == maxScroll.
    //
    // Re-running the existing anchor once the indicator is gone re-measures
    // against the real height. Gated on the transition to null so a normal turn
    // (which never shows the indicator) is untouched.
    const prevRetryAttemptRef = useRef<number | null>(null);
    useEffect(() => {
      const had = prevRetryAttemptRef.current !== null;
      prevRetryAttemptRef.current = retryAttempt;
      if (had && retryAttempt === null && !isStreaming) {
        // Two frames: let the indicator's removal commit and reflow before the
        // anchor re-measures, same reasoning as the rAF pair inside the routine.
        requestAnimationFrame(() => requestAnimationFrame(() => scrollToLatestMessage("auto")));
      }
    }, [retryAttempt, isStreaming, scrollToLatestMessage]);

    // Re-anchor scroll to user message when streaming ends (embed mode).
    // Content height changes at stream-end (tool indicators hiding, etc.) can
    // shift the viewport in window-scroll mode. Re-snap to maintain position.
    useEffect(() => {
      if (!isStreaming && lockAutoScrollRef.current && lastUserMsgRef.current) {
        requestAnimationFrame(() => {
          const userMsg = lastUserMsgRef.current;
          if (userMsg) {
            const targetScroll = Math.max(0, getElementScrollOffset(userMsg) - ANCHOR_TOP_GAP_PX);
            window.scrollTo({ top: targetScroll });
          }
        });
      }
    }, [isStreaming]);

    // Auto-expand textarea up to 4 lines
    useEffect(() => {
      const textarea = inputRef.current;
      if (!textarea) return;

      const resize = () => {
        // TRAC-850: never measure while hidden. The loader/extension mounts this
        // embed inside a display:none panel; hidden elements measure
        // scrollHeight=0 (and computed line-height can be non-numeric), which set
        // an inline height of ~0 and left a padding-only sliver users read as a
        // dead/disabled input. Skip until we have a layout box — the observer
        // below re-runs the measurement when the panel is opened.
        if (textarea.getClientRects().length === 0) return;

        // Calculate line height on first VISIBLE render
        if (!lineHeightRef.current) {
          const computedStyle = window.getComputedStyle(textarea);
          const lineHeight = parseInt(computedStyle.lineHeight);
          const paddingTop = parseInt(computedStyle.paddingTop);
          const paddingBottom = parseInt(computedStyle.paddingBottom);
          if (!Number.isFinite(lineHeight)) return; // hidden/unstyled — retry on next signal
          lineHeightRef.current = { lineHeight, paddingTop, paddingBottom };
        }

        const { lineHeight, paddingTop, paddingBottom } = lineHeightRef.current;
        const maxLines = 3; // base 0
        const maxHeight = lineHeight * maxLines + paddingTop + paddingBottom;
        // Pre-conversation floor: the CSS min-height of .messageInputPreConversation
        // (three rows on desktop, one on a phone, TRAC-1124), read on every pass so a
        // rotation or breakpoint change picks up the other floor. One row if unreadable.
        const cssFloor = parseFloat(window.getComputedStyle(textarea).minHeight);
        const minHeight = isPreConversation
          ? (Number.isFinite(cssFloor) ? cssFloor : lineHeight + paddingTop + paddingBottom)
          : 0;

        // Reset height to auto to get proper scrollHeight
        textarea.style.height = "auto";

        // Set new height based on content, enforcing minimum, capped at max
        const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
        textarea.style.height = `${newHeight}px`;
      };

      resize();
      // TRAC-850: the panel's display flip happens OUTSIDE the iframe — no React
      // state changes, so nothing re-runs this effect. A ResizeObserver sees the
      // textarea gain a layout box when the panel opens and re-measures then.
      // (Settles in one pass: re-setting the same height doesn't re-fire it.)
      const ro = new ResizeObserver(() => resize());
      ro.observe(textarea);
      return () => ro.disconnect();
    }, [inputValue, isPreConversation]);

    // Index of last real (non-clientOnly) user message — used for current-turn scroll wrapper
    const lastUserMsgIdx = displayMessages.reduce(
      // The "Connecting you to a Tire Expert now…" turn is client-only (never sent to the rep),
      // but it IS the turn we want anchored to the top on escalate — so let it count here even
      // though it's client-only. Without this the scroll anchors on the turn above it (e.g. the
      // "Requesting live chat" marker) and the connecting turn hangs mid-screen.
      (acc, m, i) => ((!m.isClientOnly || m.isConnectingTurn) && m.role === "user" ? i : acc),
      -1,
    );

    // TRAC-732: show the end-of-chat trailing spacer only while the survey block is the
    // conversation TAIL — i.e. the customer hasn't resumed chatting with the AI yet. Once
    // they send a new message the normal min-height idiom owns the headroom again, so the
    // spacer must vanish (otherwise a phantom viewport of empty space lingers at the bottom).
    const surveyMsgIdx = displayMessages.findIndex(
      (m) => m.parts?.some((p) => p.type === 'conversation_survey'),
    );
    const showEndOfChatSpacer =
      surveyMsgIdx >= 0 &&
      !displayMessages
        .slice(surveyMsgIdx + 1)
        .some((m) => m.role === 'user' && !m.isClientOnly);

    // TRAC-732: frame the end-of-chat block on the "Chat with {name} ended" divider.
    // TWO jobs, both keyed off that divider so the customer sees the same thing:
    //  1) Size the trailing spacer to EXACTLY the room needed for that divider to reach
    //     the top — no more. A blunt full-viewport spacer let the block over-scroll so
    //     the "Back with the AI tire agent" header + welcome-back reply flew off the top;
    //     sizing it to `viewport - heightBelowTheDivider` caps the scroll right at the
    //     divider (0 when the block already exceeds a viewport — then it scrolls normally).
    //  2) Once (per chat-end), glide the divider up near the top so the whole rig — closed
    //     band, survey, and the back-to-AI header that signals "the chat is over" — frames
    //     into view together. Gated on !streaming so we wait for the welcome-back reply to
    //     land before measuring/among, and on a one-shot ref so we never yank a customer
    //     who has since scrolled away.
    useEffect(() => {
      if (!showEndOfChatSpacer) {
        endOfChatFramedRef.current = false;
        endFrameCleanupRef.current?.();  // tear down any in-flight settle guard
        endFrameCleanupRef.current = null;
        if (endSpacerPx !== 0) setEndSpacerPx(0);
        return;
      }
      if (isStreaming || isLoading) return;
      const id = setTimeout(() => {
        const sc = embedScrollRef.current;
        const endH = endHumanRef.current;
        const endMark = messagesEndRef.current;
        if (!sc || !endH) return;
        const vh = sc.clientHeight;
        const dividerOffset = Math.max(0, getElementScrollOffset(endH));
        // messagesEndRef sits just AFTER the last real content and BEFORE the spacer, so it
        // measures the block height independent of the spacer we're about to size.
        const contentBottom = endMark ? getElementScrollOffset(endMark) : sc.scrollHeight;
        const heightBelowDivider = Math.max(0, contentBottom - dividerOffset);
        const nextSpacer = Math.max(0, Math.ceil(vh - heightBelowDivider));
        // Size the spacer FIRST and bail — the state change re-runs this effect (endSpacerPx
        // is a dep) once the spacer is committed to the DOM. Only THEN do we glide, so the
        // scroll target isn't clamped short by a not-yet-grown scroll range.
        if (nextSpacer !== endSpacerPx) {
          setEndSpacerPx(nextSpacer);
          return;
        }
        if (endOfChatFramedRef.current) return;
        endOfChatFramedRef.current = true;
        isProgrammaticScrollRef.current = true;
        shouldAutoScrollRef.current = false;
        lockAutoScrollRef.current = true;
        const targetNow = () =>
          Math.min(Math.max(0, getElementScrollOffset(endH)), sc.scrollHeight - sc.clientHeight);
        // Smooth glide first…
        doScrollTo({ top: targetNow(), behavior: 'smooth' });
        scheduleProgrammaticScrollEnd(1200);
        // …then hold it there. The welcome-back reply's feedback tray mounts a beat after
        // the stream ends and shrinks the block, which used to clamp the scroll back below
        // the divider ("stuck higher", back-to-AI off-screen). A ~1s rAF guard re-pins the
        // LIVE divider offset (recomputed each frame, so it tracks the settling layout) and
        // outlasts any competing programmatic scroll — but stops the moment the customer
        // wheels/touches/keys, so we never fight a real scroll.
        endFrameUserScrolledRef.current = false;
        const onUserScroll = () => { endFrameUserScrolledRef.current = true; };
        sc.addEventListener('wheel', onUserScroll, { passive: true });
        sc.addEventListener('touchstart', onUserScroll, { passive: true });
        window.addEventListener('keydown', onUserScroll);
        const cleanup = () => {
          sc.removeEventListener('wheel', onUserScroll);
          sc.removeEventListener('touchstart', onUserScroll);
          window.removeEventListener('keydown', onUserScroll);
          if (endFrameGuardRafRef.current != null) cancelAnimationFrame(endFrameGuardRafRef.current);
          endFrameGuardRafRef.current = null;
        };
        endFrameCleanupRef.current?.();
        endFrameCleanupRef.current = cleanup;
        const startTs = performance.now();
        const guard = () => {
          if (endFrameUserScrolledRef.current) { cleanup(); return; }
          const target = targetNow();
          if (Math.abs(sc.scrollTop - target) > 6) sc.scrollTo({ top: target, behavior: 'auto' });
          if (performance.now() - startTs < 1000) {
            endFrameGuardRafRef.current = requestAnimationFrame(guard);
          } else {
            cleanup();
          }
        };
        // Give the initial smooth glide a moment to run before the guard starts holding.
        endFrameGuardRafRef.current = requestAnimationFrame(function wait() {
          if (performance.now() - startTs < 380 && !endFrameUserScrolledRef.current) {
            endFrameGuardRafRef.current = requestAnimationFrame(wait);
          } else {
            guard();
          }
        });
      }, 90);
      return () => clearTimeout(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showEndOfChatSpacer, isStreaming, isLoading, messages, endSpacerPx]);

    // Renders a single message. Extracted from the map so two sibling maps
    // (flat pre-turn list + current-turn wrapper) can share the same logic.
    const renderMessageItem = (message: Message, index: number) => {
      const isLatestMessage = index === displayMessages.length - 1;

      // Track ref for the last assistant message (for auto-scroll pinning)
      const isLastAssistantMsg =
        message.role === "assistant" &&
        index ===
          displayMessages.length -
            1 -
            [...displayMessages]
              .reverse()
              .findIndex((m) => m.role === "assistant");

      // Track ref for the last user message (for scroll-to-top on send)
      const isLastUserMsg =
        message.role === "user" &&
        index ===
          displayMessages.length -
            1 -
            [...displayMessages]
              .reverse()
              .findIndex((m) => m.role === "user");

      // Calculate turn number for assistant messages (0-indexed)
      // Excludes client-only messages that don't have backend turns
      let turnNumber: number | undefined;
      if (message.role === "assistant" && !message.isClientOnly) {
        turnNumber = displayMessages
          .slice(0, index)
          .filter(
            (m) => m.role === "assistant" && !m.isClientOnly,
          ).length;
      }

      // Handler for card selections — sends user's choice as a message
      const handleCardSelect = (
        value: string,
        label: string,
        // TRAC-896: the button that was clicked, handed over by the renderer at
        // click time. Only consumed by the YMMV branch below; every other card
        // action ignores it.
        origin?: HTMLElement | null,
      ): void => {
        // TRAC-1124: a tapped chip or answer card sends for the customer; on a phone the
        // keyboard goes with it (iOS keeps the box focused through a button tap).
        dismissPhoneKeyboard(inputRef.current);
        // TRAC-919: a door is only a door from outside. While a handoff is connecting,
        // queued or live, a department menu that is somehow still on screen must not
        // start a second request from inside the first (escalateToHuman refuses too).
        // Checked before anything else so the ignored tap does not count as engagement
        // or move anything either.
        if (value.startsWith('__route_to__:')) {
          const st = handoffStateRef.current;
          if (st === 'connecting' || st === 'queued' || st === 'active') {
            logger.warn(`[HANDOFF] department pick ignored — already ${st}`);
            return;
          }
        }
        // Miles 7/15: tapping ANY card or option is the customer engaging, so the entry
        // button squishes into the slim bar — the same thing sending a message does.
        // Only sends used to count, which meant the one path that skips the send
        // pipeline entirely (picking a door and going straight into the live-chat queue)
        // left a full-size button sitting above a conversation already in progress.
        setBannerInteracted(true);
        // TRAC-1108: a starter-prompt chip click. Chips arrive as (label, value) from the
        // welcome, so the prompt's text can be in either argument. Only its id and kind go
        // out, never its words.
        const clickedPrompt = matchStarterPrompt(starterPrompts, value, label);
        if (clickedPrompt) {
          const [promptExperiment] = starterPromptExperimentArms(agentSessionIdState);
          postStarterPromptClick(window, agentSessionIdState, {
            ...promptExperiment,
            pageBucket: promptsPageBucket,
            promptKind: clickedPrompt.promptKind ?? 'generic',
            promptId: clickedPrompt.promptId,
          });
        }
        // TRAC-732: routing_actions buttons under the "which team?" canned message —
        // route straight to the chosen department instead of sending a chat message.
        // Pass the option's LABEL as the reason so the rep-facing transcript (and the
        // LLM summary built from it) records WHY the customer asked for a person —
        // without it, a pre-conversation "Live Chat" click hands the rep a transcript
        // that says only "Requesting live chat" (the hazard-claim bug).
        if (value.startsWith('__route_to__:')) {
          escalateToHuman(value.slice('__route_to__:'.length), label, 'shopper_button');   // TRAC-1212: a door
          return;
        }
        // TRAC-790: "My orders" opens a second menu rather than connecting straight
        // away — same as LivePerson's menu bot today. Every branch still lands on
        // Customer Care, but the specific choice travels with the handoff so the rep
        // opens on "Cancel an order" instead of a bare "My orders".
        if (value === ORDERS_SUBMENU_VALUE) {
          void handleSendMessageRef.current?.(label, undefined, false, undefined, true, {
            parts: [
              { type: 'text', content: ORDERS_SUBMENU_PROMPT },
              { type: 'routing_actions', actions: ORDERS_SUBMENU_OPTIONS },
            ],
          }, undefined, false);
          return;
        }
        // TRAC-732: soft-handoff card — connect (now or on 10s timeout) vs stay with the AI.
        // Either way, remove the card and let the NORMAL flow take over (no bespoke text).
        if (value.startsWith('__soft_connect__:')) {
          removeSoftHandoff();
          // TRAC-1212: the AI's offer card, accepted (or its countdown ran out). Normally the
          // AI offered; the agent's own event says so if the shopper had asked in words.
          // TRACI-865: the label says how it connected (timer, visibility, click, paused), so
          // handoff_requested shows whether the offer connected on its own or needed a tap.
          escalateToHuman(value.slice('__soft_connect__:'.length) || undefined, undefined,
            agentHandoffInitiatorRef.current ?? 'ai_offered', label ? `soft_${label}` : undefined);
          return;
        }
        if (value === '__soft_dismiss__') {
          // TRACI-865: "No thanks" on the paused card: the shopper has already moved on.
          removeSoftHandoff();
          return;
        }
        if (value === '__soft_cancel__') {
          // Keep chatting with the AI: drop the card, then run the SAME send pipeline as
          // everything else — a "Stay with the AI" user turn + a streamed canned reply.
          removeSoftHandoff();
          void handleSendMessageRef.current?.('Stayed with the AI', undefined, false, undefined, true, {
            parts: [{ type: 'text', content: "No worries — I won't forward you to a human. I'm still here for anything tire-related." }],
          }, undefined, true);
          return;
        }
        // TRAC-232: Intercept vehicle-related card selections to activate YMMV mode
        // Re-use any agent-provided prefill so the user's vehicle info is preserved
        if (/get help choosing tires/i.test(value) || /help choosing tires/i.test(value) || /find my vehicle/i.test(value) || /find your vehicle/i.test(value)) {
          // TRAC-896: covers the cold-open starter-prompt chip AND the
          // dismissed-state "Find my vehicle" button — both arrive here as card
          // selections, so both morph out of the chip that was tapped.
          openYmmvFromOrigin(origin ?? null, ymmvPrefillRef.current || undefined);
          return;
        }

        // TRAC-185: Create user message (same format regardless of streaming state)
        const userMessage = createCardSelectionMessage(value, label);

        // Block the scroll effect — use same delayed scroll as handleSendMessage.
        // TRAC-1132 PR 2: a phone instead lets the scroll effect settle the new bubble at the bottom.
        const phoneSend = beginPhoneSend();
        if (!phoneSend) {
          shouldAutoScrollRef.current = false;
          lockAutoScrollRef.current = true;
        }

        setMessages((prev) => {
          // TRAC-478: If this is a starter-prompt click (first user action), commit the
          // welcome with the clicked prompt marked as selected so reload shows the same choice.
          const needsCommit = prev.every(m => m.isClientOnly) && !prev.some(m => m.isCommittedWelcome);
          const committedWelcome = needsCommit ? buildCommittedWelcome(value) : null;
          const newMessages = committedWelcome
            ? [committedWelcome, ...prev, userMessage]
            : [...prev, userMessage];
          if (committedWelcome) {
            chatAgentService.saveMessagesToSession(newMessages);
          }
          return newMessages;
        });

        // Smooth-scroll after header transition (same pattern as handleSendMessage)
        if (!phoneSend) setTimeout(() => {
          const userMsg = lastUserMsgRef.current;
          const wrapper = lastTurnWrapperRef.current;
          if (userMsg) {
            const savedScroll = getScrollTop();
            if (wrapper) { wrapper.style.minHeight = ''; }
            window.scrollTo({ top: savedScroll });
            if (wrapper) {
              wrapper.style.minHeight = `${getViewportHeight()}px`;
            }
            isProgrammaticScrollRef.current = true;
            const targetScroll = Math.max(0, getElementScrollOffset(userMsg) - ANCHOR_TOP_GAP_PX);
            doScrollTo({ top: targetScroll, behavior: "smooth" });
            scheduleProgrammaticScrollEnd(1000);
          }
        }, 300);

        // TRAC-396: "Browse tires by size" has a fully canned response (just
        // a prompt asking the user for their size). Skip the agent round-trip
        // and synthesize the assistant reply client-side — saves ~3-5s of
        // LLM latency and one paid invocation per click.
        if (/^Browse tires by size$/i.test(value)) {
          // The canned reply is client-only, so the agent never sees that the
          // user chose "browse by size". Flag the NEXT message as a browse-by-
          // size request so the backend routes a loosely-formatted size
          // (e.g. "225 65 17") to the size-search flow instead of YMMV / a
          // page-tire clarification. One-shot — consumed on the next send.
          chatAgentService.markBrowseBySizeIntent();
          const cannedContent =
            "What tire size are you looking for? Just enter your size in any format — I'll figure it out. Need help finding yours? [How to read a tire sidewall](https://www.tirerack.com/upgrade-garage/how-do-i-read-my-tire-size-on-my-sidewall)\n\n" +
            "- 225/65R17\n" +
            "- LT265/70R17\n" +
            "- 225/65R17 102H\n" +
            "- 245/40R18 + 275/35R18 (staggered front/rear)\n" +
            "- 35X12.50R17 (flotation / off-road)";
          const assistantMsgId = `${Date.now()}-canned`;
          // Brief loading shimmer so the response doesn't snap in jarringly —
          // ~1s matches the rough perceived TTFB of a real agent reply.
          setIsLoading(true);
          setTimeout(() => {
            // First insert the message with `isStreaming: true`. The typing-
            // animation hook (`useTypingAnimation`) latches on the first
            // observed isStreaming=true and reveals the full content in
            // batches, matching how a real agent reply types in.
            const streamingMsg: Message = {
              id: assistantMsgId,
              role: "assistant",
              content: cannedContent,
              displayContent: null,
              timestamp: new Date(),
              isClientOnly: true,
              isStreaming: true,
            };
            setMessages((prev) => [...prev, streamingMsg]);
            setIsLoading(false);

            // Approximate animation duration so we know when to flip
            // isStreaming=false and persist. Mirrors the hook's defaults:
            // charsPerBatch=3 at 20ms per batch, with a small padding.
            const animationMs = Math.ceil(cannedContent.length / 3) * 20 + 250;
            setTimeout(() => {
              setMessages((prev) => {
                const settled = prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, isStreaming: false } : m,
                );
                // Persist the settled (non-streaming) form so a reload
                // shows the message statically instead of re-typing it.
                chatAgentService.saveMessagesToSession(settled);
                return settled;
              });
            }, animationMs);
          }, 1000);
          return;
        }

        // Check if agent is currently streaming
        if (isStreaming) {
          // Agent streaming — queue the selection to send when streaming finishes
          setIsLoading(true);
          setPendingSelection({ value, label });
        } else {
          // Agent not streaming — send immediately
          handleSendMessage(userMessage.content, userMessage.displayContent, true);
        }
      };

      // TRAC-896: tap on the partial YMMV card. Re-arm the picker-resume
      // refs from THIS card's payload (tap-time, not stream-time — so a card tapped
      // turns later, or after a reload, still resumes the exact capability the agent
      // raised it with), then open the EXISTING full-screen interface. Source "card"
      // keeps agent-resume semantics on completion but dismisses silently and never
      // removes the answer bubble the card sits in.
      const handleYmmvPreviewOpen = (
        part: MessagePart,
        origin: HTMLElement | null,
        partIndex: number,
      ): void => {
        ymmvIntentRef.current = part.intent;
        ymmvPrefillSizeRef.current = part.prefillSize;
        // TRAC-896: remember WHICH card this is, so a completed selection can be
        // written back onto it. Captured here rather than looked up later for the
        // same reason as the morph origin: by completion time a newer turn may
        // have streamed in carrying its own card, and "the last card" would be
        // the wrong one. Message id + part index, because parts have no id.
        ymmvOriginCardRef.current = { messageId: message.id, partIndex };
        openYmmvFromOrigin(origin, part.prefill || undefined, "card", part.resume);
      };

      return (
        <div
          key={message.id}
          ref={(() => {
            // TRAC-732: the back-to-ai marker is the scroll anchor on chat-end — it must get
            // lastUserMsgRef even though it's momentarily the last ASSISTANT message
            // (the recap hasn't streamed yet), so check it BEFORE isLastAssistantMsg.
            const isBackAnchor = message.dividerKind === 'back-to-ai' && index === lastUserMsgIdx;
            // TRAC-732: also capture the "Chat with {name} ended" divider — the end-of-chat
            // view is framed on it (see the endSpacer/glide effect below).
            const isEndHuman = message.dividerKind === 'end-human';
            if (!isBackAnchor && !isEndHuman && !isLastAssistantMsg && !isLastUserMsg) return undefined;
            return (el: HTMLDivElement | null) => {
              if (isEndHuman) endHumanRef.current = el;
              if (isBackAnchor) lastUserMsgRef.current = el;
              else if (isLastAssistantMsg) lastAssistantMsgRef.current = el;
              else if (isLastUserMsg) lastUserMsgRef.current = el;
            };
          })()}
          className={`${styles.messageWrapper} ${
            message.role === "user"
              ? styles.userMessage
              : styles.assistantMessage
          } ${effectiveKind(message) === 'welcome' ? styles.welcomeMessageWrapper : ''} ${message.isSessionExpiry ? styles.sessionExpiryWrapper : ''} ${message.isClientOnly ? styles.clientOnlyMessage : ''} ${(message.isConnectingTurn || message.isSystemMarker) ? styles.connectingTurn : ''} ${message.isStreaming ? 'no-replay' : ''}`}
          style={(() => {
            // TRAC-732: the big "Back with the AI tire agent ✅" marker sits in an
            // .assistantMessage wrapper (36px bottom margin it doesn't need) — tighten it.
            if (message.dividerKind === 'back-to-ai') return { marginBottom: 12 };
            if (!message.inHumanZone) return undefined;
            // Enclose the human chat in one red-bordered card. The TOP (border + rounded
            // top-left) is drawn on the FIRST message of the contiguous in-human-zone run —
            // normally the "Chat with {name} started" divider, but the divider isn't always
            // present (e.g. a reload that persisted a rep message that arrived before the
            // divider was saved). Keying off "start of the run" instead of the divider means
            // the band ALWAYS has its top whenever any human-chat content shows. The bottom is
            // the mirror — drawn on the run's last message when the band is closed.
            const first = index === 0 || !displayMessages[index - 1]?.inHumanZone;
            // Bottom-most row of the contiguous band run — the one that dissolves. When the
            // typing/waiting activity block is showing it becomes the real bottom (rendered
            // after the messages), so no message dissolves then.
            const activityIsBottom = handoffActivity === 'typing' || handoffActivity === 'awaiting';
            let runEnd = index;
            while (runEnd < displayMessages.length - 1 && displayMessages[runEnd + 1]?.inHumanZone) runEnd++;
            // The band is CLOSED when its run ends at an `end-human` divider (a transfer
            // closes the chat and opens a NEW band right below, so no back-to-ai follows)
            // OR when a back-to-ai marker immediately follows (a normal return to the AI).
            const bandClosed = displayMessages[runEnd]?.dividerKind === 'end-human'
              || displayMessages[runEnd + 1]?.dividerKind === 'back-to-ai';
            // A closed band's bottom (the end-human hard edge) always draws — even while
            // the NEXT band's typing/awaiting activity block is showing after a transfer.
            // Only an OPEN band's bottom row is suppressed by that activity block.
            const isBottom = index === runEnd && (bandClosed || !activityIsBottom);
            const closedBottom = isBottom && bandClosed;
            const R = '206,17,38';
            // Uniform horizontal dissolve: hold solid to 80%, fade the last 20%.
            const fill = `linear-gradient(to right, rgba(${R},0.05) 0%, rgba(${R},0.05) 80%, rgba(${R},0) 100%)`;
            const style: React.CSSProperties = {
              background: fill,
              borderLeft: '3px solid #ce1126',
              borderTop: first ? '3px solid #ce1126' : undefined,
              borderBottom: closedBottom ? '3px solid #ce1126' : undefined,
              borderTopLeftRadius: first ? 14 : 0,
              borderBottomLeftRadius: closedBottom ? 14 : 0,
              marginTop: first ? 12 : 0, // gap above the band
              marginBottom: closedBottom ? 16 : 0,
              paddingTop: first ? 12 : 6,
              // open band's bottom row gets a tall pad to dissolve over; a closed band
              // ends in a clean hard edge with normal padding (no fade).
              paddingBottom: closedBottom ? 10 : (isBottom ? 60 : 6),
              paddingLeft: 14,
            };
            // Dissolve the band's edges with mask(s): the OPEN band's bottom row melts
            // DOWNWARD; the header row's top rail melts out to the RIGHT. In a very short
            // band the header is ALSO the bottom row, so INTERSECT the two masks.
            const bottomFade = 'linear-gradient(to bottom, #000 0%, #000 calc(100% - 56px), rgba(0,0,0,0) 100%)';
            const topFade = 'linear-gradient(to right, #000 0%, #000 calc(100% - 56px), rgba(0,0,0,0) 100%)';
            const masks: string[] = [];
            if (first) masks.push(topFade);
            if (isBottom && !bandClosed) masks.push(bottomFade);
            if (closedBottom) masks.push(topFade);
            if (masks.length) {
              const mask = masks.join(', ');
              const ext = style as React.CSSProperties & { WebkitMaskImage?: string; WebkitMaskComposite?: string };
              style.maskImage = mask;
              ext.WebkitMaskImage = mask;
              if (masks.length > 1) {
                style.maskComposite = 'intersect';
                ext.WebkitMaskComposite = 'source-in';
              }
            }
            if (message.role === 'user') {
              // The customer's messages right-align inside the human band so the exchange
              // reads like a real chat — rep on the left, you on the right.
              style.textAlign = 'right';
              style.paddingRight = 16;
            }
            return style;
          })()}
          data-message-id={message.id}
          data-message-role={message.role}
          data-message-turn={turnNumber ?? -1}
          data-message-kind={effectiveKind(message)}
        >
          {effectiveKind(message) !== 'welcome' && (
            <div className={styles.messageHeader} />
          )}
          <MessageRenderer
            id={message.id}
            content={message.role === 'user'
              ? friendlyCard(message.displayContent || message.content)
              : (message.displayContent || message.content)}
            role={message.role}
            parts={message.parts}
            toolCalls={message.toolCalls}
            timestamp={message.timestamp}
            isStreaming={message.isStreaming}
            wasInterrupted={message.wasInterrupted}
            formatTime={formatTime}
            isLatestMessage={isLatestMessage}
            onCardSelect={handleCardSelect}
            onYmmvPreviewOpen={handleYmmvPreviewOpen}
            onSurveySubmit={handleSurveySubmit}
            sessionId={agentSessionIdState}
            turnNumber={turnNumber}
            feedback={message.feedback}
            feedbackText={message.feedbackText}
            feedbackId={message.feedbackId}
            traceId={message.traceId}
            langfuseTraceUrl={message.langfuseTraceUrl}
            hasError={message.hasError && !message.isTimeoutError}
            errorDetails={message.errorDetails}
            onRetry={
              message.hasError && !message.isTimeoutError && !message.requestRejected
                ? createRetryHandler(message.id)
                : undefined
            }
            isSessionExpiry={message.isSessionExpiry}
            onTypingSettled={handleTypingSettled}
            responder={message.responder}
            secureFormNote={message.secureFormNote}
            agentName={message.agentName}
            agentAvatar={message.agentAvatar}
            richHtml={message.richHtml}
            dividerKind={message.dividerKind}
            dividerPending={message.dividerPending}
            summaryKind={message.summaryKind}
            inHumanZone={message.inHumanZone}
          />
          {message.cardRemovedNote && (
            <div role="status" data-testid="card-removed-note"
                 style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.4, color: '#5a6470' }}>
              {message.cardRemovedNote}
            </div>
          )}
          {/* TRAC-1035: the still-trying line, under this attempt's heading, for exactly as long
              as the "Did you know?" tip shows (same condition), so it goes when the rep joins
              or the customer leaves the queue, with the rest of the waiting state. */}
          {handoffActivity === 'awaiting' && handoffState !== 'active' && waitStatus?.connectingId === message.id && (
            <HandoffWaitStatusLine department={waitStatus.department} shown={waitStatus.shown} />
          )}
        </div>
      );
    };

    // TRAC-482: Cognito-authed users (non-embed) can dismiss the emergency overlay
    // to smoke-test the agent before re-enabling for public embed users.
    // TRAC-975: …and it never lands on a customer who is mid-conversation with a
    // rep. See emergencyOverlay.ts — the whole gate lives there now so the
    // handoff term is unit-tested rather than asserted in a comment.
    const emergencyActive = emergencyOverlayActive({
      message: announcementMessage,
      mode: announcementMode,
      dismissed: announcementDismissed,
      handoffState,
      routingPending,
    });

    // TRAC-975: what this overlay can honestly offer as a way to a person, from
    // the SAME availability state the connect banner reads. Replaces the
    // string-identity check against RETRY_EXHAUSTED_MESSAGE that made every
    // other emergency overlay — auto-maintenance included — a dead end.
    const overlayRoute = overlayHumanRoute({
      availState: liveChatAvail.state,
      // TRAC-986: `state: "open"` with `failOpen` set means the backend could not
      // reach LivePerson and guessed. Passing it through is the whole fix — the
      // route treats a guess as not-open and offers the phone number instead.
      failOpen: liveChatAvail.failOpen,
      messageHasPhone: (announcementMessage ?? "").includes(HUMAN_CONTACT.phone),
    });
    const overlayMessage =
      overlayRoute.kind === "offline"
        ? `${announcementMessage}\n\n${LIVE_CHAT_CLOSED_CONTACT}`
        : announcementMessage;

    // TRAC-1002: the synthetics continuity canary (?health_check=true) is never
    // blocked by the screen, so it keeps producing real turns through an outage
    // and chat-turns-failing clears only when turns succeed. Everything below
    // that the screen would hide or disable reads this, not the gate above.
    const emergencyBlocksChat = overlayBlocksChat(emergencyActive, isSyntheticHealthCheck());

    return (
      <div ref={ymmvMorphContainerRef} className={`${styles.chatContainer} ${styles.embedChatContainer}${isPreConversation ? ` ${styles.preConversationContainer}` : ''}`} style={{ position: 'relative', background: ymmvVisual ? '#133e4d' : undefined }}>
        {/* TRAC-419: Emergency overlay covers the chat container only (header stays clickable — TRAC-482) */}
        {emergencyBlocksChat && (
          <AnnouncementBanner
            message={overlayMessage}
            mode="emergency"
            onDismiss={isPortalContext ? () => setAnnouncementDismissed(true) : undefined}
            prominentDismiss={isPortalContext}
            testerNote={outagePreviewForced ? OUTAGE_PREVIEW_NOTE : undefined}
            // TRAC-862 opened this door for the retry-exhausted overlay only,
            // gated on a string-identity check against the module constant.
            // TRAC-975 replaces that with a capability check: ANY emergency
            // overlay gets the action whenever LivePerson says live chat is
            // open, because whether a rep can be reached has nothing to do with
            // which sentence the overlay happens to be showing.
            //
            // escalateToHuman() posts straight to /agent/escalate-to-human,
            // which needs a session id and nothing else. Critically it does NOT
            // route through the agent — `route_to_human` is an agent tool, and
            // the agent is exactly what is unavailable here. The LivePerson
            // bridge lives in the backend and is unaffected by an agent outage.
            //
            // When live chat is CLOSED there is no button, because one that
            // cannot connect anyone is worse than none. `overlayMessage` gains
            // the phone/hours block instead (LIVE_CHAT_CLOSED_CONTACT), which
            // needs no network at all — the same reason it is a compiled-in
            // constant.
            actionLabel={overlayRoute.kind === "live" ? overlayRoute.label : undefined}
            onAction={
              overlayRoute.kind === "live"
                ? () => {
                    // Hand off, then GET OUT OF THE WAY.
                    //
                    // Clearing the overlay is not cosmetic: the escalation
                    // connects the customer to a real rep, and the overlay is a
                    // full-container blocking layer. Leaving it up meant a human
                    // picked up on the other side while the customer stared at
                    // an "we can't reach our tire expert" screen they could not
                    // get past — the Dismiss button is gated on isPortalContext,
                    // so it does not even exist for a customer in the embed.
                    // That is a worse outcome than the dead end this ticket set
                    // out to fix.
                    //
                    // TRAC-975: DISMISS, don't clear. `setAnnouncementMessage(null)`
                    // was enough for the client-owned retry-exhausted overlay, where
                    // the server has no announcement to re-send. It is NOT enough for
                    // a server-owned one: the 15s poll re-sets the same message and
                    // the overlay lands back on top of the live rep. `dismissed` is
                    // the state that survives — the poll only resets it when the
                    // message or mode actually changes.
                    //
                    // Released before escalating so the handoff UI is visible
                    // the moment it starts rendering.
                    retryExhaustedOverlayRef.current = false;
                    // Remember that THIS dismissal was the route being taken, so
                    // the handoff-end branch can undo it. An admin's Dismiss must
                    // survive, which is why this is a flag and not an unconditional
                    // reset over there.
                    overlayDismissedForHandoffRef.current = true;
                    setAnnouncementDismissed(true);
                    escalateToHumanRef.current('shopper_button');   // TRAC-1212: the overlay's button
                  }
                : undefined
            }
          />
        )}

        {/* Miles 7/15: full-width mode banner — the TRAC-732 pill's state machine
            reskinned. EmbedPage owns the widget header and renders ChatInterface
            directly beneath it, so this being our first child puts it under the
            header exactly as the mockup shows.

            TRAC-790: suppressed on the human-first opening (see humanFirstMode above) —
            the four red doors on screen already ARE the way to a person, and this button
            only offered the same four again. It comes back the moment the customer talks
            to the agent instead, which is the one situation it exists for. The exception
            is outside live-chat hours: then it's the greyed "here's when we reopen"
            affordance, which the menu doesn't say anywhere. */}
        {!ymmvVisual && !isDemoMode && !emergencyBlocksChat && !hideBannerForHumanFirst && (() => {
          // Appearance (label/colour/icon) is derived by the pure bannerModel helper so
          // the state-machine mapping stays unit-tested (chatBanner.test.ts). The click
          // handler below still needs the raw predicates, so they live here too.
          const idleState = handoffState === 'idle' || handoffState === 'ended';
          const isChecking = liveChatAvail.state === 'checking';
          const chatClosed = idleState && liveChatAvail.state !== 'open';
          const requested = idleState && routingPending;
          const { label, variant, leaving } = bannerModel({
            handoffState, availState: liveChatAvail.state, routingPending,
          });
          const variantClass = variant === 'closed' ? styles.modeBannerClosed
            : variant === 'leave' ? styles.modeBannerLeave : styles.modeBannerAi;
          const popupOpen = hoursPopupOpen;
          return (
            <div className={`${styles.modeBannerWrap} ${bannerSquished ? styles.modeBannerWrapSquish : ''}`} style={{ visibility: popupOpen ? 'hidden' : 'visible' }}>
              <button
                data-handoff-banner
                // TRAC-959: while the answer is still on its way the banner is a blank
                // the size of itself (the skeleton's SkeletonBanner), not a grey
                // "Checking availability…" that reads as a third loading screen.
                className={`${styles.modeBanner} ${variantClass} ${bannerSquished ? styles.modeBannerSquish : ''}${isChecking ? ` ${styles.modeBannerGhost}` : ''}`}
                disabled={isChecking}
                title={idleState
                  ? (isChecking ? 'Checking live chat availability…'
                     : requested ? 'Cancel your live chat request and keep chatting here'
                     : chatClosed ? 'Live chat is currently unavailable — tap to see when we reopen'
                     : 'Connect with a Tire Expert')
                  : 'Leave the live chat'}
                onClick={() => {
                  // TRAC-1124: same as a send; the keyboard does not stay up on a phone.
                  dismissPhoneKeyboard(inputRef.current);
                  if (idleState) {
                    if (isChecking) return;
                    if (requested) { cancelRoutingPrompt(); return; }
                    if (chatClosed) {
                      // Counted, so after-hours demand shows on the Live Person tab.
                      // TRAC-965: deliberately NOT an Adobe "live chat requested" — there was
                      // no "available" before it, and a request with no available reads as a
                      // bug in Adobe. The funnel has this moment; Adobe gets the four above.
                      handoffFunnel.handoffAskedWhileClosed(
                        liveChatAvail.state === 'closed_hours' ? 'closed_hours' : 'closed_no_agents',
                        liveChatAvail.nextOpen);   // TRAC-1212: when we reopen, on the record
                      setHoursPopupOpen(true);
                      return;
                    }
                    // No message yet → ask why they're here so we route Sales vs Care.
                    // Once they've said something, let the agent/classifier decide.
                    // Head-start the LivePerson monitoring session so the rep gets the
                    // hyperlink button (its ids are read at escalate time).
                    chatAgentService.prewarmLpSession?.();
                    const hasUserMsg = messages.some(
                      (m) => m.role === 'user' && !m.isClientOnly && !m.isSyntheticTurn && (m.displayContent || m.content || '').trim(),
                    );
                    if (hasUserMsg) escalateToHuman(undefined, undefined, 'shopper_button');   // TRAC-1212
                    else showRoutingPrompt();
                  } else endHandoffByUser();
                }}
              >
                {leaving ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                ) : (
                  /* headset — signals "talk to a real Tire Rack person" */
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M4 14v-3a8 8 0 0 1 16 0v3" />
                    <path d="M20 15v1.5a3.5 3.5 0 0 1-3.5 3.5H14" />
                    <rect x="2" y="13.5" width="4" height="6" rx="1.6" />
                    <rect x="18" y="13.5" width="4" height="6" rx="1.6" />
                  </svg>
                )}
                <span>{label}</span>
              </button>
              {/* TRAC-952: the "Est. wait" caption is hidden — LivePerson's queue-health
                  metric can serve a stale, unverifiable record (a customer saw ~343 min
                  while reps were free). bannerModel still computes showWait and the
                  backend still returns wait_seconds, so bringing it back is a render
                  change, gated on the trusted source TRAC-952 describes. */}
            </div>
          );
        })()}

        {/* TRAC-527: Delegated click + impression tracking covers the vehicle header,
            YMMV typeahead, and all messages via data-track-id annotations. */}
        <ChatTrackingRoot sessionId={agentSessionIdState} getToken={getToken}>
        {/* TRAC-277: Persistent vehicle header bar */}
        {/* TRAC-291 retired: the A/B gate is gone — the vehicle bar always renders when
            there's a vehicle (from an in-chat pick OR adopted from the page). */}
        {activeVehicle && (
          <VehicleHeaderBar
            activeVehicle={activeVehicle}
            vehicleHistory={vehicleHistory}
            onSwitchVehicle={(vehicle) => {
              setActiveVehicle(vehicle);
              pendingVehicleContextRef.current = vehicle;
            }}
            // TRAC-896: both header-bar entries morph out of the control that was
            // pressed. The two that live in the vehicle dropdown close it as they
            // fire, so their close falls back to a fade — the header-bar tire-size
            // button persists and gets a real shrink back.
            onChooseNewVehicle={(origin) => openYmmvFromOrigin(origin ?? null, undefined, "user")}
            onSelectTires={(vehicle, origin) => {
              // Pre-fill YMMV with the vehicle so user just needs to pick a tire size
              openYmmvFromOrigin(
                origin ?? null,
                `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.variant ? ` ${vehicle.variant}` : ""}`,
                "user",
              );
            }}
          />
        )}

        {/* TRAC-232: YMMV vehicle search — separate flex child, above messages wrapper */}
        {ymmvVisual && (
          <VehicleTypeahead
            results={ymmvResults}
            groups={ymmvGroups}
            isLoading={ymmvLoading}
            // TRAC-698: highlight against the sanitized query (what we actually
            // searched), so stray chars in the box don't break prefix highlighting.
            query={sanitizeVehicleQuery(inputValue)}
            selectedIndex={ymmvSelectedIndex}
            totalCount={ymmvTotalCount}
            groupType={ymmvGroupType}
            multiTier={ymmvMultiTier}
            totalMatches={ymmvTotalMatches}
            droppedYear={ymmvDroppedYear}
            step={ymmvStep}
            tireSizeData={ymmvTireSizeData}
            tireSizeLoading={ymmvTireSizeLoading}
            tireSizeError={ymmvTireSizeError}
            selectedVehicle={ymmvSelectedVehicle}
            allTireSizes={allTireSizesRef.current}
            onSelect={handleYmmvSelect}
            onGroupSelect={handleYmmvGroupSelect}
            onTireSizeSelect={handleTireSizeSelect}
            onTireSizeRetry={handleTireSizeRetry}
            onBackToVehicle={handleBackToVehicle}
            onStepBack={handleYmmvStepBack}
            canGoBack={ymmvCanGoBack}
            // TRAC-896: the close morph is no longer started here. It lives
            // inside handleYmmvDismiss (via the onDismissRequested hook below)
            // so that EVERY user-initiated dismissal gets it — the X here, and
            // the Escape key, which used to bypass this wrapper entirely and
            // hard-cut.
            onDismiss={handleYmmvDismiss}
            embedMode={true}
            isPreConversation={isPreConversation}
          />
        )}
        {/* Messages Area Wrapper */}
        <div
          ref={embedScrollRef}
          /* TRAC-790: the opening screen normally can't scroll, because it is measured to
             fit — the starter prompts are trimmed to the number that do. The connect-to-a-
             person menu can't be trimmed that way (every door has to be there), so in a
             short panel it CAN overflow, and locked at no-scroll the customer simply could
             not reach the last line. Scrolling is left on for that opening only; every
             other opening keeps the exact-fit behaviour. */
          className={`${styles.messagesWrapper} ${styles.embedMessagesWrapper} ${isPreConversation && !humanFirstMode && !ymmvVisual ? styles.embedMessagesWrapperNoScroll : ''} ${!isPreConversation ? styles.embedMessagesWrapperWithFade : ''}`}
          onScroll={ymmvVisual ? undefined : handleScrollWithBanner}
          style={ymmvVisual ? { display: 'none' } : { opacity: isInitialScrollPending ? 0 : 1 }}
          /* TRAC-1152: what the phone keyboard ride moves (embedKeyboardRide.ts), and whether this list shows its bottom fade */
          data-traci-kb-part="list"
          data-traci-kb-fade={!isPreConversation ? '' : undefined}
        >
          <div
            className={`${styles.messagesArea} ${styles.embedMessagesArea}${ymmvVisual ? ` ${styles.ymmvMessagesArea}` : ""}${humanFirstMode && !ymmvVisual ? ` ${styles.humanFirstMessagesArea}` : ""}`}
            ref={messagesAreaRef}
          >
          {/* TRAC-195: No empty state UI - show welcome message as first chat bubble */}
          <>
            {announcementMessage && announcementMode === "informational" && !announcementDismissed && !ymmvVisual && (
              <AnnouncementBanner
                message={announcementMessage}
                mode="informational"
                onDismiss={() => setAnnouncementDismissed(true)}
              />
            )}
            {/* TRAC-291: Context loading bubble removed in TRAC-454 — welcome message now shows immediately */}
            {/* TRAC-959: except on an opening that offers people, which cannot be drawn
                until we know whether anyone is there (optimisticWelcome is null while
                the hours are being checked). That gap used to be a bare white canvas
                under a grey "Loading…" header — a second loading screen after the
                skeleton. Keep the skeleton's placeholders on the canvas instead, so
                the customer sees one loading frame until the opening is the answer. */}
            {openingIsUndecided && <SkeletonCanvas inset />}
            {/* Flat: client-only messages (welcome, expiry) + all turns before the current one */}
            {displayMessages.map((message, index) => {
              if (lastUserMsgIdx >= 0 && index >= lastUserMsgIdx) return null;
              return renderMessageItem(message, index);
            })}
            {/* Current-turn wrapper: last user msg + streaming response */}
            {/* JS sets min-height on send so user message can always scroll to top */}
            <div ref={lastTurnWrapperRef}>
              {displayMessages.map((message, index) => {
                if (lastUserMsgIdx < 0 || index < lastUserMsgIdx) return null;
                return renderMessageItem(message, index);
              })}
              {/* TRAC-732: live handoff status — connecting / typing / waiting */}
              {handoffActivity && (
                <div
                  className={`${styles.messageWrapper} ${styles.assistantMessage}`}
                  style={(handoffActivity === 'typing' || handoffActivity === 'awaiting') ? {
                    // The waiting/typing slot IS the band's bottom: fade the fill right
                    // (matching the band) and dissolve the whole block + its left rail
                    // downward so the band melts out here.
                    background: 'linear-gradient(to right, rgba(206,17,38,0.05) 0%, rgba(206,17,38,0.05) 80%, rgba(206,17,38,0) 100%)',
                    borderLeft: '3px solid #ce1126',
                    marginTop: 0, marginBottom: 14, paddingTop: 6, paddingBottom: 44, paddingLeft: 14,
                    maskImage: 'linear-gradient(to bottom, #000 0%, #000 calc(100% - 56px), rgba(0,0,0,0) 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 calc(100% - 56px), rgba(0,0,0,0) 100%)',
                  } : undefined}
                >
                  <style>{`
                    @keyframes traciDot{0%,80%,100%{opacity:.25;transform:scale(.7)}40%{opacity:1;transform:scale(1)}}
                    .traci-dots span{width:7px;height:7px;border-radius:50%;display:inline-block;margin:0 2px;animation:traciDot 1.2s infinite}
                    .traci-dots span:nth-child(2){animation-delay:.2s}
                    .traci-dots span:nth-child(3){animation-delay:.4s}
                    @keyframes factFade{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:translateY(0)}}
                  `}</style>
                  {handoffActivity === 'typing' && (
                    // The rep is actively typing — name + red dots.
                    <div style={{ margin: '10px 0 18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {handoffAgentAvatar ? (
                          <img src={handoffAgentAvatar} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #ce1126' }} />
                        ) : (
                          <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#ce1126', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                            {(handoffAgentName || 'T').trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#ce1126' }}>
                          {`${handoffAgentName} is typing`}
                        </span>
                        <span className="traci-dots">
                          <span style={{ background: '#ce1126' }} />
                          <span style={{ background: '#ce1126' }} />
                          <span style={{ background: '#ce1126' }} />
                        </span>
                      </div>
                    </div>
                  )}
                  {handoffActivity === 'awaiting' && handoffState !== 'active' && (
                    // TRAC-732: BEFORE the rep connects, entertain the customer with a
                    // "Did you know?" tire fact that cycles every 9s. Switches to
                    // "Waiting on {name}" once they join (below).
                    <div style={{ margin: '10px 0 18px', display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                      <span style={{ fontSize: 18, lineHeight: '20px', flexShrink: 0 }} aria-hidden>💡</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 13.5, fontWeight: 800, color: '#8a8f98', letterSpacing: 0.2 }}>Did you know?</span>
                          <span className="traci-dots">
                            <span style={{ background: '#bbbfc6' }} />
                            <span style={{ background: '#bbbfc6' }} />
                            <span style={{ background: '#bbbfc6' }} />
                          </span>
                        </div>
                        <div key={factIndex} style={{ fontSize: 13, fontWeight: 400, color: '#5a6172', lineHeight: 1.4, animation: 'factFade 0.45s ease' }}>
                          {TIRE_FUN_FACTS[factIndex]}
                        </div>
                      </div>
                    </div>
                  )}
                  {handoffActivity === 'awaiting' && handoffState === 'active' && (
                    // Rep has joined — plain "Waiting on {name}" while we await their reply.
                    <div style={{ margin: '10px 0 18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {handoffAgentAvatar ? (
                          <img src={handoffAgentAvatar} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #ce1126' }} />
                        ) : (
                          <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#ce1126', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                            {(handoffAgentName || 'T').trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#8a8f98' }}>
                          {`Waiting on ${handoffAgentName}`}
                        </span>
                        <span className="traci-dots">
                          <span style={{ background: '#bbbfc6' }} />
                          <span style={{ background: '#bbbfc6' }} />
                          <span style={{ background: '#bbbfc6' }} />
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Loading indicator - shows tire animation when thinking, dots as fallback */}
              {isLoading && <ThinkingIndicator isFadingOut={isLoadingFadingOut} />}
            </div>
          </>
          <div ref={messagesEndRef} />
          {/* TRAC-732: precise trailing scroll room so the "Chat with {name} ended" divider
              can scroll to the top — and no further (see the framing effect above for how
              endSpacerPx is sized to `viewport - heightBelowTheDivider`). On chat-end the
              last-turn wrapper holds the WHOLE human conversation, far taller than a
              viewport, so the usual `minHeight = viewportHeight` idiom adds no headroom. A
              REAL spacer (rather than a min-height bump) survives the other minHeight
              setters that would clobber it and never touches the scroll wrapper's padding
              or its sticky bottom fade. It sits AFTER messagesEndRef — the auto-scroll
              target — so the spacer is pure trailing range, not something the default
              landing rests inside. */}
          {showEndOfChatSpacer && endSpacerPx > 0 && (
            <div aria-hidden style={{ height: endSpacerPx, flexShrink: 0, pointerEvents: 'none' }} />
          )}
        </div>
        {/* Scroll to bottom button — positioned absolute in messagesWrapper, above input island */}
        {showScrollButton && !ymmvVisual && (
          <button
            className={`${styles.scrollToBottomButton} ${styles.embedScrollToBottomButton}${hasNewMessage ? ` ${styles.newMessagePill}` : ''}`}
            onClick={handleScrollButtonClick}
            title={hasNewMessage ? "Go to the new message" : "Scroll to bottom"}
            aria-label={hasNewMessage ? "New message, go to the bottom" : "Scroll to bottom"}
          >
            {/* TRAC-1132 PR 2: a reply arrived while the customer was scrolled up (phones) */}
            {hasNewMessage && <span className={styles.newMessagePillLabel}>New message</span>}
            <svg
              width="16"
              height="16"
              viewBox="0 0 225 350"
              fill="#fff"
              style={{ transform: "rotate(90deg)" }}
            >
              <path d="M67.42,6.54l150,168.69a19.47,19.47,0,0,1-1.14,27.06L66.39,344.64a19.48,19.48,0,0,1-27.54-.7L13.71,317.47a19.47,19.47,0,0,1,.71-27.53l96.9-92.07a19.47,19.47,0,0,0,1.14-27.06L11,56.69A19.48,19.48,0,0,1,12.63,29.2L39.92,4.92A19.48,19.48,0,0,1,67.42,6.54Z" />
            </svg>
          </button>
        )}
        </div>{/* end messagesWrapper */}
        </ChatTrackingRoot>

        {/* TRAC-732: closed-hours modal — opened from the mode banner (which now lives
            at the top of the widget) when the customer taps it outside LP hours. It
            stays down here rather than moving up with the banner: it's a position:fixed
            full-screen overlay, so its place in the tree doesn't affect where it lands. */}
        {!ymmvMode && !isDemoMode && !emergencyBlocksChat && (
          <>
            {hoursPopupOpen && (() => {
              // TRAC-732: full-screen modal when the customer taps the closed Live Chat
              // button — explicit "closed right now", the reopen time, AND the full hours.
              // TRACI-425: busy copy instead when the greyed state is a no-agents decline.
              const modalCopy = hoursModalCopy(liveChatAvail.state, liveChatAvail.hours);
              return (
              <div
                onClick={() => setHoursPopupOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(17,20,24,0.55)',
                         display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
              >
                <div
                  ref={hoursTrayRef}
                  onClick={(e) => e.stopPropagation()}
                  style={{ position: 'relative', background: '#fff', borderRadius: 16, maxWidth: 420,
                           width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                           padding: '30px 26px 22px', textAlign: 'left' }}
                >
                  <button
                    onClick={() => setHoursPopupOpen(false)}
                    aria-label="Close"
                    style={{ position: 'absolute', top: 12, right: 12, border: 'none', background: 'transparent',
                             fontSize: 18, lineHeight: 1, color: '#9aa3ad', cursor: 'pointer', padding: 6 }}
                  >✕</button>
                  <div style={{ fontSize: 21, fontWeight: 800, color: '#1b1d22', marginBottom: 8, paddingRight: 24 }}>
                    {modalCopy.title}
                  </div>
                  <div style={{ fontSize: 15, color: '#5a6172', fontWeight: 500, lineHeight: 1.5 }}>
                    {modalCopy.body}
                  </div>
                  {modalCopy.showSchedule && liveChatAvail.schedule.length > 0 && (
                    <div style={{ borderTop: '1px solid #eef1f4', marginTop: 18, paddingTop: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
                                    color: '#8a8f98', marginBottom: 10 }}>
                        Live chat hours (Eastern)
                      </div>
                      {(() => {
                        const todayIdx = (new Date().getDay() + 6) % 7; // JS Sun=0 → Mon=0
                        return liveChatAvail.schedule.map((d, i) => {
                          const isToday = i === todayIdx;
                          const closed = d.hours === 'Closed';
                          return (
                            <div key={d.day} style={{ display: 'flex', justifyContent: 'space-between', gap: 16,
                                          fontSize: 14.5, marginBottom: 6,
                                          color: isToday ? '#1b1d22' : '#5a6172', fontWeight: isToday ? 800 : 600 }}>
                              <span>{d.day}{isToday ? ' (today)' : ''}</span>
                              <span style={{ color: closed ? '#aeb4bc' : (isToday ? '#1b1d22' : '#33425a'),
                                             fontWeight: isToday ? 800 : 700 }}>{d.hours}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                  <button
                    onClick={() => setHoursPopupOpen(false)}
                    style={{ width: '100%', marginTop: 22, border: 'none', borderRadius: 999,
                             background: '#ce1126', color: '#fff', fontSize: 15, fontWeight: 700,
                             padding: '11px 16px', cursor: 'pointer' }}
                  >
                    OK
                  </button>
                </div>
              </div>
              );
            })()}
          </>
        )}


        {/* Beta Disclaimer - Show above input when chat is empty (TRAC-195) */}
        {isPreConversation && !ymmvVisual && !isLoading && !isDemoMode && <BetaDisclaimer />}

        {/* TRAC-550 B8: subtle auto-retry indicator. Replaces TRAC-81's 15s
            countdown — auto-recovery is the model so there's no manual
            "Try again" button. Pin-flip mid-retry abandons the loop. */}
        {retryAttempt !== null && (
          <div
            className={styles.retryingIndicator}
            role="status"
            aria-live="polite"
          >
            <div className={styles.retryText}>
              {retryPhase === "inflight"
                ? `Trying again now… (attempt ${retryAttempt} of ${MAX_RETRIES})`
                : retrySecondsLeft && retrySecondsLeft > 0
                  ? `Connection lost. Trying again in ${retrySecondsLeft}s… (attempt ${retryAttempt} of ${MAX_RETRIES})`
                  : `Connection lost. Trying again… (attempt ${retryAttempt} of ${MAX_RETRIES})`}
            </div>
            {/* One bar, always proportional: it fills across the real window —
                the backoff delay while waiting, the attempt's timeout budget
                while in flight. Position therefore always means "this much of
                the window has elapsed", in both phases. */}
            <div className={styles.retryTrack}>
              <div
                className={styles.retryBar}
                style={{
                  width: `${retryBarFill}%`,
                  // The rewind to 0 must be INSTANT. `transition-property: width`
                  // applies in both directions, so animating back to 0 over the
                  // window meant the bar crawled backwards for one frame and was
                  // then sent forward again — it simply stayed pinned at 100% for
                  // every window after the first. Only the fill is animated.
                  transitionDuration:
                    retryBarFill === 0 ? "0ms" : `${retryWindowMs ?? 0}ms`,
                }}
              />
            </div>
          </div>
        )}

        {/* TRAC-550 B5: SSE reconnecting after region pin-flip — distinct
            from B8's per-message retry. Lives just above the input so it's
            visible without crowding the message stream. */}
        {isReconnecting && (
          <div
            className={styles.reconnectingIndicator}
            role="status"
            aria-live="polite"
          >
            Reconnecting…
          </div>
        )}

        {/* TRACI-27: the chat's own order card. Email and ZIP go straight to the
            backend's order lookup, never into a chat message. Local and dev only
            until the real web adapter replaces the fixture orders. */}
        {isOrderLookupEnabled() && agentSessionIdState && !ymmvMode && (handoffState === 'idle' || handoffState === 'ended') && (
          <OrderLookupCard sessionId={agentSessionIdState} getToken={getToken} />
        )}

        {/* Floating Island Input — hidden during tire size step (step 2 has its own UI) */}
        <ChatInputBar
          inputRef={inputRef}
          inputValue={inputValue}
          onChangeText={(value) => {
            if (ymmvMode) { handleYmmvInputChange(value); return; }
            setInputValue(value);
            // TRAC-1198: let the live rep see the customer typing (rides on the next poll).
            if (handoffState === 'active' && value.trim()) chatAgentService.noteCustomerTyping();
          }}
          onKeyPress={ymmvMode ? undefined : handleKeyPress}
          onKeyDown={ymmvMode ? handleYmmvKeyDown : undefined}
          onSend={() => {
            // TRAC-1124: on a phone the keyboard closes and the chat is full screen again
            // for the reply. Desktop keeps focus in the box (no-op there).
            dismissPhoneKeyboard(inputRef.current);
            handleSendMessage();
          }}
          ymmvMode={ymmvVisual}
          ymmvStep={ymmvStep}
          emergencyActive={emergencyBlocksChat}
          isDemoMode={isDemoMode}
          highlightInput={highlightInput}
          isLoading={isLoading}
          isStreaming={isStreaming}
          pendingSelection={!!pendingSelection}
          retryAttempt={retryAttempt}
          handoffActive={handoffState === 'connecting' || handoffState === 'queued' || handoffState === 'active'}
          handoffAgentName={handoffAgentName}
          preConversation={isPreConversation}
        />

        {/* Legal disclaimer — anchored to bottom of widget */}
        <LegalDisclaimer className={ymmvVisual ? styles.ymmvLegalDisclaimer : undefined} />

        <ChatModals
          isSurveyOpen={isSurveyModalOpen}
          onCloseSurvey={() => setIsSurveyModalOpen(false)}
          sessionId={chatAgentService.agentSessionId}
          getToken={getToken}
          showDevSettings={showDevSettings}
          onCloseDevSettings={() => setShowDevSettings(false)}
          chunkTimeout={chunkTimeout}
          onChunkTimeoutChange={handleChunkTimeoutChange}
          onSimulateSessionExpiry={handleSimulateSessionExpiry}
          onSeverHandoffBridge={handleSeverHandoffBridge}
          handoffActive={handoffState === 'active' || handoffState === 'queued'}
          onExperimentChange={() => {
            handleClearSession();
            setShowDevSettings(false);
          }}
        />
      </div>
    );
  },
);

ChatInterface.displayName = "ChatInterface";

export default ChatInterface;
